import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
  APIKeyCreateDTO,
  APIKeyCreatedDTO,
  APIKeyDTO,
  AdminNotificationSettingsDTO,
  AllpayUninvoicedTransactionSummaryDTO,
  AllpayUserCreateDTO,
  AllpayUserCreditLimitPatchDTO,
  AllpayUserDTO,
  AllpayUserWithBillingDetailsDTO,
  AllpayUserWithStateDTO,
  BadRequestResponse,
  BobcatOfferDTO,
  BobcatOfferGroupDTO,
  BobcatProductSetDTO,
  BobcatShallowOfferDTO,
  BobcatTagDTO,
  BobcatTravellerWithBearersDTO,
  CardAssignmentDTO,
  CardCreditCreateDTO,
  CardCreditDTO,
  CardCreditUpdateDTO,
  CardCreditValidationDTO,
  CardTransferTaskDTO,
  ChildAndYouthCreateDTO,
  ClaimabilityStatusDTO,
  CollectionImportListItemDTO,
  CollectionImportRequestDTO,
  CollectionImportResultDTO,
  CollectionMemberCreateWithOptionalCardDTO,
  CollectionMemberDTO,
  CollectionMemberInactivateDTO,
  CollectionMemberUpdateDTO,
  CollectionMemberWithTravellerDTO,
  CollectionTicketClaimCreateDTO,
  CollectionTicketClaimDTO,
  CollectionTicketClaimMemberStateDTO,
  CollectionTicketClaimUpdateDTO,
  ConflictResponse,
  DepartmentAccessDTO,
  DepartmentCollectionDTO,
  DepartmentCreateDTO,
  DepartmentDTO,
  DepartmentPayerDTO,
  DepartmentTreeNodeDTO,
  DepartmentUserDTO,
  DepartmentUserInviteDTO,
  DepartmentUserUpdateDTO,
  EtisJourneyResultDTO,
  EtisStopDTO,
  ExportTransactionsParams,
  ExternalTransactionCreateDTO,
  FileDTO,
  FileUploadFormDataDTO,
  ForbiddenResponse,
  GetBobcatDataForTicketsParams,
  GetBobcatProductSetsParams,
  GetEtisJourneysParams,
  GetEtisStopsParams,
  GetFilesListParams,
  GetIssuedTicketsWithTravellerByTransactionIDParams,
  GetTransactionsParams,
  GroupTicketProductSetCreateDTO,
  GroupTicketProductSetDTO,
  HealthResponse,
  ImpersonateAdminResultDTO,
  InternalServerErrorResponse,
  IsTravellerEmailAvailableParams,
  IssuedTicketBearerDTO,
  IssuedTicketBearerUpdateDTO,
  IssuedTicketBobcatDataMapDTO,
  IssuedTicketDTO,
  IssuedTicketFindResultDTO,
  IssuedTicketLendDTO,
  IssuedTicketRevokeDTO,
  IssuedTicketWithTravellerDTO,
  ListAllpayUsersParams,
  ListBearersParams,
  ListCardCreditsParams,
  ListTenantCollectionTicketClaimsParams,
  ListTenantOrganizationUsersParams,
  LookupBobcatOffersByTagsParams,
  M2mListTransactionsWithBillingDetailsParams,
  M2mTravellersGetManyParams,
  NotFoundResponse,
  NotImplementedResponse,
  OrganizationAllowedBobcatOfferGroupDTO,
  OrganizationAllowedBobcatTagDTO,
  OrganizationApplicationApproveDTO,
  OrganizationApplicationCreateDTO,
  OrganizationApplicationDTO,
  OrganizationApplicationRejectDTO,
  OrganizationApplicationUpdateDTO,
  OrganizationCreateManualDTO,
  OrganizationDTO,
  OrganizationPatchStateDTO,
  OrganizationUpdateDTO,
  OrganizationUserRoleUpdateDTO,
  OrganizationUsersResponseDTO,
  PayerCreateDTO,
  PayerCreateWithDetailsDTO,
  PayerDTO,
  PayerDetailsDTO,
  PayerUpdateDTO,
  PayerUpdateWithDetailsDTO,
  PaymentRequiredResponse,
  ProductPurchaseForCollectionMembersDTO,
  ProductPurchaseForExternalRecipientsDTO,
  PurchaseResultDTO,
  ResellerPurchaseCreateDTO,
  ResellerPurchaseResultDTO,
  SchoolTransportationCreateDTO,
  SelfServiceBearerUpdateDTO,
  SelfServiceBearersResponseDTO,
  SelfServiceCardDTO,
  SelfServiceCardIsClaimableParams,
  SelfServiceClaimCardRequestDTO,
  SelfServiceJWTResponseDTO,
  SelfServiceMagicLinkRequestDTO,
  SelfServiceTicketsResponseDTO,
  SelfServiceTravellerUpdateDTO,
  SelfServiceUpdateTraveller200,
  SelfServiceVerifyRequestDTO,
  SetAllowedOrganizationOfferGroupsBodies,
  SetAllowedOrganizationTagsBodies,
  StudentTravelCreateDTO,
  TenantCollectionTicketClaimsCreateDTO,
  TenantCollectionTicketClaimsDeleteDTO,
  TenantCollectionTicketClaimsUpdateDTO,
  TenantDTO,
  TenantPublicDTO,
  TenantUpdateDTO,
  TransactionDTO,
  TransactionWithBillingDetailsDTO,
  TransactionWithProductsDTO,
  TravelCardClaimAnonymousRequestDTO,
  TravelCardClaimAnonymousResponseDTO,
  TravelCardClaimRequestDTO,
  TravelCardIsClaimableParams,
  TravelCardRenameRequestDTO,
  TravelCardResponseDTO,
  TravellerBearerDTO,
  TravellerDTO,
  TravellerEmailAvailabilityDTO,
  TravellerFavoredBearerUpdateDTO,
  TravellerGlobalSearchParams,
  TravellerImportDTO,
  TravellerImportResultDTO,
  TravellerLinkPrivateBobcatAccountDTO,
  TravellerReplaceSchoolCardRequestDTO,
  TravellerUpdateDTO,
  TravellerWithCardsDTO,
  TravellerWithCollectionMembershipsDTO,
  UnauthorizedResponse,
  UserDTO,
  UserInviteDTO,
  UserNameInfoDTO,
  UserProfileDTO,
  UserWithDepartmentsDTO,
  UsersListNamesForIDsParams,
} from './models';

import { useAxiosAlltix } from '../../hooks/useAxiosAlltix';
import type { ErrorType } from '../../hooks/useAxiosAlltix';
/**
 * @summary Health check
 */
export const useHealthCheckHook = () => {
  const healthCheck = useAxiosAlltix<HealthResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return healthCheck({ url: `/health`, method: 'GET', signal });
    },
    [healthCheck],
  );
};

export const getHealthCheckQueryKey = () => {
  return [`/health`] as const;
};

export const useHealthCheckQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useHealthCheckHook>>>,
  TError = ErrorType<HealthResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useHealthCheckHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getHealthCheckQueryKey();

  const healthCheck = useHealthCheckHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useHealthCheckHook>>>
  > = ({ signal }) => healthCheck(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useHealthCheckHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type HealthCheckQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useHealthCheckHook>>>
>;
export type HealthCheckQueryError = ErrorType<HealthResponse>;

export function useHealthCheck<
  TData = Awaited<ReturnType<ReturnType<typeof useHealthCheckHook>>>,
  TError = ErrorType<HealthResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useHealthCheckHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useHealthCheckHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useHealthCheckHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useHealthCheck<
  TData = Awaited<ReturnType<ReturnType<typeof useHealthCheckHook>>>,
  TError = ErrorType<HealthResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useHealthCheckHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useHealthCheckHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useHealthCheckHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useHealthCheck<
  TData = Awaited<ReturnType<ReturnType<typeof useHealthCheckHook>>>,
  TError = ErrorType<HealthResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useHealthCheckHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Health check
 */

export function useHealthCheck<
  TData = Awaited<ReturnType<ReturnType<typeof useHealthCheckHook>>>,
  TError = ErrorType<HealthResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useHealthCheckHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useHealthCheckQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get the current users tenant
 * @summary Get tenant
 */
export const useGetTenantHook = () => {
  const getTenant = useAxiosAlltix<TenantDTO>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getTenant({ url: `/tenant`, method: 'GET', signal });
    },
    [getTenant],
  );
};

export const getGetTenantQueryKey = () => {
  return [`/tenant`] as const;
};

export const useGetTenantQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
  TError = ErrorType<InternalServerErrorResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTenantQueryKey();

  const getTenant = useGetTenantHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>
  > = ({ signal }) => getTenant(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetTenantQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>
>;
export type GetTenantQueryError = ErrorType<InternalServerErrorResponse>;

export function useGetTenant<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
  TError = ErrorType<InternalServerErrorResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetTenant<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
  TError = ErrorType<InternalServerErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetTenant<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
  TError = ErrorType<InternalServerErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get tenant
 */

export function useGetTenant<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
  TError = ErrorType<InternalServerErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetTenantQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Updates the tenants information
 * @summary Update tenant
 */
export const useTenantUpdateHook = () => {
  const tenantUpdate = useAxiosAlltix<TenantDTO>();

  return useCallback(
    (tenantUpdateDTO: TenantUpdateDTO) => {
      return tenantUpdate({
        url: `/tenant`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: tenantUpdateDTO,
      });
    },
    [tenantUpdate],
  );
};

export const useTenantUpdateMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useTenantUpdateHook>>>,
    TError,
    { data: TenantUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useTenantUpdateHook>>>,
  TError,
  { data: TenantUpdateDTO },
  TContext
> => {
  const mutationKey = ['tenantUpdate'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const tenantUpdate = useTenantUpdateHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useTenantUpdateHook>>>,
    { data: TenantUpdateDTO }
  > = (props) => {
    const { data } = props ?? {};

    return tenantUpdate(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type TenantUpdateMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useTenantUpdateHook>>>
>;
export type TenantUpdateMutationBody = TenantUpdateDTO;
export type TenantUpdateMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | InternalServerErrorResponse
>;

/**
 * @summary Update tenant
 */
export const useTenantUpdate = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useTenantUpdateHook>>>,
      TError,
      { data: TenantUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useTenantUpdateHook>>>,
  TError,
  { data: TenantUpdateDTO },
  TContext
> => {
  const mutationOptions = useTenantUpdateMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get a list of all payers for the current tenant
 * @summary List payers in tenant
 */
export const useListPayersForTenantHook = () => {
  const listPayersForTenant = useAxiosAlltix<PayerDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listPayersForTenant({
        url: `/tenant/payers`,
        method: 'GET',
        signal,
      });
    },
    [listPayersForTenant],
  );
};

export const getListPayersForTenantQueryKey = () => {
  return [`/tenant/payers`] as const;
};

export const useListPayersForTenantQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListPayersForTenantHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListPayersForTenantHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListPayersForTenantQueryKey();

  const listPayersForTenant = useListPayersForTenantHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListPayersForTenantHook>>>
  > = ({ signal }) => listPayersForTenant(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListPayersForTenantHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListPayersForTenantQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListPayersForTenantHook>>>
>;
export type ListPayersForTenantQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useListPayersForTenant<
  TData = Awaited<ReturnType<ReturnType<typeof useListPayersForTenantHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListPayersForTenantHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListPayersForTenantHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListPayersForTenantHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListPayersForTenant<
  TData = Awaited<ReturnType<ReturnType<typeof useListPayersForTenantHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListPayersForTenantHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListPayersForTenantHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListPayersForTenantHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListPayersForTenant<
  TData = Awaited<ReturnType<ReturnType<typeof useListPayersForTenantHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListPayersForTenantHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List payers in tenant
 */

export function useListPayersForTenant<
  TData = Awaited<ReturnType<ReturnType<typeof useListPayersForTenantHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListPayersForTenantHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListPayersForTenantQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Lists all users in the tenant
 * @summary List tenant users
 */
export const useListTenantUsersHook = () => {
  const listTenantUsers = useAxiosAlltix<UserDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listTenantUsers({ url: `/tenant/users`, method: 'GET', signal });
    },
    [listTenantUsers],
  );
};

export const getListTenantUsersQueryKey = () => {
  return [`/tenant/users`] as const;
};

export const useListTenantUsersQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantUsersHook>>>,
  TError = ErrorType<ForbiddenResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListTenantUsersHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListTenantUsersQueryKey();

  const listTenantUsers = useListTenantUsersHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListTenantUsersHook>>>
  > = ({ signal }) => listTenantUsers(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListTenantUsersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListTenantUsersQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListTenantUsersHook>>>
>;
export type ListTenantUsersQueryError = ErrorType<ForbiddenResponse>;

export function useListTenantUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantUsersHook>>>,
  TError = ErrorType<ForbiddenResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantUsersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListTenantUsersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListTenantUsersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListTenantUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantUsersHook>>>,
  TError = ErrorType<ForbiddenResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantUsersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListTenantUsersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListTenantUsersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListTenantUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantUsersHook>>>,
  TError = ErrorType<ForbiddenResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantUsersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List tenant users
 */

export function useListTenantUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantUsersHook>>>,
  TError = ErrorType<ForbiddenResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantUsersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListTenantUsersQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * As a tenant administrator, update billing and payment information for an organization
 * @summary Update organization payer information
 */
export const useTenantUpdatePayerHook = () => {
  const tenantUpdatePayer = useAxiosAlltix<PayerDTO>();

  return useCallback(
    (payerID: string, payerUpdateWithDetailsDTO: PayerUpdateWithDetailsDTO) => {
      return tenantUpdatePayer({
        url: `/tenant/payers/${payerID}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: payerUpdateWithDetailsDTO,
      });
    },
    [tenantUpdatePayer],
  );
};

export const useTenantUpdatePayerMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useTenantUpdatePayerHook>>>,
    TError,
    { payerID: string; data: PayerUpdateWithDetailsDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useTenantUpdatePayerHook>>>,
  TError,
  { payerID: string; data: PayerUpdateWithDetailsDTO },
  TContext
> => {
  const mutationKey = ['tenantUpdatePayer'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const tenantUpdatePayer = useTenantUpdatePayerHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useTenantUpdatePayerHook>>>,
    { payerID: string; data: PayerUpdateWithDetailsDTO }
  > = (props) => {
    const { payerID, data } = props ?? {};

    return tenantUpdatePayer(payerID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type TenantUpdatePayerMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useTenantUpdatePayerHook>>>
>;
export type TenantUpdatePayerMutationBody = PayerUpdateWithDetailsDTO;
export type TenantUpdatePayerMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Update organization payer information
 */
export const useTenantUpdatePayer = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useTenantUpdatePayerHook>>>,
      TError,
      { payerID: string; data: PayerUpdateWithDetailsDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useTenantUpdatePayerHook>>>,
  TError,
  { payerID: string; data: PayerUpdateWithDetailsDTO },
  TContext
> => {
  const mutationOptions = useTenantUpdatePayerMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * As a tenant administrator, update payment details information for an organization
 * @summary Update organization payer details
 */
export const useTenantUpdatePayerDetailsHook = () => {
  const tenantUpdatePayerDetails = useAxiosAlltix<PayerDTO>();

  return useCallback(
    (payerID: string, payerDetailsDTO: PayerDetailsDTO) => {
      return tenantUpdatePayerDetails({
        url: `/tenant/payers/${payerID}/details`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: payerDetailsDTO,
      });
    },
    [tenantUpdatePayerDetails],
  );
};

export const useTenantUpdatePayerDetailsMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useTenantUpdatePayerDetailsHook>>>,
    TError,
    { payerID: string; data: PayerDetailsDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useTenantUpdatePayerDetailsHook>>>,
  TError,
  { payerID: string; data: PayerDetailsDTO },
  TContext
> => {
  const mutationKey = ['tenantUpdatePayerDetails'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const tenantUpdatePayerDetails = useTenantUpdatePayerDetailsHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useTenantUpdatePayerDetailsHook>>>,
    { payerID: string; data: PayerDetailsDTO }
  > = (props) => {
    const { payerID, data } = props ?? {};

    return tenantUpdatePayerDetails(payerID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type TenantUpdatePayerDetailsMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useTenantUpdatePayerDetailsHook>>>
>;
export type TenantUpdatePayerDetailsMutationBody = PayerDetailsDTO;
export type TenantUpdatePayerDetailsMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Update organization payer details
 */
export const useTenantUpdatePayerDetails = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useTenantUpdatePayerDetailsHook>>>,
      TError,
      { payerID: string; data: PayerDetailsDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useTenantUpdatePayerDetailsHook>>>,
  TError,
  { payerID: string; data: PayerDetailsDTO },
  TContext
> => {
  const mutationOptions = useTenantUpdatePayerDetailsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Invite a tenant user to the system.
 * @summary Invite a tenant user
 */
export const useInviteTenantUserHook = () => {
  const inviteTenantUser = useAxiosAlltix<void>();

  return useCallback(
    (userInviteDTO: UserInviteDTO, signal?: AbortSignal) => {
      return inviteTenantUser({
        url: `/tenant/invite-user`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: userInviteDTO,
        signal,
      });
    },
    [inviteTenantUser],
  );
};

export const useInviteTenantUserMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useInviteTenantUserHook>>>,
    TError,
    { data: UserInviteDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useInviteTenantUserHook>>>,
  TError,
  { data: UserInviteDTO },
  TContext
> => {
  const mutationKey = ['inviteTenantUser'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const inviteTenantUser = useInviteTenantUserHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useInviteTenantUserHook>>>,
    { data: UserInviteDTO }
  > = (props) => {
    const { data } = props ?? {};

    return inviteTenantUser(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type InviteTenantUserMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useInviteTenantUserHook>>>
>;
export type InviteTenantUserMutationBody = UserInviteDTO;
export type InviteTenantUserMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Invite a tenant user
 */
export const useInviteTenantUser = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useInviteTenantUserHook>>>,
      TError,
      { data: UserInviteDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useInviteTenantUserHook>>>,
  TError,
  { data: UserInviteDTO },
  TContext
> => {
  const mutationOptions = useInviteTenantUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Returns organization users (admins and viewers) grouped by organization ID
 * @summary List tenant organization users
 */
export const useListTenantOrganizationUsersHook = () => {
  const listTenantOrganizationUsers =
    useAxiosAlltix<OrganizationUsersResponseDTO>();

  return useCallback(
    (params?: ListTenantOrganizationUsersParams, signal?: AbortSignal) => {
      return listTenantOrganizationUsers({
        url: `/tenant/organization-users`,
        method: 'GET',
        params,
        signal,
      });
    },
    [listTenantOrganizationUsers],
  );
};

export const getListTenantOrganizationUsersQueryKey = (
  params?: ListTenantOrganizationUsersParams,
) => {
  return [`/tenant/organization-users`, ...(params ? [params] : [])] as const;
};

export const useListTenantOrganizationUsersQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantOrganizationUsersHook>>
  >,
  TError = ErrorType<ForbiddenResponse>,
>(
  params?: ListTenantOrganizationUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListTenantOrganizationUsersHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListTenantOrganizationUsersQueryKey(params);

  const listTenantOrganizationUsers = useListTenantOrganizationUsersHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListTenantOrganizationUsersHook>>>
  > = ({ signal }) => listTenantOrganizationUsers(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListTenantOrganizationUsersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListTenantOrganizationUsersQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListTenantOrganizationUsersHook>>>
>;
export type ListTenantOrganizationUsersQueryError =
  ErrorType<ForbiddenResponse>;

export function useListTenantOrganizationUsers<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantOrganizationUsersHook>>
  >,
  TError = ErrorType<ForbiddenResponse>,
>(
  params: undefined | ListTenantOrganizationUsersParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListTenantOrganizationUsersHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListTenantOrganizationUsersHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListTenantOrganizationUsersHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListTenantOrganizationUsers<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantOrganizationUsersHook>>
  >,
  TError = ErrorType<ForbiddenResponse>,
>(
  params?: ListTenantOrganizationUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListTenantOrganizationUsersHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListTenantOrganizationUsersHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListTenantOrganizationUsersHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListTenantOrganizationUsers<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantOrganizationUsersHook>>
  >,
  TError = ErrorType<ForbiddenResponse>,
>(
  params?: ListTenantOrganizationUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListTenantOrganizationUsersHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List tenant organization users
 */

export function useListTenantOrganizationUsers<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantOrganizationUsersHook>>
  >,
  TError = ErrorType<ForbiddenResponse>,
>(
  params?: ListTenantOrganizationUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListTenantOrganizationUsersHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListTenantOrganizationUsersQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns the ticket claims on school collections across all organizations in the tenant
 * @summary List school collection ticket claims for the tenant
 */
export const useListTenantCollectionTicketClaimsHook = () => {
  const listTenantCollectionTicketClaims =
    useAxiosAlltix<CollectionTicketClaimDTO[]>();

  return useCallback(
    (params?: ListTenantCollectionTicketClaimsParams, signal?: AbortSignal) => {
      return listTenantCollectionTicketClaims({
        url: `/tenant/collection-ticket-claims`,
        method: 'GET',
        params,
        signal,
      });
    },
    [listTenantCollectionTicketClaims],
  );
};

export const getListTenantCollectionTicketClaimsQueryKey = (
  params?: ListTenantCollectionTicketClaimsParams,
) => {
  return [
    `/tenant/collection-ticket-claims`,
    ...(params ? [params] : []),
  ] as const;
};

export const useListTenantCollectionTicketClaimsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantCollectionTicketClaimsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: ListTenantCollectionTicketClaimsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListTenantCollectionTicketClaimsHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListTenantCollectionTicketClaimsQueryKey(params);

  const listTenantCollectionTicketClaims =
    useListTenantCollectionTicketClaimsHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<ReturnType<typeof useListTenantCollectionTicketClaimsHook>>
    >
  > = ({ signal }) => listTenantCollectionTicketClaims(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<
      ReturnType<ReturnType<typeof useListTenantCollectionTicketClaimsHook>>
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListTenantCollectionTicketClaimsQueryResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useListTenantCollectionTicketClaimsHook>>
  >
>;
export type ListTenantCollectionTicketClaimsQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useListTenantCollectionTicketClaims<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantCollectionTicketClaimsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: undefined | ListTenantCollectionTicketClaimsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListTenantCollectionTicketClaimsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useListTenantCollectionTicketClaimsHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useListTenantCollectionTicketClaimsHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListTenantCollectionTicketClaims<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantCollectionTicketClaimsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: ListTenantCollectionTicketClaimsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListTenantCollectionTicketClaimsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useListTenantCollectionTicketClaimsHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useListTenantCollectionTicketClaimsHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListTenantCollectionTicketClaims<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantCollectionTicketClaimsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: ListTenantCollectionTicketClaimsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListTenantCollectionTicketClaimsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List school collection ticket claims for the tenant
 */

export function useListTenantCollectionTicketClaims<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantCollectionTicketClaimsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: ListTenantCollectionTicketClaimsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListTenantCollectionTicketClaimsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListTenantCollectionTicketClaimsQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Creates one ticket claim per organization on that organization's collection of the given school type
 * @summary Create school collection ticket claims for several organizations
 */
export const useCreateTenantCollectionTicketClaimsHook = () => {
  const createTenantCollectionTicketClaims =
    useAxiosAlltix<CollectionTicketClaimDTO[]>();

  return useCallback(
    (
      tenantCollectionTicketClaimsCreateDTO: TenantCollectionTicketClaimsCreateDTO,
      signal?: AbortSignal,
    ) => {
      return createTenantCollectionTicketClaims({
        url: `/tenant/collection-ticket-claims`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: tenantCollectionTicketClaimsCreateDTO,
        signal,
      });
    },
    [createTenantCollectionTicketClaims],
  );
};

export const useCreateTenantCollectionTicketClaimsMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | NotImplementedResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useCreateTenantCollectionTicketClaimsHook>>
    >,
    TError,
    { data: TenantCollectionTicketClaimsCreateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<
    ReturnType<ReturnType<typeof useCreateTenantCollectionTicketClaimsHook>>
  >,
  TError,
  { data: TenantCollectionTicketClaimsCreateDTO },
  TContext
> => {
  const mutationKey = ['createTenantCollectionTicketClaims'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createTenantCollectionTicketClaims =
    useCreateTenantCollectionTicketClaimsHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useCreateTenantCollectionTicketClaimsHook>>
    >,
    { data: TenantCollectionTicketClaimsCreateDTO }
  > = (props) => {
    const { data } = props ?? {};

    return createTenantCollectionTicketClaims(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateTenantCollectionTicketClaimsMutationResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useCreateTenantCollectionTicketClaimsHook>>
  >
>;
export type CreateTenantCollectionTicketClaimsMutationBody =
  TenantCollectionTicketClaimsCreateDTO;
export type CreateTenantCollectionTicketClaimsMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | NotImplementedResponse
>;

/**
 * @summary Create school collection ticket claims for several organizations
 */
export const useCreateTenantCollectionTicketClaims = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | NotImplementedResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useCreateTenantCollectionTicketClaimsHook>>
      >,
      TError,
      { data: TenantCollectionTicketClaimsCreateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<ReturnType<typeof useCreateTenantCollectionTicketClaimsHook>>
  >,
  TError,
  { data: TenantCollectionTicketClaimsCreateDTO },
  TContext
> => {
  const mutationOptions =
    useCreateTenantCollectionTicketClaimsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Updates the validity dates and, when given, the non-invoiced flag of the listed ticket claims.

`data` replaces both validity dates of every listed claim: an omitted `valid_from` or `valid_to`
clears that date rather than keeping it. Send the current dates back to leave them unchanged,
including when only `non_invoiced` is being changed. Clearing `valid_from` makes a pending claim
active immediately, after which its `non_invoiced` flag can no longer be changed.

 * @summary Update several school collection ticket claims
 */
export const useUpdateTenantCollectionTicketClaimsHook = () => {
  const updateTenantCollectionTicketClaims =
    useAxiosAlltix<CollectionTicketClaimDTO[]>();

  return useCallback(
    (
      tenantCollectionTicketClaimsUpdateDTO: TenantCollectionTicketClaimsUpdateDTO,
    ) => {
      return updateTenantCollectionTicketClaims({
        url: `/tenant/collection-ticket-claims`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: tenantCollectionTicketClaimsUpdateDTO,
      });
    },
    [updateTenantCollectionTicketClaims],
  );
};

export const useUpdateTenantCollectionTicketClaimsMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useUpdateTenantCollectionTicketClaimsHook>>
    >,
    TError,
    { data: TenantCollectionTicketClaimsUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<
    ReturnType<ReturnType<typeof useUpdateTenantCollectionTicketClaimsHook>>
  >,
  TError,
  { data: TenantCollectionTicketClaimsUpdateDTO },
  TContext
> => {
  const mutationKey = ['updateTenantCollectionTicketClaims'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateTenantCollectionTicketClaims =
    useUpdateTenantCollectionTicketClaimsHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useUpdateTenantCollectionTicketClaimsHook>>
    >,
    { data: TenantCollectionTicketClaimsUpdateDTO }
  > = (props) => {
    const { data } = props ?? {};

    return updateTenantCollectionTicketClaims(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateTenantCollectionTicketClaimsMutationResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useUpdateTenantCollectionTicketClaimsHook>>
  >
>;
export type UpdateTenantCollectionTicketClaimsMutationBody =
  TenantCollectionTicketClaimsUpdateDTO;
export type UpdateTenantCollectionTicketClaimsMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Update several school collection ticket claims
 */
export const useUpdateTenantCollectionTicketClaims = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useUpdateTenantCollectionTicketClaimsHook>>
      >,
      TError,
      { data: TenantCollectionTicketClaimsUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<ReturnType<typeof useUpdateTenantCollectionTicketClaimsHook>>
  >,
  TError,
  { data: TenantCollectionTicketClaimsUpdateDTO },
  TContext
> => {
  const mutationOptions =
    useUpdateTenantCollectionTicketClaimsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Deletes the listed ticket claims
 * @summary Delete several school collection ticket claims
 */
export const useDeleteTenantCollectionTicketClaimsHook = () => {
  const deleteTenantCollectionTicketClaims =
    useAxiosAlltix<CollectionTicketClaimDTO[]>();

  return useCallback(
    (
      tenantCollectionTicketClaimsDeleteDTO: TenantCollectionTicketClaimsDeleteDTO,
    ) => {
      return deleteTenantCollectionTicketClaims({
        url: `/tenant/collection-ticket-claims`,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        data: tenantCollectionTicketClaimsDeleteDTO,
      });
    },
    [deleteTenantCollectionTicketClaims],
  );
};

export const useDeleteTenantCollectionTicketClaimsMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | NotImplementedResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useDeleteTenantCollectionTicketClaimsHook>>
    >,
    TError,
    { data: TenantCollectionTicketClaimsDeleteDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<
    ReturnType<ReturnType<typeof useDeleteTenantCollectionTicketClaimsHook>>
  >,
  TError,
  { data: TenantCollectionTicketClaimsDeleteDTO },
  TContext
> => {
  const mutationKey = ['deleteTenantCollectionTicketClaims'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteTenantCollectionTicketClaims =
    useDeleteTenantCollectionTicketClaimsHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useDeleteTenantCollectionTicketClaimsHook>>
    >,
    { data: TenantCollectionTicketClaimsDeleteDTO }
  > = (props) => {
    const { data } = props ?? {};

    return deleteTenantCollectionTicketClaims(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTenantCollectionTicketClaimsMutationResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useDeleteTenantCollectionTicketClaimsHook>>
  >
>;
export type DeleteTenantCollectionTicketClaimsMutationBody =
  TenantCollectionTicketClaimsDeleteDTO;
export type DeleteTenantCollectionTicketClaimsMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | NotImplementedResponse
>;

/**
 * @summary Delete several school collection ticket claims
 */
export const useDeleteTenantCollectionTicketClaims = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | NotImplementedResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useDeleteTenantCollectionTicketClaimsHook>>
      >,
      TError,
      { data: TenantCollectionTicketClaimsDeleteDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<ReturnType<typeof useDeleteTenantCollectionTicketClaimsHook>>
  >,
  TError,
  { data: TenantCollectionTicketClaimsDeleteDTO },
  TContext
> => {
  const mutationOptions =
    useDeleteTenantCollectionTicketClaimsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get a list of all organizations for the current tenant
 * @summary List organizations
 */
export const useListOrganizationsHook = () => {
  const listOrganizations = useAxiosAlltix<OrganizationDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listOrganizations({
        url: `/organizations`,
        method: 'GET',
        signal,
      });
    },
    [listOrganizations],
  );
};

export const getListOrganizationsQueryKey = () => {
  return [`/organizations`] as const;
};

export const useListOrganizationsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListOrganizationsQueryKey();

  const listOrganizations = useListOrganizationsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>
  > = ({ signal }) => listOrganizations(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListOrganizationsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>
>;
export type ListOrganizationsQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useListOrganizations<
  TData = Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListOrganizations<
  TData = Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListOrganizations<
  TData = Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List organizations
 */

export function useListOrganizations<
  TData = Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListOrganizationsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get the current users organization
 * @summary Get organization by id
 */
export const useGetOrganizationByIDHook = () => {
  const getOrganizationByID = useAxiosAlltix<OrganizationDTO>();

  return useCallback(
    (organizationID: string, signal?: AbortSignal) => {
      return getOrganizationByID({
        url: `/organizations/${organizationID}`,
        method: 'GET',
        signal,
      });
    },
    [getOrganizationByID],
  );
};

export const getGetOrganizationByIDQueryKey = (organizationID?: string) => {
  return [`/organizations/${organizationID}`] as const;
};

export const useGetOrganizationByIDQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetOrganizationByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  organizationID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetOrganizationByIDHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetOrganizationByIDQueryKey(organizationID);

  const getOrganizationByID = useGetOrganizationByIDHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetOrganizationByIDHook>>>
  > = ({ signal }) => getOrganizationByID(organizationID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!organizationID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetOrganizationByIDHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetOrganizationByIDQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetOrganizationByIDHook>>>
>;
export type GetOrganizationByIDQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetOrganizationByID<
  TData = Awaited<ReturnType<ReturnType<typeof useGetOrganizationByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  organizationID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetOrganizationByIDHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetOrganizationByIDHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetOrganizationByIDHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetOrganizationByID<
  TData = Awaited<ReturnType<ReturnType<typeof useGetOrganizationByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  organizationID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetOrganizationByIDHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetOrganizationByIDHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetOrganizationByIDHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrganizationByID<
  TData = Awaited<ReturnType<ReturnType<typeof useGetOrganizationByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  organizationID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetOrganizationByIDHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get organization by id
 */

export function useGetOrganizationByID<
  TData = Awaited<ReturnType<ReturnType<typeof useGetOrganizationByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  organizationID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetOrganizationByIDHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetOrganizationByIDQueryOptions(
    organizationID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Update the current users organization
 * @summary Update organization by id
 */
export const useUpdateOrganizationByIDHook = () => {
  const updateOrganizationByID = useAxiosAlltix<OrganizationDTO>();

  return useCallback(
    (organizationID: string, organizationUpdateDTO: OrganizationUpdateDTO) => {
      return updateOrganizationByID({
        url: `/organizations/${organizationID}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: organizationUpdateDTO,
      });
    },
    [updateOrganizationByID],
  );
};

export const useUpdateOrganizationByIDMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationByIDHook>>>,
    TError,
    { organizationID: string; data: OrganizationUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationByIDHook>>>,
  TError,
  { organizationID: string; data: OrganizationUpdateDTO },
  TContext
> => {
  const mutationKey = ['updateOrganizationByID'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateOrganizationByID = useUpdateOrganizationByIDHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationByIDHook>>>,
    { organizationID: string; data: OrganizationUpdateDTO }
  > = (props) => {
    const { organizationID, data } = props ?? {};

    return updateOrganizationByID(organizationID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateOrganizationByIDMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationByIDHook>>>
>;
export type UpdateOrganizationByIDMutationBody = OrganizationUpdateDTO;
export type UpdateOrganizationByIDMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Update organization by id
 */
export const useUpdateOrganizationByID = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationByIDHook>>>,
      TError,
      { organizationID: string; data: OrganizationUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationByIDHook>>>,
  TError,
  { organizationID: string; data: OrganizationUpdateDTO },
  TContext
> => {
  const mutationOptions = useUpdateOrganizationByIDMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Create a temporary admin user-profile for yourself
 * @summary Create a temporary admin user-profile for yourself
 */
export const useImpersonateOrganizationAdminHook = () => {
  const impersonateOrganizationAdmin =
    useAxiosAlltix<ImpersonateAdminResultDTO>();

  return useCallback(
    (organizationID: string, signal?: AbortSignal) => {
      return impersonateOrganizationAdmin({
        url: `/organizations/${organizationID}/impersonate-admin`,
        method: 'POST',
        signal,
      });
    },
    [impersonateOrganizationAdmin],
  );
};

export const useImpersonateOrganizationAdminMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useImpersonateOrganizationAdminHook>>>,
    TError,
    { organizationID: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useImpersonateOrganizationAdminHook>>>,
  TError,
  { organizationID: string },
  TContext
> => {
  const mutationKey = ['impersonateOrganizationAdmin'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const impersonateOrganizationAdmin = useImpersonateOrganizationAdminHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useImpersonateOrganizationAdminHook>>>,
    { organizationID: string }
  > = (props) => {
    const { organizationID } = props ?? {};

    return impersonateOrganizationAdmin(organizationID);
  };

  return { mutationFn, ...mutationOptions };
};

export type ImpersonateOrganizationAdminMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useImpersonateOrganizationAdminHook>>>
>;

export type ImpersonateOrganizationAdminMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Create a temporary admin user-profile for yourself
 */
export const useImpersonateOrganizationAdmin = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useImpersonateOrganizationAdminHook>>
      >,
      TError,
      { organizationID: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useImpersonateOrganizationAdminHook>>>,
  TError,
  { organizationID: string },
  TContext
> => {
  const mutationOptions =
    useImpersonateOrganizationAdminMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Sets the allowed tags for the organization
 * @summary Set allowed tags
 */
export const useSetAllowedOrganizationTagsHook = () => {
  const setAllowedOrganizationTags =
    useAxiosAlltix<OrganizationAllowedBobcatTagDTO[]>();

  return useCallback(
    (
      organizationID: string,
      setAllowedOrganizationTagsBodies: SetAllowedOrganizationTagsBodies,
      signal?: AbortSignal,
    ) => {
      return setAllowedOrganizationTags({
        url: `/organizations/${organizationID}/tags`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: setAllowedOrganizationTagsBodies,
        signal,
      });
    },
    [setAllowedOrganizationTags],
  );
};

export const useSetAllowedOrganizationTagsMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSetAllowedOrganizationTagsHook>>>,
    TError,
    { organizationID: string; data: SetAllowedOrganizationTagsBodies },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useSetAllowedOrganizationTagsHook>>>,
  TError,
  { organizationID: string; data: SetAllowedOrganizationTagsBodies },
  TContext
> => {
  const mutationKey = ['setAllowedOrganizationTags'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const setAllowedOrganizationTags = useSetAllowedOrganizationTagsHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useSetAllowedOrganizationTagsHook>>>,
    { organizationID: string; data: SetAllowedOrganizationTagsBodies }
  > = (props) => {
    const { organizationID, data } = props ?? {};

    return setAllowedOrganizationTags(organizationID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SetAllowedOrganizationTagsMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSetAllowedOrganizationTagsHook>>>
>;
export type SetAllowedOrganizationTagsMutationBody =
  SetAllowedOrganizationTagsBodies;
export type SetAllowedOrganizationTagsMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Set allowed tags
 */
export const useSetAllowedOrganizationTags = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useSetAllowedOrganizationTagsHook>>>,
      TError,
      { organizationID: string; data: SetAllowedOrganizationTagsBodies },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useSetAllowedOrganizationTagsHook>>>,
  TError,
  { organizationID: string; data: SetAllowedOrganizationTagsBodies },
  TContext
> => {
  const mutationOptions = useSetAllowedOrganizationTagsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get the organization allowed tags
 * @summary Get allowed tags
 */
export const useGetAllowedOrganizationTagsHook = () => {
  const getAllowedOrganizationTags =
    useAxiosAlltix<OrganizationAllowedBobcatTagDTO[]>();

  return useCallback(
    (organizationID: string, signal?: AbortSignal) => {
      return getAllowedOrganizationTags({
        url: `/organizations/${organizationID}/tags`,
        method: 'GET',
        signal,
      });
    },
    [getAllowedOrganizationTags],
  );
};

export const getGetAllowedOrganizationTagsQueryKey = (
  organizationID?: string,
) => {
  return [`/organizations/${organizationID}/tags`] as const;
};

export const useGetAllowedOrganizationTagsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAllowedOrganizationTagsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  organizationID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAllowedOrganizationTagsHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetAllowedOrganizationTagsQueryKey(organizationID);

  const getAllowedOrganizationTags = useGetAllowedOrganizationTagsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetAllowedOrganizationTagsHook>>>
  > = ({ signal }) => getAllowedOrganizationTags(organizationID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!organizationID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetAllowedOrganizationTagsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetAllowedOrganizationTagsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetAllowedOrganizationTagsHook>>>
>;
export type GetAllowedOrganizationTagsQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetAllowedOrganizationTags<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAllowedOrganizationTagsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  organizationID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAllowedOrganizationTagsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetAllowedOrganizationTagsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetAllowedOrganizationTagsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetAllowedOrganizationTags<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAllowedOrganizationTagsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  organizationID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAllowedOrganizationTagsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetAllowedOrganizationTagsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetAllowedOrganizationTagsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetAllowedOrganizationTags<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAllowedOrganizationTagsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  organizationID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAllowedOrganizationTagsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get allowed tags
 */

export function useGetAllowedOrganizationTags<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAllowedOrganizationTagsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  organizationID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAllowedOrganizationTagsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetAllowedOrganizationTagsQueryOptions(
    organizationID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Sets the allowed offer groups for the organization
 * @summary Set allowed offer groups
 */
export const useSetAllowedOrganizationOfferGroupsHook = () => {
  const setAllowedOrganizationOfferGroups =
    useAxiosAlltix<OrganizationAllowedBobcatOfferGroupDTO[]>();

  return useCallback(
    (
      organizationID: string,
      setAllowedOrganizationOfferGroupsBodies: SetAllowedOrganizationOfferGroupsBodies,
      signal?: AbortSignal,
    ) => {
      return setAllowedOrganizationOfferGroups({
        url: `/organizations/${organizationID}/offer-groups`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: setAllowedOrganizationOfferGroupsBodies,
        signal,
      });
    },
    [setAllowedOrganizationOfferGroups],
  );
};

export const useSetAllowedOrganizationOfferGroupsMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useSetAllowedOrganizationOfferGroupsHook>>
    >,
    TError,
    { organizationID: string; data: SetAllowedOrganizationOfferGroupsBodies },
    TContext
  >;
}): UseMutationOptions<
  Awaited<
    ReturnType<ReturnType<typeof useSetAllowedOrganizationOfferGroupsHook>>
  >,
  TError,
  { organizationID: string; data: SetAllowedOrganizationOfferGroupsBodies },
  TContext
> => {
  const mutationKey = ['setAllowedOrganizationOfferGroups'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const setAllowedOrganizationOfferGroups =
    useSetAllowedOrganizationOfferGroupsHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useSetAllowedOrganizationOfferGroupsHook>>
    >,
    { organizationID: string; data: SetAllowedOrganizationOfferGroupsBodies }
  > = (props) => {
    const { organizationID, data } = props ?? {};

    return setAllowedOrganizationOfferGroups(organizationID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SetAllowedOrganizationOfferGroupsMutationResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useSetAllowedOrganizationOfferGroupsHook>>
  >
>;
export type SetAllowedOrganizationOfferGroupsMutationBody =
  SetAllowedOrganizationOfferGroupsBodies;
export type SetAllowedOrganizationOfferGroupsMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Set allowed offer groups
 */
export const useSetAllowedOrganizationOfferGroups = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useSetAllowedOrganizationOfferGroupsHook>>
      >,
      TError,
      { organizationID: string; data: SetAllowedOrganizationOfferGroupsBodies },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<ReturnType<typeof useSetAllowedOrganizationOfferGroupsHook>>
  >,
  TError,
  { organizationID: string; data: SetAllowedOrganizationOfferGroupsBodies },
  TContext
> => {
  const mutationOptions =
    useSetAllowedOrganizationOfferGroupsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get the organization allowed offer groups
 * @summary Get allowed offer groups
 */
export const useGetAllowedOrganizationOfferGroupsHook = () => {
  const getAllowedOrganizationOfferGroups =
    useAxiosAlltix<OrganizationAllowedBobcatOfferGroupDTO[]>();

  return useCallback(
    (organizationID: string, signal?: AbortSignal) => {
      return getAllowedOrganizationOfferGroups({
        url: `/organizations/${organizationID}/offer-groups`,
        method: 'GET',
        signal,
      });
    },
    [getAllowedOrganizationOfferGroups],
  );
};

export const getGetAllowedOrganizationOfferGroupsQueryKey = (
  organizationID?: string,
) => {
  return [`/organizations/${organizationID}/offer-groups`] as const;
};

export const useGetAllowedOrganizationOfferGroupsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAllowedOrganizationOfferGroupsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  organizationID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetAllowedOrganizationOfferGroupsHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetAllowedOrganizationOfferGroupsQueryKey(organizationID);

  const getAllowedOrganizationOfferGroups =
    useGetAllowedOrganizationOfferGroupsHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<ReturnType<typeof useGetAllowedOrganizationOfferGroupsHook>>
    >
  > = ({ signal }) => getAllowedOrganizationOfferGroups(organizationID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!organizationID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<
      ReturnType<ReturnType<typeof useGetAllowedOrganizationOfferGroupsHook>>
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetAllowedOrganizationOfferGroupsQueryResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useGetAllowedOrganizationOfferGroupsHook>>
  >
>;
export type GetAllowedOrganizationOfferGroupsQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetAllowedOrganizationOfferGroups<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAllowedOrganizationOfferGroupsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  organizationID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetAllowedOrganizationOfferGroupsHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useGetAllowedOrganizationOfferGroupsHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useGetAllowedOrganizationOfferGroupsHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetAllowedOrganizationOfferGroups<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAllowedOrganizationOfferGroupsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  organizationID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetAllowedOrganizationOfferGroupsHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useGetAllowedOrganizationOfferGroupsHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useGetAllowedOrganizationOfferGroupsHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetAllowedOrganizationOfferGroups<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAllowedOrganizationOfferGroupsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  organizationID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetAllowedOrganizationOfferGroupsHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get allowed offer groups
 */

export function useGetAllowedOrganizationOfferGroups<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAllowedOrganizationOfferGroupsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  organizationID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetAllowedOrganizationOfferGroupsHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetAllowedOrganizationOfferGroupsQueryOptions(
    organizationID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Create a new payer with details
 * @summary Create payer with details
 */
export const useCreatePayerWithDetailsHook = () => {
  const createPayerWithDetails = useAxiosAlltix<PayerDTO>();

  return useCallback(
    (
      organizationID: string,
      payerCreateWithDetailsDTO: PayerCreateWithDetailsDTO,
      signal?: AbortSignal,
    ) => {
      return createPayerWithDetails({
        url: `/organizations/${organizationID}/payers`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: payerCreateWithDetailsDTO,
        signal,
      });
    },
    [createPayerWithDetails],
  );
};

export const useCreatePayerWithDetailsMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreatePayerWithDetailsHook>>>,
    TError,
    { organizationID: string; data: PayerCreateWithDetailsDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreatePayerWithDetailsHook>>>,
  TError,
  { organizationID: string; data: PayerCreateWithDetailsDTO },
  TContext
> => {
  const mutationKey = ['createPayerWithDetails'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createPayerWithDetails = useCreatePayerWithDetailsHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreatePayerWithDetailsHook>>>,
    { organizationID: string; data: PayerCreateWithDetailsDTO }
  > = (props) => {
    const { organizationID, data } = props ?? {};

    return createPayerWithDetails(organizationID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreatePayerWithDetailsMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreatePayerWithDetailsHook>>>
>;
export type CreatePayerWithDetailsMutationBody = PayerCreateWithDetailsDTO;
export type CreatePayerWithDetailsMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Create payer with details
 */
export const useCreatePayerWithDetails = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreatePayerWithDetailsHook>>>,
      TError,
      { organizationID: string; data: PayerCreateWithDetailsDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreatePayerWithDetailsHook>>>,
  TError,
  { organizationID: string; data: PayerCreateWithDetailsDTO },
  TContext
> => {
  const mutationOptions = useCreatePayerWithDetailsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Activate or inactivate an organization
 * @summary Update organization state
 */
export const usePatchOrganizationStateHook = () => {
  const patchOrganizationState = useAxiosAlltix<OrganizationDTO>();

  return useCallback(
    (
      organizationID: string,
      organizationPatchStateDTO: OrganizationPatchStateDTO,
    ) => {
      return patchOrganizationState({
        url: `/organizations/${organizationID}/state`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: organizationPatchStateDTO,
      });
    },
    [patchOrganizationState],
  );
};

export const usePatchOrganizationStateMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePatchOrganizationStateHook>>>,
    TError,
    { organizationID: string; data: OrganizationPatchStateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePatchOrganizationStateHook>>>,
  TError,
  { organizationID: string; data: OrganizationPatchStateDTO },
  TContext
> => {
  const mutationKey = ['patchOrganizationState'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const patchOrganizationState = usePatchOrganizationStateHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePatchOrganizationStateHook>>>,
    { organizationID: string; data: OrganizationPatchStateDTO }
  > = (props) => {
    const { organizationID, data } = props ?? {};

    return patchOrganizationState(organizationID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PatchOrganizationStateMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePatchOrganizationStateHook>>>
>;
export type PatchOrganizationStateMutationBody = OrganizationPatchStateDTO;
export type PatchOrganizationStateMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Update organization state
 */
export const usePatchOrganizationState = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePatchOrganizationStateHook>>>,
      TError,
      { organizationID: string; data: OrganizationPatchStateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePatchOrganizationStateHook>>>,
  TError,
  { organizationID: string; data: OrganizationPatchStateDTO },
  TContext
> => {
  const mutationOptions = usePatchOrganizationStateMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get the current users organization
 * @summary Get current organization
 */
export const useGetCurrentOrganizationHook = () => {
  const getCurrentOrganization = useAxiosAlltix<OrganizationDTO>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getCurrentOrganization({
        url: `/organization`,
        method: 'GET',
        signal,
      });
    },
    [getCurrentOrganization],
  );
};

export const getGetCurrentOrganizationQueryKey = () => {
  return [`/organization`] as const;
};

export const useGetCurrentOrganizationQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentOrganizationHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useGetCurrentOrganizationHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetCurrentOrganizationQueryKey();

  const getCurrentOrganization = useGetCurrentOrganizationHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetCurrentOrganizationHook>>>
  > = ({ signal }) => getCurrentOrganization(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetCurrentOrganizationHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetCurrentOrganizationQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetCurrentOrganizationHook>>>
>;
export type GetCurrentOrganizationQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetCurrentOrganization<
  TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentOrganizationHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCurrentOrganizationHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetCurrentOrganizationHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetCurrentOrganizationHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetCurrentOrganization<
  TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentOrganizationHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCurrentOrganizationHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetCurrentOrganizationHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetCurrentOrganizationHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetCurrentOrganization<
  TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentOrganizationHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCurrentOrganizationHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get current organization
 */

export function useGetCurrentOrganization<
  TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentOrganizationHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCurrentOrganizationHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetCurrentOrganizationQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Manually create a new organization
 * @summary Create organization manually
 */
export const useCreateOrganizationManualHook = () => {
  const createOrganizationManual = useAxiosAlltix<OrganizationDTO>();

  return useCallback(
    (
      organizationCreateManualDTO: OrganizationCreateManualDTO,
      signal?: AbortSignal,
    ) => {
      return createOrganizationManual({
        url: `/organization`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: organizationCreateManualDTO,
        signal,
      });
    },
    [createOrganizationManual],
  );
};

export const useCreateOrganizationManualMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateOrganizationManualHook>>>,
    TError,
    { data: OrganizationCreateManualDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateOrganizationManualHook>>>,
  TError,
  { data: OrganizationCreateManualDTO },
  TContext
> => {
  const mutationKey = ['createOrganizationManual'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createOrganizationManual = useCreateOrganizationManualHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateOrganizationManualHook>>>,
    { data: OrganizationCreateManualDTO }
  > = (props) => {
    const { data } = props ?? {};

    return createOrganizationManual(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateOrganizationManualMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateOrganizationManualHook>>>
>;
export type CreateOrganizationManualMutationBody = OrganizationCreateManualDTO;
export type CreateOrganizationManualMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Create organization manually
 */
export const useCreateOrganizationManual = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateOrganizationManualHook>>>,
      TError,
      { data: OrganizationCreateManualDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateOrganizationManualHook>>>,
  TError,
  { data: OrganizationCreateManualDTO },
  TContext
> => {
  const mutationOptions = useCreateOrganizationManualMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get the travellers per organization
 * @summary Get travellers per organization
 */
export const useGetTravellersPerOrganizationHook = () => {
  const getTravellersPerOrganization =
    useAxiosAlltix<TravellerWithCollectionMembershipsDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getTravellersPerOrganization({
        url: `/organization/travellers`,
        method: 'GET',
        signal,
      });
    },
    [getTravellersPerOrganization],
  );
};

export const getGetTravellersPerOrganizationQueryKey = () => {
  return [`/organization/travellers`] as const;
};

export const useGetTravellersPerOrganizationQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTravellersPerOrganizationHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<
        ReturnType<ReturnType<typeof useGetTravellersPerOrganizationHook>>
      >,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTravellersPerOrganizationQueryKey();

  const getTravellersPerOrganization = useGetTravellersPerOrganizationHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetTravellersPerOrganizationHook>>>
  > = ({ signal }) => getTravellersPerOrganization(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTravellersPerOrganizationHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetTravellersPerOrganizationQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTravellersPerOrganizationHook>>>
>;
export type GetTravellersPerOrganizationQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetTravellersPerOrganization<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTravellersPerOrganizationHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTravellersPerOrganizationHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetTravellersPerOrganizationHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetTravellersPerOrganizationHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetTravellersPerOrganization<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTravellersPerOrganizationHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTravellersPerOrganizationHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetTravellersPerOrganizationHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetTravellersPerOrganizationHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetTravellersPerOrganization<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTravellersPerOrganizationHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTravellersPerOrganizationHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get travellers per organization
 */

export function useGetTravellersPerOrganization<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTravellersPerOrganizationHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTravellersPerOrganizationHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetTravellersPerOrganizationQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Lists all users in the organization
 * @summary List organization users
 */
export const useListOrganizationUsersHook = () => {
  const listOrganizationUsers = useAxiosAlltix<UserDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listOrganizationUsers({
        url: `/organization/users`,
        method: 'GET',
        signal,
      });
    },
    [listOrganizationUsers],
  );
};

export const getListOrganizationUsersQueryKey = () => {
  return [`/organization/users`] as const;
};

export const useListOrganizationUsersQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListOrganizationUsersHook>>>,
  TError = ErrorType<ForbiddenResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListOrganizationUsersHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListOrganizationUsersQueryKey();

  const listOrganizationUsers = useListOrganizationUsersHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListOrganizationUsersHook>>>
  > = ({ signal }) => listOrganizationUsers(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListOrganizationUsersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListOrganizationUsersQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListOrganizationUsersHook>>>
>;
export type ListOrganizationUsersQueryError = ErrorType<ForbiddenResponse>;

export function useListOrganizationUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useListOrganizationUsersHook>>>,
  TError = ErrorType<ForbiddenResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationUsersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListOrganizationUsersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListOrganizationUsersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListOrganizationUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useListOrganizationUsersHook>>>,
  TError = ErrorType<ForbiddenResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationUsersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListOrganizationUsersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListOrganizationUsersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListOrganizationUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useListOrganizationUsersHook>>>,
  TError = ErrorType<ForbiddenResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationUsersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List organization users
 */

export function useListOrganizationUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useListOrganizationUsersHook>>>,
  TError = ErrorType<ForbiddenResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationUsersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListOrganizationUsersQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get the user organization allowed offer groups
 * @summary Get user organization allowed offer groups
 */
export const useGetUserAllowedOrganizationOfferGroupsHook = () => {
  const getUserAllowedOrganizationOfferGroups =
    useAxiosAlltix<OrganizationAllowedBobcatOfferGroupDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getUserAllowedOrganizationOfferGroups({
        url: `/organization/offer-groups`,
        method: 'GET',
        signal,
      });
    },
    [getUserAllowedOrganizationOfferGroups],
  );
};

export const getGetUserAllowedOrganizationOfferGroupsQueryKey = () => {
  return [`/organization/offer-groups`] as const;
};

export const useGetUserAllowedOrganizationOfferGroupsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetUserAllowedOrganizationOfferGroupsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<
        ReturnType<
          ReturnType<typeof useGetUserAllowedOrganizationOfferGroupsHook>
        >
      >,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetUserAllowedOrganizationOfferGroupsQueryKey();

  const getUserAllowedOrganizationOfferGroups =
    useGetUserAllowedOrganizationOfferGroupsHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<
        ReturnType<typeof useGetUserAllowedOrganizationOfferGroupsHook>
      >
    >
  > = ({ signal }) => getUserAllowedOrganizationOfferGroups(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<
      ReturnType<
        ReturnType<typeof useGetUserAllowedOrganizationOfferGroupsHook>
      >
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetUserAllowedOrganizationOfferGroupsQueryResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useGetUserAllowedOrganizationOfferGroupsHook>>
  >
>;
export type GetUserAllowedOrganizationOfferGroupsQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetUserAllowedOrganizationOfferGroups<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetUserAllowedOrganizationOfferGroupsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetUserAllowedOrganizationOfferGroupsHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useGetUserAllowedOrganizationOfferGroupsHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useGetUserAllowedOrganizationOfferGroupsHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetUserAllowedOrganizationOfferGroups<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetUserAllowedOrganizationOfferGroupsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetUserAllowedOrganizationOfferGroupsHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useGetUserAllowedOrganizationOfferGroupsHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useGetUserAllowedOrganizationOfferGroupsHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetUserAllowedOrganizationOfferGroups<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetUserAllowedOrganizationOfferGroupsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetUserAllowedOrganizationOfferGroupsHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get user organization allowed offer groups
 */

export function useGetUserAllowedOrganizationOfferGroups<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetUserAllowedOrganizationOfferGroupsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetUserAllowedOrganizationOfferGroupsHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions =
    useGetUserAllowedOrganizationOfferGroupsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get the user organization allowed tags
 * @summary Get user organization allowed tags
 */
export const useGetUserAllowedOrganizationTagsHook = () => {
  const getUserAllowedOrganizationTags =
    useAxiosAlltix<OrganizationAllowedBobcatTagDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getUserAllowedOrganizationTags({
        url: `/organization/tags`,
        method: 'GET',
        signal,
      });
    },
    [getUserAllowedOrganizationTags],
  );
};

export const getGetUserAllowedOrganizationTagsQueryKey = () => {
  return [`/organization/tags`] as const;
};

export const useGetUserAllowedOrganizationTagsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetUserAllowedOrganizationTagsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<
        ReturnType<ReturnType<typeof useGetUserAllowedOrganizationTagsHook>>
      >,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetUserAllowedOrganizationTagsQueryKey();

  const getUserAllowedOrganizationTags =
    useGetUserAllowedOrganizationTagsHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<ReturnType<typeof useGetUserAllowedOrganizationTagsHook>>
    >
  > = ({ signal }) => getUserAllowedOrganizationTags(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<
      ReturnType<ReturnType<typeof useGetUserAllowedOrganizationTagsHook>>
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetUserAllowedOrganizationTagsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetUserAllowedOrganizationTagsHook>>>
>;
export type GetUserAllowedOrganizationTagsQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetUserAllowedOrganizationTags<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetUserAllowedOrganizationTagsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetUserAllowedOrganizationTagsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetUserAllowedOrganizationTagsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetUserAllowedOrganizationTagsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetUserAllowedOrganizationTags<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetUserAllowedOrganizationTagsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetUserAllowedOrganizationTagsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetUserAllowedOrganizationTagsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetUserAllowedOrganizationTagsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetUserAllowedOrganizationTags<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetUserAllowedOrganizationTagsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetUserAllowedOrganizationTagsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get user organization allowed tags
 */

export function useGetUserAllowedOrganizationTags<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetUserAllowedOrganizationTagsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetUserAllowedOrganizationTagsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetUserAllowedOrganizationTagsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Invite a organization user to the system.
 * @summary Invite a organization user
 */
export const useInviteOrganizationUserHook = () => {
  const inviteOrganizationUser = useAxiosAlltix<void>();

  return useCallback(
    (userInviteDTO: UserInviteDTO, signal?: AbortSignal) => {
      return inviteOrganizationUser({
        url: `/organization/invite-user`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: userInviteDTO,
        signal,
      });
    },
    [inviteOrganizationUser],
  );
};

export const useInviteOrganizationUserMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useInviteOrganizationUserHook>>>,
    TError,
    { data: UserInviteDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useInviteOrganizationUserHook>>>,
  TError,
  { data: UserInviteDTO },
  TContext
> => {
  const mutationKey = ['inviteOrganizationUser'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const inviteOrganizationUser = useInviteOrganizationUserHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useInviteOrganizationUserHook>>>,
    { data: UserInviteDTO }
  > = (props) => {
    const { data } = props ?? {};

    return inviteOrganizationUser(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type InviteOrganizationUserMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useInviteOrganizationUserHook>>>
>;
export type InviteOrganizationUserMutationBody = UserInviteDTO;
export type InviteOrganizationUserMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Invite a organization user
 */
export const useInviteOrganizationUser = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useInviteOrganizationUserHook>>>,
      TError,
      { data: UserInviteDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useInviteOrganizationUserHook>>>,
  TError,
  { data: UserInviteDTO },
  TContext
> => {
  const mutationOptions = useInviteOrganizationUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Update organization user role as editor or viewer
 * @summary Update organization user role
 */
export const useUpdateOrganizationUserRoleHook = () => {
  const updateOrganizationUserRole = useAxiosAlltix<UserDTO>();

  return useCallback(
    (organizationUserRoleUpdateDTO: OrganizationUserRoleUpdateDTO) => {
      return updateOrganizationUserRole({
        url: `/organization/user-role`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: organizationUserRoleUpdateDTO,
      });
    },
    [updateOrganizationUserRole],
  );
};

export const useUpdateOrganizationUserRoleMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationUserRoleHook>>>,
    TError,
    { data: OrganizationUserRoleUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationUserRoleHook>>>,
  TError,
  { data: OrganizationUserRoleUpdateDTO },
  TContext
> => {
  const mutationKey = ['updateOrganizationUserRole'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateOrganizationUserRole = useUpdateOrganizationUserRoleHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationUserRoleHook>>>,
    { data: OrganizationUserRoleUpdateDTO }
  > = (props) => {
    const { data } = props ?? {};

    return updateOrganizationUserRole(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateOrganizationUserRoleMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationUserRoleHook>>>
>;
export type UpdateOrganizationUserRoleMutationBody =
  OrganizationUserRoleUpdateDTO;
export type UpdateOrganizationUserRoleMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Update organization user role
 */
export const useUpdateOrganizationUserRole = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationUserRoleHook>>>,
      TError,
      { data: OrganizationUserRoleUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationUserRoleHook>>>,
  TError,
  { data: OrganizationUserRoleUpdateDTO },
  TContext
> => {
  const mutationOptions = useUpdateOrganizationUserRoleMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get a list of all organization applications for the current tenant
 * @summary List organization applications
 */
export const useListOrganizationApplicationsHook = () => {
  const listOrganizationApplications =
    useAxiosAlltix<OrganizationApplicationDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listOrganizationApplications({
        url: `/organization-applications`,
        method: 'GET',
        signal,
      });
    },
    [listOrganizationApplications],
  );
};

export const getListOrganizationApplicationsQueryKey = () => {
  return [`/organization-applications`] as const;
};

export const useListOrganizationApplicationsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationApplicationsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<
        ReturnType<ReturnType<typeof useListOrganizationApplicationsHook>>
      >,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListOrganizationApplicationsQueryKey();

  const listOrganizationApplications = useListOrganizationApplicationsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListOrganizationApplicationsHook>>>
  > = ({ signal }) => listOrganizationApplications(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListOrganizationApplicationsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListOrganizationApplicationsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListOrganizationApplicationsHook>>>
>;
export type ListOrganizationApplicationsQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useListOrganizationApplications<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationApplicationsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListOrganizationApplicationsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListOrganizationApplicationsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListOrganizationApplicationsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListOrganizationApplications<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationApplicationsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListOrganizationApplicationsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListOrganizationApplicationsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListOrganizationApplicationsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListOrganizationApplications<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationApplicationsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListOrganizationApplicationsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List organization applications
 */

export function useListOrganizationApplications<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationApplicationsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListOrganizationApplicationsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListOrganizationApplicationsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Update the selected organization application
 * @summary Update an organization application
 */
export const useUpdateOrganizationApplicationHook = () => {
  const updateOrganizationApplication =
    useAxiosAlltix<OrganizationApplicationDTO>();

  return useCallback(
    (
      organizationApplicationID: string,
      organizationApplicationUpdateDTO: OrganizationApplicationUpdateDTO,
    ) => {
      return updateOrganizationApplication({
        url: `/organization-applications/${organizationApplicationID}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: organizationApplicationUpdateDTO,
      });
    },
    [updateOrganizationApplication],
  );
};

export const useUpdateOrganizationApplicationMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useUpdateOrganizationApplicationHook>>
    >,
    TError,
    {
      organizationApplicationID: string;
      data: OrganizationApplicationUpdateDTO;
    },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationApplicationHook>>>,
  TError,
  { organizationApplicationID: string; data: OrganizationApplicationUpdateDTO },
  TContext
> => {
  const mutationKey = ['updateOrganizationApplication'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateOrganizationApplication = useUpdateOrganizationApplicationHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useUpdateOrganizationApplicationHook>>
    >,
    {
      organizationApplicationID: string;
      data: OrganizationApplicationUpdateDTO;
    }
  > = (props) => {
    const { organizationApplicationID, data } = props ?? {};

    return updateOrganizationApplication(organizationApplicationID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateOrganizationApplicationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationApplicationHook>>>
>;
export type UpdateOrganizationApplicationMutationBody =
  OrganizationApplicationUpdateDTO;
export type UpdateOrganizationApplicationMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Update an organization application
 */
export const useUpdateOrganizationApplication = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useUpdateOrganizationApplicationHook>>
      >,
      TError,
      {
        organizationApplicationID: string;
        data: OrganizationApplicationUpdateDTO;
      },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationApplicationHook>>>,
  TError,
  { organizationApplicationID: string; data: OrganizationApplicationUpdateDTO },
  TContext
> => {
  const mutationOptions =
    useUpdateOrganizationApplicationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Approve the selected organization application
 * @summary Approve an organization application
 */
export const useApproveOrganizationApplicationHook = () => {
  const approveOrganizationApplication = useAxiosAlltix<OrganizationDTO>();

  return useCallback(
    (
      organizationApplicationID: string,
      organizationApplicationApproveDTO: OrganizationApplicationApproveDTO,
      signal?: AbortSignal,
    ) => {
      return approveOrganizationApplication({
        url: `/organization-applications/${organizationApplicationID}/approve`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: organizationApplicationApproveDTO,
        signal,
      });
    },
    [approveOrganizationApplication],
  );
};

export const useApproveOrganizationApplicationMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useApproveOrganizationApplicationHook>>
    >,
    TError,
    {
      organizationApplicationID: string;
      data: OrganizationApplicationApproveDTO;
    },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useApproveOrganizationApplicationHook>>>,
  TError,
  {
    organizationApplicationID: string;
    data: OrganizationApplicationApproveDTO;
  },
  TContext
> => {
  const mutationKey = ['approveOrganizationApplication'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const approveOrganizationApplication =
    useApproveOrganizationApplicationHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useApproveOrganizationApplicationHook>>
    >,
    {
      organizationApplicationID: string;
      data: OrganizationApplicationApproveDTO;
    }
  > = (props) => {
    const { organizationApplicationID, data } = props ?? {};

    return approveOrganizationApplication(organizationApplicationID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type ApproveOrganizationApplicationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useApproveOrganizationApplicationHook>>>
>;
export type ApproveOrganizationApplicationMutationBody =
  OrganizationApplicationApproveDTO;
export type ApproveOrganizationApplicationMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Approve an organization application
 */
export const useApproveOrganizationApplication = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useApproveOrganizationApplicationHook>>
      >,
      TError,
      {
        organizationApplicationID: string;
        data: OrganizationApplicationApproveDTO;
      },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useApproveOrganizationApplicationHook>>>,
  TError,
  {
    organizationApplicationID: string;
    data: OrganizationApplicationApproveDTO;
  },
  TContext
> => {
  const mutationOptions =
    useApproveOrganizationApplicationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Reject the selected organization application
 * @summary Reject an organization application
 */
export const useRejectOrganizationApplicationHook = () => {
  const rejectOrganizationApplication =
    useAxiosAlltix<OrganizationApplicationDTO>();

  return useCallback(
    (
      organizationApplicationID: string,
      organizationApplicationRejectDTO: OrganizationApplicationRejectDTO,
      signal?: AbortSignal,
    ) => {
      return rejectOrganizationApplication({
        url: `/organization-applications/${organizationApplicationID}/reject`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: organizationApplicationRejectDTO,
        signal,
      });
    },
    [rejectOrganizationApplication],
  );
};

export const useRejectOrganizationApplicationMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useRejectOrganizationApplicationHook>>
    >,
    TError,
    {
      organizationApplicationID: string;
      data: OrganizationApplicationRejectDTO;
    },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useRejectOrganizationApplicationHook>>>,
  TError,
  { organizationApplicationID: string; data: OrganizationApplicationRejectDTO },
  TContext
> => {
  const mutationKey = ['rejectOrganizationApplication'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const rejectOrganizationApplication = useRejectOrganizationApplicationHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useRejectOrganizationApplicationHook>>
    >,
    {
      organizationApplicationID: string;
      data: OrganizationApplicationRejectDTO;
    }
  > = (props) => {
    const { organizationApplicationID, data } = props ?? {};

    return rejectOrganizationApplication(organizationApplicationID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type RejectOrganizationApplicationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useRejectOrganizationApplicationHook>>>
>;
export type RejectOrganizationApplicationMutationBody =
  OrganizationApplicationRejectDTO;
export type RejectOrganizationApplicationMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Reject an organization application
 */
export const useRejectOrganizationApplication = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useRejectOrganizationApplicationHook>>
      >,
      TError,
      {
        organizationApplicationID: string;
        data: OrganizationApplicationRejectDTO;
      },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useRejectOrganizationApplicationHook>>>,
  TError,
  { organizationApplicationID: string; data: OrganizationApplicationRejectDTO },
  TContext
> => {
  const mutationOptions =
    useRejectOrganizationApplicationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get the departments as list
 * @summary Get departments as list
 */
export const useGetDepartmentsListHook = () => {
  const getDepartmentsList = useAxiosAlltix<DepartmentDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getDepartmentsList({ url: `/departments`, method: 'GET', signal });
    },
    [getDepartmentsList],
  );
};

export const getGetDepartmentsListQueryKey = () => {
  return [`/departments`] as const;
};

export const useGetDepartmentsListQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentsListHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useGetDepartmentsListHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDepartmentsListQueryKey();

  const getDepartmentsList = useGetDepartmentsListHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetDepartmentsListHook>>>
  > = ({ signal }) => getDepartmentsList(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetDepartmentsListHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetDepartmentsListQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetDepartmentsListHook>>>
>;
export type GetDepartmentsListQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetDepartmentsList<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentsListHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDepartmentsListHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetDepartmentsListHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetDepartmentsListHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetDepartmentsList<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentsListHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDepartmentsListHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetDepartmentsListHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetDepartmentsListHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetDepartmentsList<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentsListHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDepartmentsListHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get departments as list
 */

export function useGetDepartmentsList<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentsListHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDepartmentsListHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetDepartmentsListQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Create a department
 */
export const useCreateDepartmentHook = () => {
  const createDepartment = useAxiosAlltix<DepartmentDTO>();

  return useCallback(
    (departmentCreateDTO: DepartmentCreateDTO, signal?: AbortSignal) => {
      return createDepartment({
        url: `/departments`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: departmentCreateDTO,
        signal,
      });
    },
    [createDepartment],
  );
};

export const useCreateDepartmentMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateDepartmentHook>>>,
    TError,
    { data: DepartmentCreateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateDepartmentHook>>>,
  TError,
  { data: DepartmentCreateDTO },
  TContext
> => {
  const mutationKey = ['createDepartment'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createDepartment = useCreateDepartmentHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateDepartmentHook>>>,
    { data: DepartmentCreateDTO }
  > = (props) => {
    const { data } = props ?? {};

    return createDepartment(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateDepartmentMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateDepartmentHook>>>
>;
export type CreateDepartmentMutationBody = DepartmentCreateDTO;
export type CreateDepartmentMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Create a department
 */
export const useCreateDepartment = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateDepartmentHook>>>,
      TError,
      { data: DepartmentCreateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateDepartmentHook>>>,
  TError,
  { data: DepartmentCreateDTO },
  TContext
> => {
  const mutationOptions = useCreateDepartmentMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Returns the user's access privileges for all accessible departments in their organization
 * @summary Get user's department access levels
 */
export const useGetDepartmentsAccessHook = () => {
  const getDepartmentsAccess = useAxiosAlltix<DepartmentAccessDTO>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getDepartmentsAccess({
        url: `/departments/access`,
        method: 'GET',
        signal,
      });
    },
    [getDepartmentsAccess],
  );
};

export const getGetDepartmentsAccessQueryKey = () => {
  return [`/departments/access`] as const;
};

export const useGetDepartmentsAccessQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentsAccessHook>>>,
  TError = ErrorType<ForbiddenResponse | InternalServerErrorResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useGetDepartmentsAccessHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDepartmentsAccessQueryKey();

  const getDepartmentsAccess = useGetDepartmentsAccessHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetDepartmentsAccessHook>>>
  > = ({ signal }) => getDepartmentsAccess(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetDepartmentsAccessHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetDepartmentsAccessQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetDepartmentsAccessHook>>>
>;
export type GetDepartmentsAccessQueryError = ErrorType<
  ForbiddenResponse | InternalServerErrorResponse
>;

export function useGetDepartmentsAccess<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentsAccessHook>>>,
  TError = ErrorType<ForbiddenResponse | InternalServerErrorResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDepartmentsAccessHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetDepartmentsAccessHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetDepartmentsAccessHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetDepartmentsAccess<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentsAccessHook>>>,
  TError = ErrorType<ForbiddenResponse | InternalServerErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDepartmentsAccessHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetDepartmentsAccessHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetDepartmentsAccessHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetDepartmentsAccess<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentsAccessHook>>>,
  TError = ErrorType<ForbiddenResponse | InternalServerErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDepartmentsAccessHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get user's department access levels
 */

export function useGetDepartmentsAccess<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentsAccessHook>>>,
  TError = ErrorType<ForbiddenResponse | InternalServerErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDepartmentsAccessHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetDepartmentsAccessQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get the department tree for the current organization
 * @summary Get department tree
 */
export const useGetCurrentDepartmentTreeHook = () => {
  const getCurrentDepartmentTree = useAxiosAlltix<DepartmentTreeNodeDTO>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getCurrentDepartmentTree({
        url: `/departments/department-tree`,
        method: 'GET',
        signal,
      });
    },
    [getCurrentDepartmentTree],
  );
};

export const getGetCurrentDepartmentTreeQueryKey = () => {
  return [`/departments/department-tree`] as const;
};

export const useGetCurrentDepartmentTreeQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetCurrentDepartmentTreeHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useGetCurrentDepartmentTreeHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetCurrentDepartmentTreeQueryKey();

  const getCurrentDepartmentTree = useGetCurrentDepartmentTreeHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetCurrentDepartmentTreeHook>>>
  > = ({ signal }) => getCurrentDepartmentTree(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetCurrentDepartmentTreeHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetCurrentDepartmentTreeQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetCurrentDepartmentTreeHook>>>
>;
export type GetCurrentDepartmentTreeQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetCurrentDepartmentTree<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetCurrentDepartmentTreeHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCurrentDepartmentTreeHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetCurrentDepartmentTreeHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetCurrentDepartmentTreeHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetCurrentDepartmentTree<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetCurrentDepartmentTreeHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCurrentDepartmentTreeHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetCurrentDepartmentTreeHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetCurrentDepartmentTreeHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetCurrentDepartmentTree<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetCurrentDepartmentTreeHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCurrentDepartmentTreeHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get department tree
 */

export function useGetCurrentDepartmentTree<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetCurrentDepartmentTreeHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCurrentDepartmentTreeHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetCurrentDepartmentTreeQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get the department by id
 * @summary Get department by id
 */
export const useGetDepartmentByIDHook = () => {
  const getDepartmentByID = useAxiosAlltix<DepartmentDTO>();

  return useCallback(
    (departmentID: string, signal?: AbortSignal) => {
      return getDepartmentByID({
        url: `/departments/${departmentID}`,
        method: 'GET',
        signal,
      });
    },
    [getDepartmentByID],
  );
};

export const getGetDepartmentByIDQueryKey = (departmentID?: string) => {
  return [`/departments/${departmentID}`] as const;
};

export const useGetDepartmentByIDQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDepartmentByIDHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetDepartmentByIDQueryKey(departmentID);

  const getDepartmentByID = useGetDepartmentByIDHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetDepartmentByIDHook>>>
  > = ({ signal }) => getDepartmentByID(departmentID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!departmentID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetDepartmentByIDHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetDepartmentByIDQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetDepartmentByIDHook>>>
>;
export type GetDepartmentByIDQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetDepartmentByID<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDepartmentByIDHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetDepartmentByIDHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetDepartmentByIDHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetDepartmentByID<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDepartmentByIDHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetDepartmentByIDHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetDepartmentByIDHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetDepartmentByID<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDepartmentByIDHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get department by id
 */

export function useGetDepartmentByID<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDepartmentByIDHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetDepartmentByIDQueryOptions(departmentID, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Update the selected department
 * @summary Update department by id
 */
export const useUpdateDepartmentByIDHook = () => {
  const updateDepartmentByID = useAxiosAlltix<DepartmentDTO>();

  return useCallback(
    (departmentID: string, departmentDTO: DepartmentDTO) => {
      return updateDepartmentByID({
        url: `/departments/${departmentID}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: departmentDTO,
      });
    },
    [updateDepartmentByID],
  );
};

export const useUpdateDepartmentByIDMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateDepartmentByIDHook>>>,
    TError,
    { departmentID: string; data: DepartmentDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateDepartmentByIDHook>>>,
  TError,
  { departmentID: string; data: DepartmentDTO },
  TContext
> => {
  const mutationKey = ['updateDepartmentByID'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateDepartmentByID = useUpdateDepartmentByIDHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateDepartmentByIDHook>>>,
    { departmentID: string; data: DepartmentDTO }
  > = (props) => {
    const { departmentID, data } = props ?? {};

    return updateDepartmentByID(departmentID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateDepartmentByIDMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateDepartmentByIDHook>>>
>;
export type UpdateDepartmentByIDMutationBody = DepartmentDTO;
export type UpdateDepartmentByIDMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Update department by id
 */
export const useUpdateDepartmentByID = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateDepartmentByIDHook>>>,
      TError,
      { departmentID: string; data: DepartmentDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateDepartmentByIDHook>>>,
  TError,
  { departmentID: string; data: DepartmentDTO },
  TContext
> => {
  const mutationOptions = useUpdateDepartmentByIDMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Delete the selected department
 * @summary Delete department by id
 */
export const useDeleteDepartmentByIDHook = () => {
  const deleteDepartmentByID = useAxiosAlltix<DepartmentDTO>();

  return useCallback(
    (departmentID: string) => {
      return deleteDepartmentByID({
        url: `/departments/${departmentID}`,
        method: 'DELETE',
      });
    },
    [deleteDepartmentByID],
  );
};

export const useDeleteDepartmentByIDMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteDepartmentByIDHook>>>,
    TError,
    { departmentID: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteDepartmentByIDHook>>>,
  TError,
  { departmentID: string },
  TContext
> => {
  const mutationKey = ['deleteDepartmentByID'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteDepartmentByID = useDeleteDepartmentByIDHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteDepartmentByIDHook>>>,
    { departmentID: string }
  > = (props) => {
    const { departmentID } = props ?? {};

    return deleteDepartmentByID(departmentID);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteDepartmentByIDMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteDepartmentByIDHook>>>
>;

export type DeleteDepartmentByIDMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Delete department by id
 */
export const useDeleteDepartmentByID = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteDepartmentByIDHook>>>,
      TError,
      { departmentID: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteDepartmentByIDHook>>>,
  TError,
  { departmentID: string },
  TContext
> => {
  const mutationOptions = useDeleteDepartmentByIDMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get all collections for the specified department
 * @summary Get collections for specific department
 */
export const useGetCollectionsForDepartmentHook = () => {
  const getCollectionsForDepartment =
    useAxiosAlltix<DepartmentCollectionDTO[]>();

  return useCallback(
    (departmentID: string, signal?: AbortSignal) => {
      return getCollectionsForDepartment({
        url: `/departments/${departmentID}/collections`,
        method: 'GET',
        signal,
      });
    },
    [getCollectionsForDepartment],
  );
};

export const getGetCollectionsForDepartmentQueryKey = (
  departmentID?: string,
) => {
  return [`/departments/${departmentID}/collections`] as const;
};

export const useGetCollectionsForDepartmentQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetCollectionsForDepartmentHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetCollectionsForDepartmentHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetCollectionsForDepartmentQueryKey(departmentID);

  const getCollectionsForDepartment = useGetCollectionsForDepartmentHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetCollectionsForDepartmentHook>>>
  > = ({ signal }) => getCollectionsForDepartment(departmentID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!departmentID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetCollectionsForDepartmentHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetCollectionsForDepartmentQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetCollectionsForDepartmentHook>>>
>;
export type GetCollectionsForDepartmentQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetCollectionsForDepartment<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetCollectionsForDepartmentHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetCollectionsForDepartmentHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetCollectionsForDepartmentHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetCollectionsForDepartmentHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetCollectionsForDepartment<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetCollectionsForDepartmentHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetCollectionsForDepartmentHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetCollectionsForDepartmentHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetCollectionsForDepartmentHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetCollectionsForDepartment<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetCollectionsForDepartmentHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetCollectionsForDepartmentHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get collections for specific department
 */

export function useGetCollectionsForDepartment<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetCollectionsForDepartmentHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetCollectionsForDepartmentHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetCollectionsForDepartmentQueryOptions(
    departmentID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns all travellers for the specified department
 * @summary Get travellers per department
 */
export const useGetTravellersForDepartmentHook = () => {
  const getTravellersForDepartment =
    useAxiosAlltix<TravellerWithCollectionMembershipsDTO[]>();

  return useCallback(
    (departmentID: string, signal?: AbortSignal) => {
      return getTravellersForDepartment({
        url: `/departments/${departmentID}/travellers`,
        method: 'GET',
        signal,
      });
    },
    [getTravellersForDepartment],
  );
};

export const getGetTravellersForDepartmentQueryKey = (
  departmentID?: string,
) => {
  return [`/departments/${departmentID}/travellers`] as const;
};

export const useGetTravellersForDepartmentQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTravellersForDepartmentHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTravellersForDepartmentHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetTravellersForDepartmentQueryKey(departmentID);

  const getTravellersForDepartment = useGetTravellersForDepartmentHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetTravellersForDepartmentHook>>>
  > = ({ signal }) => getTravellersForDepartment(departmentID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!departmentID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTravellersForDepartmentHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetTravellersForDepartmentQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTravellersForDepartmentHook>>>
>;
export type GetTravellersForDepartmentQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetTravellersForDepartment<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTravellersForDepartmentHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTravellersForDepartmentHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetTravellersForDepartmentHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetTravellersForDepartmentHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetTravellersForDepartment<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTravellersForDepartmentHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTravellersForDepartmentHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetTravellersForDepartmentHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetTravellersForDepartmentHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetTravellersForDepartment<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTravellersForDepartmentHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTravellersForDepartmentHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get travellers per department
 */

export function useGetTravellersForDepartment<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTravellersForDepartmentHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTravellersForDepartmentHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetTravellersForDepartmentQueryOptions(
    departmentID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns payer for the specified department
 * @summary Get payer per department
 */
export const useGetPayerForDepartmentHook = () => {
  const getPayerForDepartment = useAxiosAlltix<DepartmentPayerDTO>();

  return useCallback(
    (departmentID: string, signal?: AbortSignal) => {
      return getPayerForDepartment({
        url: `/departments/${departmentID}/payer`,
        method: 'GET',
        signal,
      });
    },
    [getPayerForDepartment],
  );
};

export const getGetPayerForDepartmentQueryKey = (departmentID?: string) => {
  return [`/departments/${departmentID}/payer`] as const;
};

export const useGetPayerForDepartmentQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetPayerForDepartmentHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPayerForDepartmentHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetPayerForDepartmentQueryKey(departmentID);

  const getPayerForDepartment = useGetPayerForDepartmentHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetPayerForDepartmentHook>>>
  > = ({ signal }) => getPayerForDepartment(departmentID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!departmentID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetPayerForDepartmentHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetPayerForDepartmentQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetPayerForDepartmentHook>>>
>;
export type GetPayerForDepartmentQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetPayerForDepartment<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPayerForDepartmentHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPayerForDepartmentHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetPayerForDepartmentHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetPayerForDepartmentHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetPayerForDepartment<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPayerForDepartmentHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPayerForDepartmentHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetPayerForDepartmentHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetPayerForDepartmentHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetPayerForDepartment<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPayerForDepartmentHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPayerForDepartmentHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get payer per department
 */

export function useGetPayerForDepartment<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPayerForDepartmentHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPayerForDepartmentHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetPayerForDepartmentQueryOptions(
    departmentID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Updates information for the payer linked to a department
 * @summary Update payer for department
 */
export const useUpdatePayerForDepartmentHook = () => {
  const updatePayerForDepartment = useAxiosAlltix<DepartmentPayerDTO>();

  return useCallback(
    (departmentID: string, payerUpdateDTO: PayerUpdateDTO) => {
      return updatePayerForDepartment({
        url: `/departments/${departmentID}/payer`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: payerUpdateDTO,
      });
    },
    [updatePayerForDepartment],
  );
};

export const useUpdatePayerForDepartmentMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdatePayerForDepartmentHook>>>,
    TError,
    { departmentID: string; data: PayerUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdatePayerForDepartmentHook>>>,
  TError,
  { departmentID: string; data: PayerUpdateDTO },
  TContext
> => {
  const mutationKey = ['updatePayerForDepartment'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updatePayerForDepartment = useUpdatePayerForDepartmentHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdatePayerForDepartmentHook>>>,
    { departmentID: string; data: PayerUpdateDTO }
  > = (props) => {
    const { departmentID, data } = props ?? {};

    return updatePayerForDepartment(departmentID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdatePayerForDepartmentMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdatePayerForDepartmentHook>>>
>;
export type UpdatePayerForDepartmentMutationBody = PayerUpdateDTO;
export type UpdatePayerForDepartmentMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Update payer for department
 */
export const useUpdatePayerForDepartment = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdatePayerForDepartmentHook>>>,
      TError,
      { departmentID: string; data: PayerUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdatePayerForDepartmentHook>>>,
  TError,
  { departmentID: string; data: PayerUpdateDTO },
  TContext
> => {
  const mutationOptions = useUpdatePayerForDepartmentMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Returns users for a specific department
 * @summary Get users for department
 */
export const useGetUsersForDepartmentHook = () => {
  const getUsersForDepartment = useAxiosAlltix<DepartmentUserDTO[]>();

  return useCallback(
    (departmentID: string, signal?: AbortSignal) => {
      return getUsersForDepartment({
        url: `/departments/${departmentID}/users`,
        method: 'GET',
        signal,
      });
    },
    [getUsersForDepartment],
  );
};

export const getGetUsersForDepartmentQueryKey = (departmentID?: string) => {
  return [`/departments/${departmentID}/users`] as const;
};

export const useGetUsersForDepartmentQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetUsersForDepartmentHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUsersForDepartmentHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetUsersForDepartmentQueryKey(departmentID);

  const getUsersForDepartment = useGetUsersForDepartmentHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetUsersForDepartmentHook>>>
  > = ({ signal }) => getUsersForDepartment(departmentID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!departmentID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetUsersForDepartmentHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetUsersForDepartmentQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetUsersForDepartmentHook>>>
>;
export type GetUsersForDepartmentQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetUsersForDepartment<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUsersForDepartmentHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUsersForDepartmentHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetUsersForDepartmentHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetUsersForDepartmentHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetUsersForDepartment<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUsersForDepartmentHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUsersForDepartmentHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetUsersForDepartmentHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetUsersForDepartmentHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetUsersForDepartment<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUsersForDepartmentHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUsersForDepartmentHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get users for department
 */

export function useGetUsersForDepartment<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUsersForDepartmentHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  departmentID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUsersForDepartmentHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetUsersForDepartmentQueryOptions(
    departmentID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Removes a user from a specific department.
 * @summary Delete User from Department
 */
export const useDeleteDepartmentUserHook = () => {
  const deleteDepartmentUser = useAxiosAlltix<void>();

  return useCallback(
    (departmentID: string, userID: string) => {
      return deleteDepartmentUser({
        url: `/departments/${departmentID}/users/${userID}`,
        method: 'DELETE',
      });
    },
    [deleteDepartmentUser],
  );
};

export const useDeleteDepartmentUserMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteDepartmentUserHook>>>,
    TError,
    { departmentID: string; userID: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteDepartmentUserHook>>>,
  TError,
  { departmentID: string; userID: string },
  TContext
> => {
  const mutationKey = ['deleteDepartmentUser'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteDepartmentUser = useDeleteDepartmentUserHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteDepartmentUserHook>>>,
    { departmentID: string; userID: string }
  > = (props) => {
    const { departmentID, userID } = props ?? {};

    return deleteDepartmentUser(departmentID, userID);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteDepartmentUserMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteDepartmentUserHook>>>
>;

export type DeleteDepartmentUserMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Delete User from Department
 */
export const useDeleteDepartmentUser = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteDepartmentUserHook>>>,
      TError,
      { departmentID: string; userID: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteDepartmentUserHook>>>,
  TError,
  { departmentID: string; userID: string },
  TContext
> => {
  const mutationOptions = useDeleteDepartmentUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get a department user and their departments
 * @summary Get department user with departments
 */
export const useGetDepartmentUserWithDepartmentsHook = () => {
  const getDepartmentUserWithDepartments =
    useAxiosAlltix<UserWithDepartmentsDTO>();

  return useCallback(
    (id: string, signal?: AbortSignal) => {
      return getDepartmentUserWithDepartments({
        url: `/department-users/${id}/departments`,
        method: 'GET',
        signal,
      });
    },
    [getDepartmentUserWithDepartments],
  );
};

export const getGetDepartmentUserWithDepartmentsQueryKey = (id?: string) => {
  return [`/department-users/${id}/departments`] as const;
};

export const useGetDepartmentUserWithDepartmentsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetDepartmentUserWithDepartmentsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetDepartmentUserWithDepartmentsHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetDepartmentUserWithDepartmentsQueryKey(id);

  const getDepartmentUserWithDepartments =
    useGetDepartmentUserWithDepartmentsHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<ReturnType<typeof useGetDepartmentUserWithDepartmentsHook>>
    >
  > = ({ signal }) => getDepartmentUserWithDepartments(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<
      ReturnType<ReturnType<typeof useGetDepartmentUserWithDepartmentsHook>>
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetDepartmentUserWithDepartmentsQueryResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useGetDepartmentUserWithDepartmentsHook>>
  >
>;
export type GetDepartmentUserWithDepartmentsQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetDepartmentUserWithDepartments<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetDepartmentUserWithDepartmentsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetDepartmentUserWithDepartmentsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useGetDepartmentUserWithDepartmentsHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useGetDepartmentUserWithDepartmentsHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetDepartmentUserWithDepartments<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetDepartmentUserWithDepartmentsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetDepartmentUserWithDepartmentsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useGetDepartmentUserWithDepartmentsHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useGetDepartmentUserWithDepartmentsHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetDepartmentUserWithDepartments<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetDepartmentUserWithDepartmentsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetDepartmentUserWithDepartmentsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get department user with departments
 */

export function useGetDepartmentUserWithDepartments<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetDepartmentUserWithDepartmentsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetDepartmentUserWithDepartmentsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetDepartmentUserWithDepartmentsQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns users in departments the current user has access to
 * @summary List department users
 */
export const useGetDepartmentUsersHook = () => {
  const getDepartmentUsers = useAxiosAlltix<DepartmentUserDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getDepartmentUsers({
        url: `/department-users`,
        method: 'GET',
        signal,
      });
    },
    [getDepartmentUsers],
  );
};

export const getGetDepartmentUsersQueryKey = () => {
  return [`/department-users`] as const;
};

export const useGetDepartmentUsersQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentUsersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useGetDepartmentUsersHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDepartmentUsersQueryKey();

  const getDepartmentUsers = useGetDepartmentUsersHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetDepartmentUsersHook>>>
  > = ({ signal }) => getDepartmentUsers(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetDepartmentUsersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetDepartmentUsersQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetDepartmentUsersHook>>>
>;
export type GetDepartmentUsersQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetDepartmentUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentUsersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDepartmentUsersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetDepartmentUsersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetDepartmentUsersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetDepartmentUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentUsersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDepartmentUsersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetDepartmentUsersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetDepartmentUsersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetDepartmentUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentUsersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDepartmentUsersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List department users
 */

export function useGetDepartmentUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDepartmentUsersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDepartmentUsersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetDepartmentUsersQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Creates a new invitation to a user to a department
 * @summary Invite a user to a department
 */
export const useInviteUserToDepartmentHook = () => {
  const inviteUserToDepartment = useAxiosAlltix<void>();

  return useCallback(
    (
      departmentUserInviteDTO: DepartmentUserInviteDTO,
      signal?: AbortSignal,
    ) => {
      return inviteUserToDepartment({
        url: `/department-users/invite`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: departmentUserInviteDTO,
        signal,
      });
    },
    [inviteUserToDepartment],
  );
};

export const useInviteUserToDepartmentMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useInviteUserToDepartmentHook>>>,
    TError,
    { data: DepartmentUserInviteDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useInviteUserToDepartmentHook>>>,
  TError,
  { data: DepartmentUserInviteDTO },
  TContext
> => {
  const mutationKey = ['inviteUserToDepartment'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const inviteUserToDepartment = useInviteUserToDepartmentHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useInviteUserToDepartmentHook>>>,
    { data: DepartmentUserInviteDTO }
  > = (props) => {
    const { data } = props ?? {};

    return inviteUserToDepartment(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type InviteUserToDepartmentMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useInviteUserToDepartmentHook>>>
>;
export type InviteUserToDepartmentMutationBody = DepartmentUserInviteDTO;
export type InviteUserToDepartmentMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Invite a user to a department
 */
export const useInviteUserToDepartment = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useInviteUserToDepartmentHook>>>,
      TError,
      { data: DepartmentUserInviteDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useInviteUserToDepartmentHook>>>,
  TError,
  { data: DepartmentUserInviteDTO },
  TContext
> => {
  const mutationOptions = useInviteUserToDepartmentMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Update department user
 */
export const useUpdateDepartmentUserHook = () => {
  const updateDepartmentUser = useAxiosAlltix<DepartmentUserUpdateDTO>();

  return useCallback(
    (id: string, departmentUserUpdateDTO: DepartmentUserUpdateDTO) => {
      return updateDepartmentUser({
        url: `/department-users/${id}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: departmentUserUpdateDTO,
      });
    },
    [updateDepartmentUser],
  );
};

export const useUpdateDepartmentUserMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateDepartmentUserHook>>>,
    TError,
    { id: string; data: DepartmentUserUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateDepartmentUserHook>>>,
  TError,
  { id: string; data: DepartmentUserUpdateDTO },
  TContext
> => {
  const mutationKey = ['updateDepartmentUser'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateDepartmentUser = useUpdateDepartmentUserHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateDepartmentUserHook>>>,
    { id: string; data: DepartmentUserUpdateDTO }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateDepartmentUser(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateDepartmentUserMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateDepartmentUserHook>>>
>;
export type UpdateDepartmentUserMutationBody = DepartmentUserUpdateDTO;
export type UpdateDepartmentUserMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Update department user
 */
export const useUpdateDepartmentUser = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateDepartmentUserHook>>>,
      TError,
      { id: string; data: DepartmentUserUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateDepartmentUserHook>>>,
  TError,
  { id: string; data: DepartmentUserUpdateDTO },
  TContext
> => {
  const mutationOptions = useUpdateDepartmentUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get traveller by id
 * @summary Get traveller by id
 */
export const useGetTravellerByIDHook = () => {
  const getTravellerByID =
    useAxiosAlltix<TravellerWithCollectionMembershipsDTO>();

  return useCallback(
    (travellerID: string, signal?: AbortSignal) => {
      return getTravellerByID({
        url: `/travellers/${travellerID}`,
        method: 'GET',
        signal,
      });
    },
    [getTravellerByID],
  );
};

export const getGetTravellerByIDQueryKey = (travellerID?: string) => {
  return [`/travellers/${travellerID}`] as const;
};

export const useGetTravellerByIDQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetTravellerByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTravellerByIDHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTravellerByIDQueryKey(travellerID);

  const getTravellerByID = useGetTravellerByIDHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetTravellerByIDHook>>>
  > = ({ signal }) => getTravellerByID(travellerID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!travellerID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTravellerByIDHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetTravellerByIDQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTravellerByIDHook>>>
>;
export type GetTravellerByIDQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetTravellerByID<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTravellerByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  travellerID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTravellerByIDHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTravellerByIDHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTravellerByIDHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetTravellerByID<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTravellerByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTravellerByIDHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTravellerByIDHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTravellerByIDHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetTravellerByID<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTravellerByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTravellerByIDHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get traveller by id
 */

export function useGetTravellerByID<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTravellerByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTravellerByIDHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetTravellerByIDQueryOptions(travellerID, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Update a traveller by ID. Depending on the traveller's memberships, some fields are optionally required:
- `email` is required for members of collection types:
    - `master`
    - `default`
- `national_identity_number` is required for members of collection types
  - `school-transportation`
  - `school-student-travel`
  - `school-child-and-youth`

 * @summary Update traveller
 */
export const useUpdateTravellerByIDHook = () => {
  const updateTravellerByID = useAxiosAlltix<TravellerDTO>();

  return useCallback(
    (travellerID: string, travellerUpdateDTO: TravellerUpdateDTO) => {
      return updateTravellerByID({
        url: `/travellers/${travellerID}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: travellerUpdateDTO,
      });
    },
    [updateTravellerByID],
  );
};

export const useUpdateTravellerByIDMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateTravellerByIDHook>>>,
    TError,
    { travellerID: string; data: TravellerUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateTravellerByIDHook>>>,
  TError,
  { travellerID: string; data: TravellerUpdateDTO },
  TContext
> => {
  const mutationKey = ['updateTravellerByID'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateTravellerByID = useUpdateTravellerByIDHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateTravellerByIDHook>>>,
    { travellerID: string; data: TravellerUpdateDTO }
  > = (props) => {
    const { travellerID, data } = props ?? {};

    return updateTravellerByID(travellerID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateTravellerByIDMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateTravellerByIDHook>>>
>;
export type UpdateTravellerByIDMutationBody = TravellerUpdateDTO;
export type UpdateTravellerByIDMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Update traveller
 */
export const useUpdateTravellerByID = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateTravellerByIDHook>>>,
      TError,
      { travellerID: string; data: TravellerUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateTravellerByIDHook>>>,
  TError,
  { travellerID: string; data: TravellerUpdateDTO },
  TContext
> => {
  const mutationOptions = useUpdateTravellerByIDMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Every card assigned to the traveller in the user's organization, newest first, including cards since released. Requires view access to the traveller. Returns 404 if the traveller is not in the organization.
 * @summary Get a traveller's card history
 */
export const useGetTravellerCardHistoryHook = () => {
  const getTravellerCardHistory = useAxiosAlltix<CardAssignmentDTO[]>();

  return useCallback(
    (travellerID: string, signal?: AbortSignal) => {
      return getTravellerCardHistory({
        url: `/travellers/${travellerID}/card-history`,
        method: 'GET',
        signal,
      });
    },
    [getTravellerCardHistory],
  );
};

export const getGetTravellerCardHistoryQueryKey = (travellerID?: string) => {
  return [`/travellers/${travellerID}/card-history`] as const;
};

export const useGetTravellerCardHistoryQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTravellerCardHistoryHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTravellerCardHistoryHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTravellerCardHistoryQueryKey(travellerID);

  const getTravellerCardHistory = useGetTravellerCardHistoryHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetTravellerCardHistoryHook>>>
  > = ({ signal }) => getTravellerCardHistory(travellerID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!travellerID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTravellerCardHistoryHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetTravellerCardHistoryQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTravellerCardHistoryHook>>>
>;
export type GetTravellerCardHistoryQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
>;

export function useGetTravellerCardHistory<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTravellerCardHistoryHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
>(
  travellerID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTravellerCardHistoryHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetTravellerCardHistoryHook>>
          >,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTravellerCardHistoryHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetTravellerCardHistory<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTravellerCardHistoryHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTravellerCardHistoryHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetTravellerCardHistoryHook>>
          >,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTravellerCardHistoryHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetTravellerCardHistory<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTravellerCardHistoryHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTravellerCardHistoryHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get a traveller's card history
 */

export function useGetTravellerCardHistory<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTravellerCardHistoryHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTravellerCardHistoryHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetTravellerCardHistoryQueryOptions(
    travellerID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Get traveller bearers
 */
export const useGetTravellerBearersHook = () => {
  const getTravellerBearers = useAxiosAlltix<BobcatTravellerWithBearersDTO>();

  return useCallback(
    (travellerID: string, signal?: AbortSignal) => {
      return getTravellerBearers({
        url: `/travellers/${travellerID}/bearers`,
        method: 'GET',
        signal,
      });
    },
    [getTravellerBearers],
  );
};

export const getGetTravellerBearersQueryKey = (travellerID?: string) => {
  return [`/travellers/${travellerID}/bearers`] as const;
};

export const useGetTravellerBearersQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetTravellerBearersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTravellerBearersHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTravellerBearersQueryKey(travellerID);

  const getTravellerBearers = useGetTravellerBearersHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetTravellerBearersHook>>>
  > = ({ signal }) => getTravellerBearers(travellerID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!travellerID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTravellerBearersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetTravellerBearersQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTravellerBearersHook>>>
>;
export type GetTravellerBearersQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetTravellerBearers<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTravellerBearersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  travellerID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTravellerBearersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTravellerBearersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTravellerBearersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetTravellerBearers<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTravellerBearersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTravellerBearersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTravellerBearersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTravellerBearersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetTravellerBearers<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTravellerBearersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTravellerBearersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get traveller bearers
 */

export function useGetTravellerBearers<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTravellerBearersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTravellerBearersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetTravellerBearersQueryOptions(travellerID, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Set the bearer that new tickets are issued against for this traveller. The bearer must belong to the traveller in bobcat and must not be blocked.
 * @summary Set the traveller favored bearer
 */
export const useSetTravellerFavoredBearerHook = () => {
  const setTravellerFavoredBearer = useAxiosAlltix<void>();

  return useCallback(
    (
      travellerID: string,
      travellerFavoredBearerUpdateDTO: TravellerFavoredBearerUpdateDTO,
    ) => {
      return setTravellerFavoredBearer({
        url: `/travellers/${travellerID}/favored-bearer`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: travellerFavoredBearerUpdateDTO,
      });
    },
    [setTravellerFavoredBearer],
  );
};

export const useSetTravellerFavoredBearerMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSetTravellerFavoredBearerHook>>>,
    TError,
    { travellerID: string; data: TravellerFavoredBearerUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useSetTravellerFavoredBearerHook>>>,
  TError,
  { travellerID: string; data: TravellerFavoredBearerUpdateDTO },
  TContext
> => {
  const mutationKey = ['setTravellerFavoredBearer'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const setTravellerFavoredBearer = useSetTravellerFavoredBearerHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useSetTravellerFavoredBearerHook>>>,
    { travellerID: string; data: TravellerFavoredBearerUpdateDTO }
  > = (props) => {
    const { travellerID, data } = props ?? {};

    return setTravellerFavoredBearer(travellerID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SetTravellerFavoredBearerMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSetTravellerFavoredBearerHook>>>
>;
export type SetTravellerFavoredBearerMutationBody =
  TravellerFavoredBearerUpdateDTO;
export type SetTravellerFavoredBearerMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Set the traveller favored bearer
 */
export const useSetTravellerFavoredBearer = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useSetTravellerFavoredBearerHook>>>,
      TError,
      { travellerID: string; data: TravellerFavoredBearerUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useSetTravellerFavoredBearerHook>>>,
  TError,
  { travellerID: string; data: TravellerFavoredBearerUpdateDTO },
  TContext
> => {
  const mutationOptions = useSetTravellerFavoredBearerMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Clear the favored bearer for this traveller
 * @summary Clear the traveller favored bearer
 */
export const useClearTravellerFavoredBearerHook = () => {
  const clearTravellerFavoredBearer = useAxiosAlltix<void>();

  return useCallback(
    (travellerID: string) => {
      return clearTravellerFavoredBearer({
        url: `/travellers/${travellerID}/favored-bearer`,
        method: 'DELETE',
      });
    },
    [clearTravellerFavoredBearer],
  );
};

export const useClearTravellerFavoredBearerMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useClearTravellerFavoredBearerHook>>>,
    TError,
    { travellerID: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useClearTravellerFavoredBearerHook>>>,
  TError,
  { travellerID: string },
  TContext
> => {
  const mutationKey = ['clearTravellerFavoredBearer'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const clearTravellerFavoredBearer = useClearTravellerFavoredBearerHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useClearTravellerFavoredBearerHook>>>,
    { travellerID: string }
  > = (props) => {
    const { travellerID } = props ?? {};

    return clearTravellerFavoredBearer(travellerID);
  };

  return { mutationFn, ...mutationOptions };
};

export type ClearTravellerFavoredBearerMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useClearTravellerFavoredBearerHook>>>
>;

export type ClearTravellerFavoredBearerMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Clear the traveller favored bearer
 */
export const useClearTravellerFavoredBearer = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useClearTravellerFavoredBearerHook>>
      >,
      TError,
      { travellerID: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useClearTravellerFavoredBearerHook>>>,
  TError,
  { travellerID: string },
  TContext
> => {
  const mutationOptions =
    useClearTravellerFavoredBearerMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Link a private bobcat account to the Traveller
 * @summary Link a private bobcat account to the Traveller
 */
export const useLinkTravellerBobcatAccountHook = () => {
  const linkTravellerBobcatAccount = useAxiosAlltix<TravellerDTO>();

  return useCallback(
    (
      travellerID: string,
      travellerLinkPrivateBobcatAccountDTO: TravellerLinkPrivateBobcatAccountDTO,
    ) => {
      return linkTravellerBobcatAccount({
        url: `/travellers/${travellerID}/link-private-bobcat-account`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: travellerLinkPrivateBobcatAccountDTO,
      });
    },
    [linkTravellerBobcatAccount],
  );
};

export const useLinkTravellerBobcatAccountMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useLinkTravellerBobcatAccountHook>>>,
    TError,
    { travellerID: string; data: TravellerLinkPrivateBobcatAccountDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useLinkTravellerBobcatAccountHook>>>,
  TError,
  { travellerID: string; data: TravellerLinkPrivateBobcatAccountDTO },
  TContext
> => {
  const mutationKey = ['linkTravellerBobcatAccount'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const linkTravellerBobcatAccount = useLinkTravellerBobcatAccountHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useLinkTravellerBobcatAccountHook>>>,
    { travellerID: string; data: TravellerLinkPrivateBobcatAccountDTO }
  > = (props) => {
    const { travellerID, data } = props ?? {};

    return linkTravellerBobcatAccount(travellerID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type LinkTravellerBobcatAccountMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useLinkTravellerBobcatAccountHook>>>
>;
export type LinkTravellerBobcatAccountMutationBody =
  TravellerLinkPrivateBobcatAccountDTO;
export type LinkTravellerBobcatAccountMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Link a private bobcat account to the Traveller
 */
export const useLinkTravellerBobcatAccount = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useLinkTravellerBobcatAccountHook>>>,
      TError,
      { travellerID: string; data: TravellerLinkPrivateBobcatAccountDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useLinkTravellerBobcatAccountHook>>>,
  TError,
  { travellerID: string; data: TravellerLinkPrivateBobcatAccountDTO },
  TContext
> => {
  const mutationOptions = useLinkTravellerBobcatAccountMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Replaces the traveller's school card with a blank card, for the lost-card case handled by customer service. The new card must be unclaimed here and carry no bearer in bobcat. Any active products move from the old card to the new one, the traveller's issued card is swapped in place, and the old bearer is destroyed in bobcat. A blocked (blacklisted) old bearer is left in place — bobcat refuses to delete it — and the swap proceeds without moving products. The operation is idempotent: replaying it after a partial failure resumes from wherever the previous attempt stopped. Cards of other types the traveller holds are left alone. Returns 404 if the traveller holds no cards at all, and 409 if they hold cards but no single replaceable school card, if a card transfer is already in flight, or if the replacement card is blocked (blacklisted).
 * @summary Replace a Traveller's school card
 */
export const useTravellerReplaceSchoolCardHook = () => {
  const travellerReplaceSchoolCard = useAxiosAlltix<TravelCardResponseDTO>();

  return useCallback(
    (
      travellerID: string,
      travellerReplaceSchoolCardRequestDTO: TravellerReplaceSchoolCardRequestDTO,
      signal?: AbortSignal,
    ) => {
      return travellerReplaceSchoolCard({
        url: `/travellers/${travellerID}/replace-school-card`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: travellerReplaceSchoolCardRequestDTO,
        signal,
      });
    },
    [travellerReplaceSchoolCard],
  );
};

export const useTravellerReplaceSchoolCardMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useTravellerReplaceSchoolCardHook>>>,
    TError,
    { travellerID: string; data: TravellerReplaceSchoolCardRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useTravellerReplaceSchoolCardHook>>>,
  TError,
  { travellerID: string; data: TravellerReplaceSchoolCardRequestDTO },
  TContext
> => {
  const mutationKey = ['travellerReplaceSchoolCard'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const travellerReplaceSchoolCard = useTravellerReplaceSchoolCardHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useTravellerReplaceSchoolCardHook>>>,
    { travellerID: string; data: TravellerReplaceSchoolCardRequestDTO }
  > = (props) => {
    const { travellerID, data } = props ?? {};

    return travellerReplaceSchoolCard(travellerID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type TravellerReplaceSchoolCardMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useTravellerReplaceSchoolCardHook>>>
>;
export type TravellerReplaceSchoolCardMutationBody =
  TravellerReplaceSchoolCardRequestDTO;
export type TravellerReplaceSchoolCardMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | ConflictResponse
>;

/**
 * @summary Replace a Traveller's school card
 */
export const useTravellerReplaceSchoolCard = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useTravellerReplaceSchoolCardHook>>>,
      TError,
      { travellerID: string; data: TravellerReplaceSchoolCardRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useTravellerReplaceSchoolCardHook>>>,
  TError,
  { travellerID: string; data: TravellerReplaceSchoolCardRequestDTO },
  TContext
> => {
  const mutationOptions = useTravellerReplaceSchoolCardMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Import travellers from JSOn data
 * @summary Import travellers
 */
export const useImportTravellersHook = () => {
  const importTravellers = useAxiosAlltix<TravellerImportResultDTO>();

  return useCallback(
    (travellerImportDTO: TravellerImportDTO, signal?: AbortSignal) => {
      return importTravellers({
        url: `/travellers/import`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: travellerImportDTO,
        signal,
      });
    },
    [importTravellers],
  );
};

export const useImportTravellersMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useImportTravellersHook>>>,
    TError,
    { data: TravellerImportDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useImportTravellersHook>>>,
  TError,
  { data: TravellerImportDTO },
  TContext
> => {
  const mutationKey = ['importTravellers'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const importTravellers = useImportTravellersHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useImportTravellersHook>>>,
    { data: TravellerImportDTO }
  > = (props) => {
    const { data } = props ?? {};

    return importTravellers(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type ImportTravellersMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useImportTravellersHook>>>
>;
export type ImportTravellersMutationBody = TravellerImportDTO;
export type ImportTravellersMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | InternalServerErrorResponse
>;

/**
 * @summary Import travellers
 */
export const useImportTravellers = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useImportTravellersHook>>>,
      TError,
      { data: TravellerImportDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useImportTravellersHook>>>,
  TError,
  { data: TravellerImportDTO },
  TContext
> => {
  const mutationOptions = useImportTravellersMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Check email availability
 */
export const useIsTravellerEmailAvailableHook = () => {
  const isTravellerEmailAvailable =
    useAxiosAlltix<TravellerEmailAvailabilityDTO>();

  return useCallback(
    (params: IsTravellerEmailAvailableParams, signal?: AbortSignal) => {
      return isTravellerEmailAvailable({
        url: `/travellers/email-available`,
        method: 'GET',
        params,
        signal,
      });
    },
    [isTravellerEmailAvailable],
  );
};

export const getIsTravellerEmailAvailableQueryKey = (
  params?: IsTravellerEmailAvailableParams,
) => {
  return [`/travellers/email-available`, ...(params ? [params] : [])] as const;
};

export const useIsTravellerEmailAvailableQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useIsTravellerEmailAvailableHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: IsTravellerEmailAvailableParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useIsTravellerEmailAvailableHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getIsTravellerEmailAvailableQueryKey(params);

  const isTravellerEmailAvailable = useIsTravellerEmailAvailableHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useIsTravellerEmailAvailableHook>>>
  > = ({ signal }) => isTravellerEmailAvailable(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useIsTravellerEmailAvailableHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type IsTravellerEmailAvailableQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useIsTravellerEmailAvailableHook>>>
>;
export type IsTravellerEmailAvailableQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useIsTravellerEmailAvailable<
  TData = Awaited<
    ReturnType<ReturnType<typeof useIsTravellerEmailAvailableHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: IsTravellerEmailAvailableParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useIsTravellerEmailAvailableHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useIsTravellerEmailAvailableHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useIsTravellerEmailAvailableHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useIsTravellerEmailAvailable<
  TData = Awaited<
    ReturnType<ReturnType<typeof useIsTravellerEmailAvailableHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: IsTravellerEmailAvailableParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useIsTravellerEmailAvailableHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useIsTravellerEmailAvailableHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useIsTravellerEmailAvailableHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useIsTravellerEmailAvailable<
  TData = Awaited<
    ReturnType<ReturnType<typeof useIsTravellerEmailAvailableHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: IsTravellerEmailAvailableParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useIsTravellerEmailAvailableHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Check email availability
 */

export function useIsTravellerEmailAvailable<
  TData = Awaited<
    ReturnType<ReturnType<typeof useIsTravellerEmailAvailableHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: IsTravellerEmailAvailableParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useIsTravellerEmailAvailableHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useIsTravellerEmailAvailableQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Autocomplete search for travellers
 * @summary Autocomplete search for travellers
 */
export const useTravellerGlobalSearchHook = () => {
  const travellerGlobalSearch = useAxiosAlltix<TravellerWithCardsDTO[]>();

  return useCallback(
    (params: TravellerGlobalSearchParams, signal?: AbortSignal) => {
      return travellerGlobalSearch({
        url: `/travellers`,
        method: 'GET',
        params,
        signal,
      });
    },
    [travellerGlobalSearch],
  );
};

export const getTravellerGlobalSearchQueryKey = (
  params?: TravellerGlobalSearchParams,
) => {
  return [`/travellers`, ...(params ? [params] : [])] as const;
};

export const useTravellerGlobalSearchQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useTravellerGlobalSearchHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: TravellerGlobalSearchParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useTravellerGlobalSearchHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getTravellerGlobalSearchQueryKey(params);

  const travellerGlobalSearch = useTravellerGlobalSearchHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useTravellerGlobalSearchHook>>>
  > = ({ signal }) => travellerGlobalSearch(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useTravellerGlobalSearchHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type TravellerGlobalSearchQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useTravellerGlobalSearchHook>>>
>;
export type TravellerGlobalSearchQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useTravellerGlobalSearch<
  TData = Awaited<ReturnType<ReturnType<typeof useTravellerGlobalSearchHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: TravellerGlobalSearchParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useTravellerGlobalSearchHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useTravellerGlobalSearchHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useTravellerGlobalSearchHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useTravellerGlobalSearch<
  TData = Awaited<ReturnType<ReturnType<typeof useTravellerGlobalSearchHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: TravellerGlobalSearchParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useTravellerGlobalSearchHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useTravellerGlobalSearchHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useTravellerGlobalSearchHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useTravellerGlobalSearch<
  TData = Awaited<ReturnType<ReturnType<typeof useTravellerGlobalSearchHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: TravellerGlobalSearchParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useTravellerGlobalSearchHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Autocomplete search for travellers
 */

export function useTravellerGlobalSearch<
  TData = Awaited<ReturnType<ReturnType<typeof useTravellerGlobalSearchHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: TravellerGlobalSearchParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useTravellerGlobalSearchHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useTravellerGlobalSearchQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get a list of all payers for the current organizations
 * @summary List payers
 */
export const useListPayersHook = () => {
  const listPayers = useAxiosAlltix<PayerDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listPayers({ url: `/payers`, method: 'GET', signal });
    },
    [listPayers],
  );
};

export const getListPayersQueryKey = () => {
  return [`/payers`] as const;
};

export const useListPayersQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListPayersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListPayersHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListPayersQueryKey();

  const listPayers = useListPayersHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListPayersHook>>>
  > = ({ signal }) => listPayers(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListPayersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListPayersQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListPayersHook>>>
>;
export type ListPayersQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useListPayers<
  TData = Awaited<ReturnType<ReturnType<typeof useListPayersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListPayersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListPayersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListPayersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListPayers<
  TData = Awaited<ReturnType<ReturnType<typeof useListPayersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListPayersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListPayersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListPayersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListPayers<
  TData = Awaited<ReturnType<ReturnType<typeof useListPayersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListPayersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List payers
 */

export function useListPayers<
  TData = Awaited<ReturnType<ReturnType<typeof useListPayersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListPayersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListPayersQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Create a new payer
 * @summary Create payer
 */
export const useCreatePayerHook = () => {
  const createPayer = useAxiosAlltix<PayerDTO>();

  return useCallback(
    (payerCreateDTO: PayerCreateDTO, signal?: AbortSignal) => {
      return createPayer({
        url: `/payers`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: payerCreateDTO,
        signal,
      });
    },
    [createPayer],
  );
};

export const useCreatePayerMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreatePayerHook>>>,
    TError,
    { data: PayerCreateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreatePayerHook>>>,
  TError,
  { data: PayerCreateDTO },
  TContext
> => {
  const mutationKey = ['createPayer'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createPayer = useCreatePayerHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreatePayerHook>>>,
    { data: PayerCreateDTO }
  > = (props) => {
    const { data } = props ?? {};

    return createPayer(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreatePayerMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreatePayerHook>>>
>;
export type CreatePayerMutationBody = PayerCreateDTO;
export type CreatePayerMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Create payer
 */
export const useCreatePayer = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreatePayerHook>>>,
      TError,
      { data: PayerCreateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreatePayerHook>>>,
  TError,
  { data: PayerCreateDTO },
  TContext
> => {
  const mutationOptions = useCreatePayerMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get the payer by id
 * @summary Get payer by id
 */
export const useGetPayerByIDHook = () => {
  const getPayerByID = useAxiosAlltix<PayerDTO>();

  return useCallback(
    (payerID: string, signal?: AbortSignal) => {
      return getPayerByID({ url: `/payers/${payerID}`, method: 'GET', signal });
    },
    [getPayerByID],
  );
};

export const getGetPayerByIDQueryKey = (payerID?: string) => {
  return [`/payers/${payerID}`] as const;
};

export const useGetPayerByIDQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetPayerByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  payerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPayerByIDHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPayerByIDQueryKey(payerID);

  const getPayerByID = useGetPayerByIDHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetPayerByIDHook>>>
  > = ({ signal }) => getPayerByID(payerID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!payerID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetPayerByIDHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetPayerByIDQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetPayerByIDHook>>>
>;
export type GetPayerByIDQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetPayerByID<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPayerByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  payerID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPayerByIDHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetPayerByIDHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetPayerByIDHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetPayerByID<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPayerByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  payerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPayerByIDHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetPayerByIDHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetPayerByIDHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetPayerByID<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPayerByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  payerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPayerByIDHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get payer by id
 */

export function useGetPayerByID<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPayerByIDHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  payerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPayerByIDHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetPayerByIDQueryOptions(payerID, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Update the selected payer
 * @summary Update payer by id
 */
export const useUpdatePayerByIDHook = () => {
  const updatePayerByID = useAxiosAlltix<PayerDTO>();

  return useCallback(
    (payerID: string, payerUpdateDTO: PayerUpdateDTO) => {
      return updatePayerByID({
        url: `/payers/${payerID}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: payerUpdateDTO,
      });
    },
    [updatePayerByID],
  );
};

export const useUpdatePayerByIDMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdatePayerByIDHook>>>,
    TError,
    { payerID: string; data: PayerUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdatePayerByIDHook>>>,
  TError,
  { payerID: string; data: PayerUpdateDTO },
  TContext
> => {
  const mutationKey = ['updatePayerByID'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updatePayerByID = useUpdatePayerByIDHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdatePayerByIDHook>>>,
    { payerID: string; data: PayerUpdateDTO }
  > = (props) => {
    const { payerID, data } = props ?? {};

    return updatePayerByID(payerID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdatePayerByIDMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdatePayerByIDHook>>>
>;
export type UpdatePayerByIDMutationBody = PayerUpdateDTO;
export type UpdatePayerByIDMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Update payer by id
 */
export const useUpdatePayerByID = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdatePayerByIDHook>>>,
      TError,
      { payerID: string; data: PayerUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdatePayerByIDHook>>>,
  TError,
  { payerID: string; data: PayerUpdateDTO },
  TContext
> => {
  const mutationOptions = useUpdatePayerByIDMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Creates a new collection member (traveller).

Supplying card_serial additionally claims a blank school card for the member, so they can be ticketed immediately instead of waiting for a card to be ordered from Areff. Only school collections accept it, and only for a traveller who holds no issued card at all: a traveller who already has one, delivered or merely ordered, is a conflict, as is a student whose card transfer from another school is still in flight. The card must be unclaimed here and carry no bearer in bobcat belonging to anyone else. The member is created inactive and becomes active in the same transaction that records the card, so a partial failure can never leave a member that gets a second card ordered for them. The operation is idempotent: replaying it after a partial failure resumes from wherever the previous attempt stopped.
 * @summary Create a new collection member (traveller)
 */
export const useCreateCollectionMemberHook = () => {
  const createCollectionMember = useAxiosAlltix<void>();

  return useCallback(
    (
      collectionMemberCreateWithOptionalCardDTO: CollectionMemberCreateWithOptionalCardDTO,
      signal?: AbortSignal,
    ) => {
      return createCollectionMember({
        url: `/collection-members`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: collectionMemberCreateWithOptionalCardDTO,
        signal,
      });
    },
    [createCollectionMember],
  );
};

export const useCreateCollectionMemberMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateCollectionMemberHook>>>,
    TError,
    { data: CollectionMemberCreateWithOptionalCardDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateCollectionMemberHook>>>,
  TError,
  { data: CollectionMemberCreateWithOptionalCardDTO },
  TContext
> => {
  const mutationKey = ['createCollectionMember'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createCollectionMember = useCreateCollectionMemberHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateCollectionMemberHook>>>,
    { data: CollectionMemberCreateWithOptionalCardDTO }
  > = (props) => {
    const { data } = props ?? {};

    return createCollectionMember(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateCollectionMemberMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateCollectionMemberHook>>>
>;
export type CreateCollectionMemberMutationBody =
  CollectionMemberCreateWithOptionalCardDTO;
export type CreateCollectionMemberMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | ConflictResponse
>;

/**
 * @summary Create a new collection member (traveller)
 */
export const useCreateCollectionMember = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateCollectionMemberHook>>>,
      TError,
      { data: CollectionMemberCreateWithOptionalCardDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateCollectionMemberHook>>>,
  TError,
  { data: CollectionMemberCreateWithOptionalCardDTO },
  TContext
> => {
  const mutationOptions = useCreateCollectionMemberMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Creates new student travel collection members
 * @summary Create new student travel collection members
 */
export const useCreateStudentTravelCollectionMembersHook = () => {
  const createStudentTravelCollectionMembers =
    useAxiosAlltix<CollectionMemberDTO[]>();

  return useCallback(
    (
      studentTravelCreateDTO: StudentTravelCreateDTO[],
      signal?: AbortSignal,
    ) => {
      return createStudentTravelCollectionMembers({
        url: `/collection-members/student-travel`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: studentTravelCreateDTO,
        signal,
      });
    },
    [createStudentTravelCollectionMembers],
  );
};

export const useCreateStudentTravelCollectionMembersMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useCreateStudentTravelCollectionMembersHook>>
    >,
    TError,
    { data: StudentTravelCreateDTO[] },
    TContext
  >;
}): UseMutationOptions<
  Awaited<
    ReturnType<ReturnType<typeof useCreateStudentTravelCollectionMembersHook>>
  >,
  TError,
  { data: StudentTravelCreateDTO[] },
  TContext
> => {
  const mutationKey = ['createStudentTravelCollectionMembers'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createStudentTravelCollectionMembers =
    useCreateStudentTravelCollectionMembersHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useCreateStudentTravelCollectionMembersHook>>
    >,
    { data: StudentTravelCreateDTO[] }
  > = (props) => {
    const { data } = props ?? {};

    return createStudentTravelCollectionMembers(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateStudentTravelCollectionMembersMutationResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useCreateStudentTravelCollectionMembersHook>>
  >
>;
export type CreateStudentTravelCollectionMembersMutationBody =
  StudentTravelCreateDTO[];
export type CreateStudentTravelCollectionMembersMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Create new student travel collection members
 */
export const useCreateStudentTravelCollectionMembers = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          ReturnType<typeof useCreateStudentTravelCollectionMembersHook>
        >
      >,
      TError,
      { data: StudentTravelCreateDTO[] },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<ReturnType<typeof useCreateStudentTravelCollectionMembersHook>>
  >,
  TError,
  { data: StudentTravelCreateDTO[] },
  TContext
> => {
  const mutationOptions =
    useCreateStudentTravelCollectionMembersMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Creates new school transportation collection members
 * @summary Create new school transportation collection members
 */
export const useCreateSchoolTransportationCollectionMembersHook = () => {
  const createSchoolTransportationCollectionMembers =
    useAxiosAlltix<CollectionMemberDTO[]>();

  return useCallback(
    (
      schoolTransportationCreateDTO: SchoolTransportationCreateDTO[],
      signal?: AbortSignal,
    ) => {
      return createSchoolTransportationCollectionMembers({
        url: `/collection-members/school-transportation`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: schoolTransportationCreateDTO,
        signal,
      });
    },
    [createSchoolTransportationCollectionMembers],
  );
};

export const useCreateSchoolTransportationCollectionMembersMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<
        ReturnType<typeof useCreateSchoolTransportationCollectionMembersHook>
      >
    >,
    TError,
    { data: SchoolTransportationCreateDTO[] },
    TContext
  >;
}): UseMutationOptions<
  Awaited<
    ReturnType<
      ReturnType<typeof useCreateSchoolTransportationCollectionMembersHook>
    >
  >,
  TError,
  { data: SchoolTransportationCreateDTO[] },
  TContext
> => {
  const mutationKey = ['createSchoolTransportationCollectionMembers'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createSchoolTransportationCollectionMembers =
    useCreateSchoolTransportationCollectionMembersHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<
        ReturnType<typeof useCreateSchoolTransportationCollectionMembersHook>
      >
    >,
    { data: SchoolTransportationCreateDTO[] }
  > = (props) => {
    const { data } = props ?? {};

    return createSchoolTransportationCollectionMembers(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateSchoolTransportationCollectionMembersMutationResult =
  NonNullable<
    Awaited<
      ReturnType<
        ReturnType<typeof useCreateSchoolTransportationCollectionMembersHook>
      >
    >
  >;
export type CreateSchoolTransportationCollectionMembersMutationBody =
  SchoolTransportationCreateDTO[];
export type CreateSchoolTransportationCollectionMembersMutationError =
  ErrorType<BadRequestResponse | UnauthorizedResponse | ForbiddenResponse>;

/**
 * @summary Create new school transportation collection members
 */
export const useCreateSchoolTransportationCollectionMembers = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          ReturnType<typeof useCreateSchoolTransportationCollectionMembersHook>
        >
      >,
      TError,
      { data: SchoolTransportationCreateDTO[] },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<
      ReturnType<typeof useCreateSchoolTransportationCollectionMembersHook>
    >
  >,
  TError,
  { data: SchoolTransportationCreateDTO[] },
  TContext
> => {
  const mutationOptions =
    useCreateSchoolTransportationCollectionMembersMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Creates new child and youth collection members
 * @summary Create new child and youth collection members
 */
export const useCreateChildAndYouthCollectionMembersHook = () => {
  const createChildAndYouthCollectionMembers =
    useAxiosAlltix<CollectionMemberDTO[]>();

  return useCallback(
    (
      childAndYouthCreateDTO: ChildAndYouthCreateDTO[],
      signal?: AbortSignal,
    ) => {
      return createChildAndYouthCollectionMembers({
        url: `/collection-members/child-and-youth`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: childAndYouthCreateDTO,
        signal,
      });
    },
    [createChildAndYouthCollectionMembers],
  );
};

export const useCreateChildAndYouthCollectionMembersMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useCreateChildAndYouthCollectionMembersHook>>
    >,
    TError,
    { data: ChildAndYouthCreateDTO[] },
    TContext
  >;
}): UseMutationOptions<
  Awaited<
    ReturnType<ReturnType<typeof useCreateChildAndYouthCollectionMembersHook>>
  >,
  TError,
  { data: ChildAndYouthCreateDTO[] },
  TContext
> => {
  const mutationKey = ['createChildAndYouthCollectionMembers'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createChildAndYouthCollectionMembers =
    useCreateChildAndYouthCollectionMembersHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useCreateChildAndYouthCollectionMembersHook>>
    >,
    { data: ChildAndYouthCreateDTO[] }
  > = (props) => {
    const { data } = props ?? {};

    return createChildAndYouthCollectionMembers(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateChildAndYouthCollectionMembersMutationResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useCreateChildAndYouthCollectionMembersHook>>
  >
>;
export type CreateChildAndYouthCollectionMembersMutationBody =
  ChildAndYouthCreateDTO[];
export type CreateChildAndYouthCollectionMembersMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Create new child and youth collection members
 */
export const useCreateChildAndYouthCollectionMembers = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          ReturnType<typeof useCreateChildAndYouthCollectionMembersHook>
        >
      >,
      TError,
      { data: ChildAndYouthCreateDTO[] },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<ReturnType<typeof useCreateChildAndYouthCollectionMembersHook>>
  >,
  TError,
  { data: ChildAndYouthCreateDTO[] },
  TContext
> => {
  const mutationOptions =
    useCreateChildAndYouthCollectionMembersMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Delete collection member, if it is the last reference to its traveller, also deletes the traveller
 * @summary Delete collection member by id
 */
export const useDeleteCollectionMemberByIDHook = () => {
  const deleteCollectionMemberByID = useAxiosAlltix<CollectionMemberDTO>();

  return useCallback(
    (collectionMemberID: string) => {
      return deleteCollectionMemberByID({
        url: `/collection-members/${collectionMemberID}`,
        method: 'DELETE',
      });
    },
    [deleteCollectionMemberByID],
  );
};

export const useDeleteCollectionMemberByIDMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteCollectionMemberByIDHook>>>,
    TError,
    { collectionMemberID: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteCollectionMemberByIDHook>>>,
  TError,
  { collectionMemberID: string },
  TContext
> => {
  const mutationKey = ['deleteCollectionMemberByID'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteCollectionMemberByID = useDeleteCollectionMemberByIDHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteCollectionMemberByIDHook>>>,
    { collectionMemberID: string }
  > = (props) => {
    const { collectionMemberID } = props ?? {};

    return deleteCollectionMemberByID(collectionMemberID);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteCollectionMemberByIDMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteCollectionMemberByIDHook>>>
>;

export type DeleteCollectionMemberByIDMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Delete collection member by id
 */
export const useDeleteCollectionMemberByID = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteCollectionMemberByIDHook>>>,
      TError,
      { collectionMemberID: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteCollectionMemberByIDHook>>>,
  TError,
  { collectionMemberID: string },
  TContext
> => {
  const mutationOptions = useDeleteCollectionMemberByIDMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Update collection member by id
 * @summary Update collection member by id
 */
export const useUpdateCollectionMemberHook = () => {
  const updateCollectionMember = useAxiosAlltix<CollectionMemberDTO>();

  return useCallback(
    (
      collectionMemberID: string,
      collectionMemberUpdateDTO: CollectionMemberUpdateDTO,
    ) => {
      return updateCollectionMember({
        url: `/collection-members/${collectionMemberID}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: collectionMemberUpdateDTO,
      });
    },
    [updateCollectionMember],
  );
};

export const useUpdateCollectionMemberMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateCollectionMemberHook>>>,
    TError,
    { collectionMemberID: string; data: CollectionMemberUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateCollectionMemberHook>>>,
  TError,
  { collectionMemberID: string; data: CollectionMemberUpdateDTO },
  TContext
> => {
  const mutationKey = ['updateCollectionMember'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateCollectionMember = useUpdateCollectionMemberHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateCollectionMemberHook>>>,
    { collectionMemberID: string; data: CollectionMemberUpdateDTO }
  > = (props) => {
    const { collectionMemberID, data } = props ?? {};

    return updateCollectionMember(collectionMemberID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateCollectionMemberMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateCollectionMemberHook>>>
>;
export type UpdateCollectionMemberMutationBody = CollectionMemberUpdateDTO;
export type UpdateCollectionMemberMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Update collection member by id
 */
export const useUpdateCollectionMember = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateCollectionMemberHook>>>,
      TError,
      { collectionMemberID: string; data: CollectionMemberUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateCollectionMemberHook>>>,
  TError,
  { collectionMemberID: string; data: CollectionMemberUpdateDTO },
  TContext
> => {
  const mutationOptions = useUpdateCollectionMemberMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Activate a previously inactivated collection member
 * @summary Activate collection member
 */
export const useActivateCollectionMemberHook = () => {
  const activateCollectionMember = useAxiosAlltix<CollectionMemberDTO>();

  return useCallback(
    (collectionMemberID: string, signal?: AbortSignal) => {
      return activateCollectionMember({
        url: `/collection-members/${collectionMemberID}/activate`,
        method: 'POST',
        signal,
      });
    },
    [activateCollectionMember],
  );
};

export const useActivateCollectionMemberMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useActivateCollectionMemberHook>>>,
    TError,
    { collectionMemberID: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useActivateCollectionMemberHook>>>,
  TError,
  { collectionMemberID: string },
  TContext
> => {
  const mutationKey = ['activateCollectionMember'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const activateCollectionMember = useActivateCollectionMemberHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useActivateCollectionMemberHook>>>,
    { collectionMemberID: string }
  > = (props) => {
    const { collectionMemberID } = props ?? {};

    return activateCollectionMember(collectionMemberID);
  };

  return { mutationFn, ...mutationOptions };
};

export type ActivateCollectionMemberMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useActivateCollectionMemberHook>>>
>;

export type ActivateCollectionMemberMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Activate collection member
 */
export const useActivateCollectionMember = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useActivateCollectionMemberHook>>>,
      TError,
      { collectionMemberID: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useActivateCollectionMemberHook>>>,
  TError,
  { collectionMemberID: string },
  TContext
> => {
  const mutationOptions = useActivateCollectionMemberMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Inactivate a collection member
 * @summary Inactivate collection member
 */
export const useInactivateCollectionMemberHook = () => {
  const inactivateCollectionMember = useAxiosAlltix<CollectionMemberDTO>();

  return useCallback(
    (
      collectionMemberID: string,
      collectionMemberInactivateDTO: CollectionMemberInactivateDTO,
      signal?: AbortSignal,
    ) => {
      return inactivateCollectionMember({
        url: `/collection-members/${collectionMemberID}/inactivate`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: collectionMemberInactivateDTO,
        signal,
      });
    },
    [inactivateCollectionMember],
  );
};

export const useInactivateCollectionMemberMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useInactivateCollectionMemberHook>>>,
    TError,
    { collectionMemberID: string; data: CollectionMemberInactivateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useInactivateCollectionMemberHook>>>,
  TError,
  { collectionMemberID: string; data: CollectionMemberInactivateDTO },
  TContext
> => {
  const mutationKey = ['inactivateCollectionMember'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const inactivateCollectionMember = useInactivateCollectionMemberHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useInactivateCollectionMemberHook>>>,
    { collectionMemberID: string; data: CollectionMemberInactivateDTO }
  > = (props) => {
    const { collectionMemberID, data } = props ?? {};

    return inactivateCollectionMember(collectionMemberID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type InactivateCollectionMemberMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useInactivateCollectionMemberHook>>>
>;
export type InactivateCollectionMemberMutationBody =
  CollectionMemberInactivateDTO;
export type InactivateCollectionMemberMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Inactivate collection member
 */
export const useInactivateCollectionMember = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useInactivateCollectionMemberHook>>>,
      TError,
      { collectionMemberID: string; data: CollectionMemberInactivateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useInactivateCollectionMemberHook>>>,
  TError,
  { collectionMemberID: string; data: CollectionMemberInactivateDTO },
  TContext
> => {
  const mutationOptions = useInactivateCollectionMemberMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Update collection ticket claim by id
 * @summary Update collection ticket claim by id
 */
export const useUpdateCollectionTicketClaimHook = () => {
  const updateCollectionTicketClaim =
    useAxiosAlltix<CollectionTicketClaimDTO>();

  return useCallback(
    (
      collectionTicketClaimID: string,
      collectionTicketClaimUpdateDTO: CollectionTicketClaimUpdateDTO,
    ) => {
      return updateCollectionTicketClaim({
        url: `/collection-ticket-claims/${collectionTicketClaimID}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: collectionTicketClaimUpdateDTO,
      });
    },
    [updateCollectionTicketClaim],
  );
};

export const useUpdateCollectionTicketClaimMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateCollectionTicketClaimHook>>>,
    TError,
    { collectionTicketClaimID: string; data: CollectionTicketClaimUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateCollectionTicketClaimHook>>>,
  TError,
  { collectionTicketClaimID: string; data: CollectionTicketClaimUpdateDTO },
  TContext
> => {
  const mutationKey = ['updateCollectionTicketClaim'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateCollectionTicketClaim = useUpdateCollectionTicketClaimHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateCollectionTicketClaimHook>>>,
    { collectionTicketClaimID: string; data: CollectionTicketClaimUpdateDTO }
  > = (props) => {
    const { collectionTicketClaimID, data } = props ?? {};

    return updateCollectionTicketClaim(collectionTicketClaimID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateCollectionTicketClaimMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateCollectionTicketClaimHook>>>
>;
export type UpdateCollectionTicketClaimMutationBody =
  CollectionTicketClaimUpdateDTO;
export type UpdateCollectionTicketClaimMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Update collection ticket claim by id
 */
export const useUpdateCollectionTicketClaim = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useUpdateCollectionTicketClaimHook>>
      >,
      TError,
      { collectionTicketClaimID: string; data: CollectionTicketClaimUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateCollectionTicketClaimHook>>>,
  TError,
  { collectionTicketClaimID: string; data: CollectionTicketClaimUpdateDTO },
  TContext
> => {
  const mutationOptions =
    useUpdateCollectionTicketClaimMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Delete collection ticket claim by id
 * @summary Delete collection ticket claim by id
 */
export const useDeleteCollectionTicketClaimHook = () => {
  const deleteCollectionTicketClaim =
    useAxiosAlltix<CollectionTicketClaimDTO>();

  return useCallback(
    (collectionTicketClaimID: string) => {
      return deleteCollectionTicketClaim({
        url: `/collection-ticket-claims/${collectionTicketClaimID}`,
        method: 'DELETE',
      });
    },
    [deleteCollectionTicketClaim],
  );
};

export const useDeleteCollectionTicketClaimMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | NotImplementedResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteCollectionTicketClaimHook>>>,
    TError,
    { collectionTicketClaimID: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteCollectionTicketClaimHook>>>,
  TError,
  { collectionTicketClaimID: string },
  TContext
> => {
  const mutationKey = ['deleteCollectionTicketClaim'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteCollectionTicketClaim = useDeleteCollectionTicketClaimHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteCollectionTicketClaimHook>>>,
    { collectionTicketClaimID: string }
  > = (props) => {
    const { collectionTicketClaimID } = props ?? {};

    return deleteCollectionTicketClaim(collectionTicketClaimID);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteCollectionTicketClaimMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteCollectionTicketClaimHook>>>
>;

export type DeleteCollectionTicketClaimMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | NotImplementedResponse
>;

/**
 * @summary Delete collection ticket claim by id
 */
export const useDeleteCollectionTicketClaim = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | NotImplementedResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useDeleteCollectionTicketClaimHook>>
      >,
      TError,
      { collectionTicketClaimID: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteCollectionTicketClaimHook>>>,
  TError,
  { collectionTicketClaimID: string },
  TContext
> => {
  const mutationOptions =
    useDeleteCollectionTicketClaimMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * List member states for a collection ticket claim
 * @summary List member states for a collection ticket claim
 */
export const useListCollectionTicketClaimMemberStatesHook = () => {
  const listCollectionTicketClaimMemberStates =
    useAxiosAlltix<CollectionTicketClaimMemberStateDTO[]>();

  return useCallback(
    (collectionTicketClaimID: string, signal?: AbortSignal) => {
      return listCollectionTicketClaimMemberStates({
        url: `/collection-ticket-claims/${collectionTicketClaimID}/member-states`,
        method: 'GET',
        signal,
      });
    },
    [listCollectionTicketClaimMemberStates],
  );
};

export const getListCollectionTicketClaimMemberStatesQueryKey = (
  collectionTicketClaimID?: string,
) => {
  return [
    `/collection-ticket-claims/${collectionTicketClaimID}/member-states`,
  ] as const;
};

export const useListCollectionTicketClaimMemberStatesQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useListCollectionTicketClaimMemberStatesHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  collectionTicketClaimID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useListCollectionTicketClaimMemberStatesHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListCollectionTicketClaimMemberStatesQueryKey(collectionTicketClaimID);

  const listCollectionTicketClaimMemberStates =
    useListCollectionTicketClaimMemberStatesHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<
        ReturnType<typeof useListCollectionTicketClaimMemberStatesHook>
      >
    >
  > = ({ signal }) =>
    listCollectionTicketClaimMemberStates(collectionTicketClaimID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!collectionTicketClaimID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<
      ReturnType<
        ReturnType<typeof useListCollectionTicketClaimMemberStatesHook>
      >
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListCollectionTicketClaimMemberStatesQueryResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useListCollectionTicketClaimMemberStatesHook>>
  >
>;
export type ListCollectionTicketClaimMemberStatesQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

export function useListCollectionTicketClaimMemberStates<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListCollectionTicketClaimMemberStatesHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  collectionTicketClaimID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useListCollectionTicketClaimMemberStatesHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useListCollectionTicketClaimMemberStatesHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useListCollectionTicketClaimMemberStatesHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListCollectionTicketClaimMemberStates<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListCollectionTicketClaimMemberStatesHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  collectionTicketClaimID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useListCollectionTicketClaimMemberStatesHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useListCollectionTicketClaimMemberStatesHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useListCollectionTicketClaimMemberStatesHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListCollectionTicketClaimMemberStates<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListCollectionTicketClaimMemberStatesHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  collectionTicketClaimID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useListCollectionTicketClaimMemberStatesHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List member states for a collection ticket claim
 */

export function useListCollectionTicketClaimMemberStates<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListCollectionTicketClaimMemberStatesHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  collectionTicketClaimID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useListCollectionTicketClaimMemberStatesHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListCollectionTicketClaimMemberStatesQueryOptions(
    collectionTicketClaimID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns all members for the specified collection with traveller data
 * @summary Get collection members
 */
export const useGetCollectionMembersHook = () => {
  const getCollectionMembers =
    useAxiosAlltix<CollectionMemberWithTravellerDTO[]>();

  return useCallback(
    (collectionID: string, signal?: AbortSignal) => {
      return getCollectionMembers({
        url: `/collections/${collectionID}/members`,
        method: 'GET',
        signal,
      });
    },
    [getCollectionMembers],
  );
};

export const getGetCollectionMembersQueryKey = (collectionID?: string) => {
  return [`/collections/${collectionID}/members`] as const;
};

export const useGetCollectionMembersQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetCollectionMembersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  collectionID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCollectionMembersHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetCollectionMembersQueryKey(collectionID);

  const getCollectionMembers = useGetCollectionMembersHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetCollectionMembersHook>>>
  > = ({ signal }) => getCollectionMembers(collectionID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!collectionID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetCollectionMembersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetCollectionMembersQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetCollectionMembersHook>>>
>;
export type GetCollectionMembersQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetCollectionMembers<
  TData = Awaited<ReturnType<ReturnType<typeof useGetCollectionMembersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  collectionID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCollectionMembersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetCollectionMembersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetCollectionMembersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetCollectionMembers<
  TData = Awaited<ReturnType<ReturnType<typeof useGetCollectionMembersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  collectionID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCollectionMembersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetCollectionMembersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetCollectionMembersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetCollectionMembers<
  TData = Awaited<ReturnType<ReturnType<typeof useGetCollectionMembersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  collectionID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCollectionMembersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get collection members
 */

export function useGetCollectionMembers<
  TData = Awaited<ReturnType<ReturnType<typeof useGetCollectionMembersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  collectionID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCollectionMembersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetCollectionMembersQueryOptions(
    collectionID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Import multiple collection members from structured data with support for different import strategies
 * @summary Import collection members in bulk
 */
export const useImportCollectionMembersHook = () => {
  const importCollectionMembers = useAxiosAlltix<CollectionImportResultDTO>();

  return useCallback(
    (
      collectionID: string,
      collectionImportRequestDTO: CollectionImportRequestDTO,
      signal?: AbortSignal,
    ) => {
      return importCollectionMembers({
        url: `/collections/${collectionID}/members/import`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: collectionImportRequestDTO,
        signal,
      });
    },
    [importCollectionMembers],
  );
};

export const useImportCollectionMembersMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | NotImplementedResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useImportCollectionMembersHook>>>,
    TError,
    { collectionID: string; data: CollectionImportRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useImportCollectionMembersHook>>>,
  TError,
  { collectionID: string; data: CollectionImportRequestDTO },
  TContext
> => {
  const mutationKey = ['importCollectionMembers'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const importCollectionMembers = useImportCollectionMembersHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useImportCollectionMembersHook>>>,
    { collectionID: string; data: CollectionImportRequestDTO }
  > = (props) => {
    const { collectionID, data } = props ?? {};

    return importCollectionMembers(collectionID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type ImportCollectionMembersMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useImportCollectionMembersHook>>>
>;
export type ImportCollectionMembersMutationBody = CollectionImportRequestDTO;
export type ImportCollectionMembersMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | NotImplementedResponse
>;

/**
 * @summary Import collection members in bulk
 */
export const useImportCollectionMembers = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | NotImplementedResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useImportCollectionMembersHook>>>,
      TError,
      { collectionID: string; data: CollectionImportRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useImportCollectionMembersHook>>>,
  TError,
  { collectionID: string; data: CollectionImportRequestDTO },
  TContext
> => {
  const mutationOptions = useImportCollectionMembersMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Validate import data without making any changes (dry run)
 * @summary Validate collection member import
 */
export const useValidateCollectionMemberImportHook = () => {
  const validateCollectionMemberImport =
    useAxiosAlltix<CollectionImportResultDTO>();

  return useCallback(
    (
      collectionID: string,
      collectionImportRequestDTO: CollectionImportRequestDTO,
      signal?: AbortSignal,
    ) => {
      return validateCollectionMemberImport({
        url: `/collections/${collectionID}/members/import/validate`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: collectionImportRequestDTO,
        signal,
      });
    },
    [validateCollectionMemberImport],
  );
};

export const useValidateCollectionMemberImportMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | NotImplementedResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useValidateCollectionMemberImportHook>>
    >,
    TError,
    { collectionID: string; data: CollectionImportRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useValidateCollectionMemberImportHook>>>,
  TError,
  { collectionID: string; data: CollectionImportRequestDTO },
  TContext
> => {
  const mutationKey = ['validateCollectionMemberImport'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const validateCollectionMemberImport =
    useValidateCollectionMemberImportHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useValidateCollectionMemberImportHook>>
    >,
    { collectionID: string; data: CollectionImportRequestDTO }
  > = (props) => {
    const { collectionID, data } = props ?? {};

    return validateCollectionMemberImport(collectionID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type ValidateCollectionMemberImportMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useValidateCollectionMemberImportHook>>>
>;
export type ValidateCollectionMemberImportMutationBody =
  CollectionImportRequestDTO;
export type ValidateCollectionMemberImportMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | NotImplementedResponse
>;

/**
 * @summary Validate collection member import
 */
export const useValidateCollectionMemberImport = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | NotImplementedResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useValidateCollectionMemberImportHook>>
      >,
      TError,
      { collectionID: string; data: CollectionImportRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useValidateCollectionMemberImportHook>>>,
  TError,
  { collectionID: string; data: CollectionImportRequestDTO },
  TContext
> => {
  const mutationOptions =
    useValidateCollectionMemberImportMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Returns a list of all imports for a collection, ordered by creation date descending
 * @summary List collection import history
 */
export const useListCollectionImportsHook = () => {
  const listCollectionImports = useAxiosAlltix<CollectionImportListItemDTO[]>();

  return useCallback(
    (collectionID: string, signal?: AbortSignal) => {
      return listCollectionImports({
        url: `/collections/${collectionID}/members/imports`,
        method: 'GET',
        signal,
      });
    },
    [listCollectionImports],
  );
};

export const getListCollectionImportsQueryKey = (collectionID?: string) => {
  return [`/collections/${collectionID}/members/imports`] as const;
};

export const useListCollectionImportsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListCollectionImportsHook>>>,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(
  collectionID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListCollectionImportsHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListCollectionImportsQueryKey(collectionID);

  const listCollectionImports = useListCollectionImportsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListCollectionImportsHook>>>
  > = ({ signal }) => listCollectionImports(collectionID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!collectionID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListCollectionImportsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListCollectionImportsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListCollectionImportsHook>>>
>;
export type ListCollectionImportsQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse
>;

export function useListCollectionImports<
  TData = Awaited<ReturnType<ReturnType<typeof useListCollectionImportsHook>>>,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(
  collectionID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListCollectionImportsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListCollectionImportsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListCollectionImportsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListCollectionImports<
  TData = Awaited<ReturnType<ReturnType<typeof useListCollectionImportsHook>>>,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(
  collectionID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListCollectionImportsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListCollectionImportsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListCollectionImportsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListCollectionImports<
  TData = Awaited<ReturnType<ReturnType<typeof useListCollectionImportsHook>>>,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(
  collectionID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListCollectionImportsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List collection import history
 */

export function useListCollectionImports<
  TData = Awaited<ReturnType<ReturnType<typeof useListCollectionImportsHook>>>,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(
  collectionID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListCollectionImportsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListCollectionImportsQueryOptions(
    collectionID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Get a single collection import with full change details
 */
export const useGetCollectionImportHook = () => {
  const getCollectionImport = useAxiosAlltix<CollectionImportResultDTO>();

  return useCallback(
    (collectionID: string, importID: string, signal?: AbortSignal) => {
      return getCollectionImport({
        url: `/collections/${collectionID}/members/imports/${importID}`,
        method: 'GET',
        signal,
      });
    },
    [getCollectionImport],
  );
};

export const getGetCollectionImportQueryKey = (
  collectionID?: string,
  importID?: string,
) => {
  return [`/collections/${collectionID}/members/imports/${importID}`] as const;
};

export const useGetCollectionImportQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetCollectionImportHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
>(
  collectionID: string,
  importID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCollectionImportHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetCollectionImportQueryKey(collectionID, importID);

  const getCollectionImport = useGetCollectionImportHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetCollectionImportHook>>>
  > = ({ signal }) => getCollectionImport(collectionID, importID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(collectionID && importID),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetCollectionImportHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetCollectionImportQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetCollectionImportHook>>>
>;
export type GetCollectionImportQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
>;

export function useGetCollectionImport<
  TData = Awaited<ReturnType<ReturnType<typeof useGetCollectionImportHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
>(
  collectionID: string,
  importID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCollectionImportHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetCollectionImportHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetCollectionImportHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetCollectionImport<
  TData = Awaited<ReturnType<ReturnType<typeof useGetCollectionImportHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
>(
  collectionID: string,
  importID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCollectionImportHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetCollectionImportHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetCollectionImportHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetCollectionImport<
  TData = Awaited<ReturnType<ReturnType<typeof useGetCollectionImportHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
>(
  collectionID: string,
  importID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCollectionImportHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get a single collection import with full change details
 */

export function useGetCollectionImport<
  TData = Awaited<ReturnType<ReturnType<typeof useGetCollectionImportHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
>(
  collectionID: string,
  importID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCollectionImportHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetCollectionImportQueryOptions(
    collectionID,
    importID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns all ticket claims for the specified collection
 * @summary Get collection ticket claims
 */
export const useGetCollectionTicketClaimsHook = () => {
  const getCollectionTicketClaims =
    useAxiosAlltix<CollectionTicketClaimDTO[]>();

  return useCallback(
    (collectionID: string, signal?: AbortSignal) => {
      return getCollectionTicketClaims({
        url: `/collections/${collectionID}/ticket-claims`,
        method: 'GET',
        signal,
      });
    },
    [getCollectionTicketClaims],
  );
};

export const getGetCollectionTicketClaimsQueryKey = (collectionID?: string) => {
  return [`/collections/${collectionID}/ticket-claims`] as const;
};

export const useGetCollectionTicketClaimsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetCollectionTicketClaimsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  collectionID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetCollectionTicketClaimsHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetCollectionTicketClaimsQueryKey(collectionID);

  const getCollectionTicketClaims = useGetCollectionTicketClaimsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetCollectionTicketClaimsHook>>>
  > = ({ signal }) => getCollectionTicketClaims(collectionID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!collectionID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetCollectionTicketClaimsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetCollectionTicketClaimsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetCollectionTicketClaimsHook>>>
>;
export type GetCollectionTicketClaimsQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

export function useGetCollectionTicketClaims<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetCollectionTicketClaimsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  collectionID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetCollectionTicketClaimsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetCollectionTicketClaimsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetCollectionTicketClaimsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetCollectionTicketClaims<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetCollectionTicketClaimsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  collectionID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetCollectionTicketClaimsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetCollectionTicketClaimsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetCollectionTicketClaimsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetCollectionTicketClaims<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetCollectionTicketClaimsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  collectionID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetCollectionTicketClaimsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get collection ticket claims
 */

export function useGetCollectionTicketClaims<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetCollectionTicketClaimsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  collectionID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetCollectionTicketClaimsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetCollectionTicketClaimsQueryOptions(
    collectionID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Create a collection ticket claim
 */
export const useCreateCollectionTicketClaimHook = () => {
  const createCollectionTicketClaim =
    useAxiosAlltix<CollectionTicketClaimDTO>();

  return useCallback(
    (
      collectionID: string,
      collectionTicketClaimCreateDTO: CollectionTicketClaimCreateDTO,
      signal?: AbortSignal,
    ) => {
      return createCollectionTicketClaim({
        url: `/collections/${collectionID}/ticket-claims`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: collectionTicketClaimCreateDTO,
        signal,
      });
    },
    [createCollectionTicketClaim],
  );
};

export const useCreateCollectionTicketClaimMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | NotImplementedResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateCollectionTicketClaimHook>>>,
    TError,
    { collectionID: string; data: CollectionTicketClaimCreateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateCollectionTicketClaimHook>>>,
  TError,
  { collectionID: string; data: CollectionTicketClaimCreateDTO },
  TContext
> => {
  const mutationKey = ['createCollectionTicketClaim'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createCollectionTicketClaim = useCreateCollectionTicketClaimHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateCollectionTicketClaimHook>>>,
    { collectionID: string; data: CollectionTicketClaimCreateDTO }
  > = (props) => {
    const { collectionID, data } = props ?? {};

    return createCollectionTicketClaim(collectionID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateCollectionTicketClaimMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateCollectionTicketClaimHook>>>
>;
export type CreateCollectionTicketClaimMutationBody =
  CollectionTicketClaimCreateDTO;
export type CreateCollectionTicketClaimMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | NotImplementedResponse
>;

/**
 * @summary Create a collection ticket claim
 */
export const useCreateCollectionTicketClaim = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | NotImplementedResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useCreateCollectionTicketClaimHook>>
      >,
      TError,
      { collectionID: string; data: CollectionTicketClaimCreateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateCollectionTicketClaimHook>>>,
  TError,
  { collectionID: string; data: CollectionTicketClaimCreateDTO },
  TContext
> => {
  const mutationOptions =
    useCreateCollectionTicketClaimMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * List the API keys of the logged in account, across all of its user profiles, including revoked and expired keys.

 * @summary List API keys
 */
export const useListAPIKeysHook = () => {
  const listAPIKeys = useAxiosAlltix<APIKeyDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listAPIKeys({ url: `/api-keys`, method: 'GET', signal });
    },
    [listAPIKeys],
  );
};

export const getListAPIKeysQueryKey = () => {
  return [`/api-keys`] as const;
};

export const useListAPIKeysQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListAPIKeysHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListAPIKeysHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAPIKeysQueryKey();

  const listAPIKeys = useListAPIKeysHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListAPIKeysHook>>>
  > = ({ signal }) => listAPIKeys(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListAPIKeysHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListAPIKeysQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListAPIKeysHook>>>
>;
export type ListAPIKeysQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useListAPIKeys<
  TData = Awaited<ReturnType<ReturnType<typeof useListAPIKeysHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListAPIKeysHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListAPIKeysHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListAPIKeysHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListAPIKeys<
  TData = Awaited<ReturnType<ReturnType<typeof useListAPIKeysHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListAPIKeysHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListAPIKeysHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListAPIKeysHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListAPIKeys<
  TData = Awaited<ReturnType<ReturnType<typeof useListAPIKeysHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListAPIKeysHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List API keys
 */

export function useListAPIKeys<
  TData = Awaited<ReturnType<ReturnType<typeof useListAPIKeysHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListAPIKeysHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListAPIKeysQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Create an API key that acts as the current user profile. The key is returned in clear text in this response only.

 * @summary Create API key
 */
export const useCreateAPIKeyHook = () => {
  const createAPIKey = useAxiosAlltix<APIKeyCreatedDTO>();

  return useCallback(
    (aPIKeyCreateDTO: APIKeyCreateDTO, signal?: AbortSignal) => {
      return createAPIKey({
        url: `/api-keys`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: aPIKeyCreateDTO,
        signal,
      });
    },
    [createAPIKey],
  );
};

export const useCreateAPIKeyMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateAPIKeyHook>>>,
    TError,
    { data: APIKeyCreateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateAPIKeyHook>>>,
  TError,
  { data: APIKeyCreateDTO },
  TContext
> => {
  const mutationKey = ['createAPIKey'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createAPIKey = useCreateAPIKeyHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateAPIKeyHook>>>,
    { data: APIKeyCreateDTO }
  > = (props) => {
    const { data } = props ?? {};

    return createAPIKey(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateAPIKeyMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateAPIKeyHook>>>
>;
export type CreateAPIKeyMutationBody = APIKeyCreateDTO;
export type CreateAPIKeyMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Create API key
 */
export const useCreateAPIKey = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateAPIKeyHook>>>,
      TError,
      { data: APIKeyCreateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateAPIKeyHook>>>,
  TError,
  { data: APIKeyCreateDTO },
  TContext
> => {
  const mutationOptions = useCreateAPIKeyMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Revoke an API key of the logged in account. Revoking an already revoked key is a no-op.
 * @summary Revoke API key
 */
export const useRevokeAPIKeyHook = () => {
  const revokeAPIKey = useAxiosAlltix<APIKeyDTO>();

  return useCallback(
    (apiKeyID: string) => {
      return revokeAPIKey({ url: `/api-keys/${apiKeyID}`, method: 'DELETE' });
    },
    [revokeAPIKey],
  );
};

export const useRevokeAPIKeyMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useRevokeAPIKeyHook>>>,
    TError,
    { apiKeyID: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useRevokeAPIKeyHook>>>,
  TError,
  { apiKeyID: string },
  TContext
> => {
  const mutationKey = ['revokeAPIKey'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const revokeAPIKey = useRevokeAPIKeyHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useRevokeAPIKeyHook>>>,
    { apiKeyID: string }
  > = (props) => {
    const { apiKeyID } = props ?? {};

    return revokeAPIKey(apiKeyID);
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeAPIKeyMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useRevokeAPIKeyHook>>>
>;

export type RevokeAPIKeyMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Revoke API key
 */
export const useRevokeAPIKey = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useRevokeAPIKeyHook>>>,
      TError,
      { apiKeyID: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useRevokeAPIKeyHook>>>,
  TError,
  { apiKeyID: string },
  TContext
> => {
  const mutationOptions = useRevokeAPIKeyMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * List Allpay users
 * @summary List Allpay users
 */
export const useListAllpayUsersHook = () => {
  const listAllpayUsers = useAxiosAlltix<AllpayUserDTO[]>();

  return useCallback(
    (params?: ListAllpayUsersParams, signal?: AbortSignal) => {
      return listAllpayUsers({
        url: `/allpay-users`,
        method: 'GET',
        params,
        signal,
      });
    },
    [listAllpayUsers],
  );
};

export const getListAllpayUsersQueryKey = (params?: ListAllpayUsersParams) => {
  return [`/allpay-users`, ...(params ? [params] : [])] as const;
};

export const useListAllpayUsersQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListAllpayUsersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: ListAllpayUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListAllpayUsersHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAllpayUsersQueryKey(params);

  const listAllpayUsers = useListAllpayUsersHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListAllpayUsersHook>>>
  > = ({ signal }) => listAllpayUsers(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListAllpayUsersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListAllpayUsersQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListAllpayUsersHook>>>
>;
export type ListAllpayUsersQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useListAllpayUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useListAllpayUsersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: undefined | ListAllpayUsersParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListAllpayUsersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListAllpayUsersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListAllpayUsersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListAllpayUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useListAllpayUsersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: ListAllpayUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListAllpayUsersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListAllpayUsersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListAllpayUsersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListAllpayUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useListAllpayUsersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: ListAllpayUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListAllpayUsersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List Allpay users
 */

export function useListAllpayUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useListAllpayUsersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: ListAllpayUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListAllpayUsersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListAllpayUsersQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Create Allpay user
 * @summary Create Allpay user
 */
export const useCreateAllpayUserHook = () => {
  const createAllpayUser = useAxiosAlltix<AllpayUserDTO>();

  return useCallback(
    (allpayUserCreateDTO: AllpayUserCreateDTO, signal?: AbortSignal) => {
      return createAllpayUser({
        url: `/allpay-users`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: allpayUserCreateDTO,
        signal,
      });
    },
    [createAllpayUser],
  );
};

export const useCreateAllpayUserMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | PaymentRequiredResponse
    | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateAllpayUserHook>>>,
    TError,
    { data: AllpayUserCreateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateAllpayUserHook>>>,
  TError,
  { data: AllpayUserCreateDTO },
  TContext
> => {
  const mutationKey = ['createAllpayUser'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createAllpayUser = useCreateAllpayUserHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateAllpayUserHook>>>,
    { data: AllpayUserCreateDTO }
  > = (props) => {
    const { data } = props ?? {};

    return createAllpayUser(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateAllpayUserMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateAllpayUserHook>>>
>;
export type CreateAllpayUserMutationBody = AllpayUserCreateDTO;
export type CreateAllpayUserMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | PaymentRequiredResponse
  | ForbiddenResponse
>;

/**
 * @summary Create Allpay user
 */
export const useCreateAllpayUser = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | PaymentRequiredResponse
    | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateAllpayUserHook>>>,
      TError,
      { data: AllpayUserCreateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateAllpayUserHook>>>,
  TError,
  { data: AllpayUserCreateDTO },
  TContext
> => {
  const mutationOptions = useCreateAllpayUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get AllpayUser by ID
 * @summary Get AllpayUser
 */
export const useGetAllpayUserHook = () => {
  const getAllpayUser = useAxiosAlltix<AllpayUserWithStateDTO>();

  return useCallback(
    (id: string, signal?: AbortSignal) => {
      return getAllpayUser({
        url: `/allpay-users/${id}`,
        method: 'GET',
        signal,
      });
    },
    [getAllpayUser],
  );
};

export const getGetAllpayUserQueryKey = (id?: string) => {
  return [`/allpay-users/${id}`] as const;
};

export const useGetAllpayUserQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetAllpayUserHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetAllpayUserHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllpayUserQueryKey(id);

  const getAllpayUser = useGetAllpayUserHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetAllpayUserHook>>>
  > = ({ signal }) => getAllpayUser(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetAllpayUserHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetAllpayUserQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetAllpayUserHook>>>
>;
export type GetAllpayUserQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

export function useGetAllpayUser<
  TData = Awaited<ReturnType<ReturnType<typeof useGetAllpayUserHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetAllpayUserHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetAllpayUserHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetAllpayUserHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetAllpayUser<
  TData = Awaited<ReturnType<ReturnType<typeof useGetAllpayUserHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetAllpayUserHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetAllpayUserHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetAllpayUserHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetAllpayUser<
  TData = Awaited<ReturnType<ReturnType<typeof useGetAllpayUserHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetAllpayUserHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get AllpayUser
 */

export function useGetAllpayUser<
  TData = Awaited<ReturnType<ReturnType<typeof useGetAllpayUserHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetAllpayUserHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetAllpayUserQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Update AllpayUser credit limit. The new limit must fit the organization's credit limit, and may not be lower than what the Allpay user has already spent this month.

 * @summary Update AllpayUser credit limit
 */
export const useUpdateAllpayUserCreditLimitHook = () => {
  const updateAllpayUserCreditLimit = useAxiosAlltix<AllpayUserDTO>();

  return useCallback(
    (
      id: string,
      allpayUserCreditLimitPatchDTO: AllpayUserCreditLimitPatchDTO,
    ) => {
      return updateAllpayUserCreditLimit({
        url: `/allpay-users/${id}`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: allpayUserCreditLimitPatchDTO,
      });
    },
    [updateAllpayUserCreditLimit],
  );
};

export const useUpdateAllpayUserCreditLimitMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | PaymentRequiredResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateAllpayUserCreditLimitHook>>>,
    TError,
    { id: string; data: AllpayUserCreditLimitPatchDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateAllpayUserCreditLimitHook>>>,
  TError,
  { id: string; data: AllpayUserCreditLimitPatchDTO },
  TContext
> => {
  const mutationKey = ['updateAllpayUserCreditLimit'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateAllpayUserCreditLimit = useUpdateAllpayUserCreditLimitHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateAllpayUserCreditLimitHook>>>,
    { id: string; data: AllpayUserCreditLimitPatchDTO }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateAllpayUserCreditLimit(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateAllpayUserCreditLimitMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateAllpayUserCreditLimitHook>>>
>;
export type UpdateAllpayUserCreditLimitMutationBody =
  AllpayUserCreditLimitPatchDTO;
export type UpdateAllpayUserCreditLimitMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | PaymentRequiredResponse
  | ForbiddenResponse
  | NotFoundResponse
  | ConflictResponse
>;

/**
 * @summary Update AllpayUser credit limit
 */
export const useUpdateAllpayUserCreditLimit = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | PaymentRequiredResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useUpdateAllpayUserCreditLimitHook>>
      >,
      TError,
      { id: string; data: AllpayUserCreditLimitPatchDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateAllpayUserCreditLimitHook>>>,
  TError,
  { id: string; data: AllpayUserCreditLimitPatchDTO },
  TContext
> => {
  const mutationOptions =
    useUpdateAllpayUserCreditLimitMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Delete the selected AllpayUser
 * @summary Delete AllpayUser
 */
export const useDeleteAllpayUserHook = () => {
  const deleteAllpayUser = useAxiosAlltix<AllpayUserDTO>();

  return useCallback(
    (id: string) => {
      return deleteAllpayUser({ url: `/allpay-users/${id}`, method: 'DELETE' });
    },
    [deleteAllpayUser],
  );
};

export const useDeleteAllpayUserMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteAllpayUserHook>>>,
    TError,
    { id: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteAllpayUserHook>>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ['deleteAllpayUser'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteAllpayUser = useDeleteAllpayUserHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteAllpayUserHook>>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return deleteAllpayUser(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteAllpayUserMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteAllpayUserHook>>>
>;

export type DeleteAllpayUserMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Delete AllpayUser
 */
export const useDeleteAllpayUser = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteAllpayUserHook>>>,
      TError,
      { id: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteAllpayUserHook>>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = useDeleteAllpayUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Gets what the Allpay user has spent this month, as reported by Allpay. Allpay calls this the uninvoiced total; it covers the current month whether or not Allpay has invoiced it yet. A credit limit may not be lowered below it.

 * @summary Summary of uninvoiced transactions
 */
export const useGetUninvoicedSummaryHook = () => {
  const getUninvoicedSummary =
    useAxiosAlltix<AllpayUninvoicedTransactionSummaryDTO>();

  return useCallback(
    (id: string, signal?: AbortSignal) => {
      return getUninvoicedSummary({
        url: `/allpay-users/${id}/transactions/uninvoiced-summary`,
        method: 'GET',
        signal,
      });
    },
    [getUninvoicedSummary],
  );
};

export const getGetUninvoicedSummaryQueryKey = (id?: string) => {
  return [`/allpay-users/${id}/transactions/uninvoiced-summary`] as const;
};

export const useGetUninvoicedSummaryQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetUninvoicedSummaryHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUninvoicedSummaryHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetUninvoicedSummaryQueryKey(id);

  const getUninvoicedSummary = useGetUninvoicedSummaryHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetUninvoicedSummaryHook>>>
  > = ({ signal }) => getUninvoicedSummary(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetUninvoicedSummaryHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetUninvoicedSummaryQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetUninvoicedSummaryHook>>>
>;
export type GetUninvoicedSummaryQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetUninvoicedSummary<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUninvoicedSummaryHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUninvoicedSummaryHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetUninvoicedSummaryHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetUninvoicedSummaryHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetUninvoicedSummary<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUninvoicedSummaryHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUninvoicedSummaryHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetUninvoicedSummaryHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetUninvoicedSummaryHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetUninvoicedSummary<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUninvoicedSummaryHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUninvoicedSummaryHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Summary of uninvoiced transactions
 */

export function useGetUninvoicedSummary<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUninvoicedSummaryHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUninvoicedSummaryHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetUninvoicedSummaryQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Imports transactions from AllPay and Alltix Resellers
 * @summary Import external transactions
 */
export const useImportExternalTransactionsHook = () => {
  const importExternalTransactions = useAxiosAlltix<void>();

  return useCallback(
    (
      externalTransactionCreateDTO: ExternalTransactionCreateDTO[],
      signal?: AbortSignal,
    ) => {
      return importExternalTransactions({
        url: `/external-transactions`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: externalTransactionCreateDTO,
        signal,
      });
    },
    [importExternalTransactions],
  );
};

export const useImportExternalTransactionsMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useImportExternalTransactionsHook>>>,
    TError,
    { data: ExternalTransactionCreateDTO[] },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useImportExternalTransactionsHook>>>,
  TError,
  { data: ExternalTransactionCreateDTO[] },
  TContext
> => {
  const mutationKey = ['importExternalTransactions'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const importExternalTransactions = useImportExternalTransactionsHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useImportExternalTransactionsHook>>>,
    { data: ExternalTransactionCreateDTO[] }
  > = (props) => {
    const { data } = props ?? {};

    return importExternalTransactions(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type ImportExternalTransactionsMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useImportExternalTransactionsHook>>>
>;
export type ImportExternalTransactionsMutationBody =
  ExternalTransactionCreateDTO[];
export type ImportExternalTransactionsMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Import external transactions
 */
export const useImportExternalTransactions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useImportExternalTransactionsHook>>>,
      TError,
      { data: ExternalTransactionCreateDTO[] },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useImportExternalTransactionsHook>>>,
  TError,
  { data: ExternalTransactionCreateDTO[] },
  TContext
> => {
  const mutationOptions = useImportExternalTransactionsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * List Bobcat tags
 * @summary List Bobcat tags
 */
export const useGetBobcatTagsListHook = () => {
  const getBobcatTagsList = useAxiosAlltix<BobcatTagDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getBobcatTagsList({ url: `/tags`, method: 'GET', signal });
    },
    [getBobcatTagsList],
  );
};

export const getGetBobcatTagsListQueryKey = () => {
  return [`/tags`] as const;
};

export const useGetBobcatTagsListQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetBobcatTagsListHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useGetBobcatTagsListHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBobcatTagsListQueryKey();

  const getBobcatTagsList = useGetBobcatTagsListHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetBobcatTagsListHook>>>
  > = ({ signal }) => getBobcatTagsList(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetBobcatTagsListHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetBobcatTagsListQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetBobcatTagsListHook>>>
>;
export type GetBobcatTagsListQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetBobcatTagsList<
  TData = Awaited<ReturnType<ReturnType<typeof useGetBobcatTagsListHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetBobcatTagsListHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetBobcatTagsListHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetBobcatTagsListHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetBobcatTagsList<
  TData = Awaited<ReturnType<ReturnType<typeof useGetBobcatTagsListHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetBobcatTagsListHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetBobcatTagsListHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetBobcatTagsListHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetBobcatTagsList<
  TData = Awaited<ReturnType<ReturnType<typeof useGetBobcatTagsListHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetBobcatTagsListHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List Bobcat tags
 */

export function useGetBobcatTagsList<
  TData = Awaited<ReturnType<ReturnType<typeof useGetBobcatTagsListHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetBobcatTagsListHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetBobcatTagsListQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * List Bobcat offers groups
 * @summary List Bobcat offers groups
 */
export const useListBobcatOfferGroupsHook = () => {
  const listBobcatOfferGroups = useAxiosAlltix<BobcatOfferGroupDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listBobcatOfferGroups({
        url: `/offer-groups`,
        method: 'GET',
        signal,
      });
    },
    [listBobcatOfferGroups],
  );
};

export const getListBobcatOfferGroupsQueryKey = () => {
  return [`/offer-groups`] as const;
};

export const useListBobcatOfferGroupsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListBobcatOfferGroupsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListBobcatOfferGroupsHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListBobcatOfferGroupsQueryKey();

  const listBobcatOfferGroups = useListBobcatOfferGroupsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListBobcatOfferGroupsHook>>>
  > = ({ signal }) => listBobcatOfferGroups(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListBobcatOfferGroupsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListBobcatOfferGroupsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListBobcatOfferGroupsHook>>>
>;
export type ListBobcatOfferGroupsQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useListBobcatOfferGroups<
  TData = Awaited<ReturnType<ReturnType<typeof useListBobcatOfferGroupsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListBobcatOfferGroupsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListBobcatOfferGroupsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListBobcatOfferGroupsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListBobcatOfferGroups<
  TData = Awaited<ReturnType<ReturnType<typeof useListBobcatOfferGroupsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListBobcatOfferGroupsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListBobcatOfferGroupsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListBobcatOfferGroupsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListBobcatOfferGroups<
  TData = Awaited<ReturnType<ReturnType<typeof useListBobcatOfferGroupsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListBobcatOfferGroupsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List Bobcat offers groups
 */

export function useListBobcatOfferGroups<
  TData = Awaited<ReturnType<ReturnType<typeof useListBobcatOfferGroupsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListBobcatOfferGroupsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListBobcatOfferGroupsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * List Bobcat offers
 * @summary List Bobcat offers
 */
export const useListBobcatOffersHook = () => {
  const listBobcatOffers = useAxiosAlltix<BobcatOfferDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listBobcatOffers({ url: `/offers`, method: 'GET', signal });
    },
    [listBobcatOffers],
  );
};

export const getListBobcatOffersQueryKey = () => {
  return [`/offers`] as const;
};

export const useListBobcatOffersQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListBobcatOffersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListBobcatOffersHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListBobcatOffersQueryKey();

  const listBobcatOffers = useListBobcatOffersHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListBobcatOffersHook>>>
  > = ({ signal }) => listBobcatOffers(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListBobcatOffersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListBobcatOffersQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListBobcatOffersHook>>>
>;
export type ListBobcatOffersQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useListBobcatOffers<
  TData = Awaited<ReturnType<ReturnType<typeof useListBobcatOffersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListBobcatOffersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListBobcatOffersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListBobcatOffersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListBobcatOffers<
  TData = Awaited<ReturnType<ReturnType<typeof useListBobcatOffersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListBobcatOffersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListBobcatOffersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListBobcatOffersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListBobcatOffers<
  TData = Awaited<ReturnType<ReturnType<typeof useListBobcatOffersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListBobcatOffersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List Bobcat offers
 */

export function useListBobcatOffers<
  TData = Awaited<ReturnType<ReturnType<typeof useListBobcatOffersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListBobcatOffersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListBobcatOffersQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Lookup Bobcat offers by tags
 * @summary Lookup bobcat offers by tags
 */
export const useLookupBobcatOffersByTagsHook = () => {
  const lookupBobcatOffersByTags = useAxiosAlltix<BobcatShallowOfferDTO[]>();

  return useCallback(
    (params: LookupBobcatOffersByTagsParams, signal?: AbortSignal) => {
      return lookupBobcatOffersByTags({
        url: `/bobcat-offer-lookup`,
        method: 'GET',
        params,
        signal,
      });
    },
    [lookupBobcatOffersByTags],
  );
};

export const getLookupBobcatOffersByTagsQueryKey = (
  params?: LookupBobcatOffersByTagsParams,
) => {
  return [`/bobcat-offer-lookup`, ...(params ? [params] : [])] as const;
};

export const useLookupBobcatOffersByTagsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useLookupBobcatOffersByTagsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: LookupBobcatOffersByTagsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useLookupBobcatOffersByTagsHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getLookupBobcatOffersByTagsQueryKey(params);

  const lookupBobcatOffersByTags = useLookupBobcatOffersByTagsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useLookupBobcatOffersByTagsHook>>>
  > = ({ signal }) => lookupBobcatOffersByTags(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useLookupBobcatOffersByTagsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type LookupBobcatOffersByTagsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useLookupBobcatOffersByTagsHook>>>
>;
export type LookupBobcatOffersByTagsQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useLookupBobcatOffersByTags<
  TData = Awaited<
    ReturnType<ReturnType<typeof useLookupBobcatOffersByTagsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: LookupBobcatOffersByTagsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useLookupBobcatOffersByTagsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useLookupBobcatOffersByTagsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useLookupBobcatOffersByTagsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useLookupBobcatOffersByTags<
  TData = Awaited<
    ReturnType<ReturnType<typeof useLookupBobcatOffersByTagsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: LookupBobcatOffersByTagsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useLookupBobcatOffersByTagsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useLookupBobcatOffersByTagsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useLookupBobcatOffersByTagsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useLookupBobcatOffersByTags<
  TData = Awaited<
    ReturnType<ReturnType<typeof useLookupBobcatOffersByTagsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: LookupBobcatOffersByTagsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useLookupBobcatOffersByTagsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Lookup bobcat offers by tags
 */

export function useLookupBobcatOffersByTags<
  TData = Awaited<
    ReturnType<ReturnType<typeof useLookupBobcatOffersByTagsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: LookupBobcatOffersByTagsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useLookupBobcatOffersByTagsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useLookupBobcatOffersByTagsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * List bearers
 * @summary List bearers
 */
export const useListBearersHook = () => {
  const listBearers = useAxiosAlltix<TravellerBearerDTO[]>();

  return useCallback(
    (params?: ListBearersParams, signal?: AbortSignal) => {
      return listBearers({ url: `/bearers`, method: 'GET', params, signal });
    },
    [listBearers],
  );
};

export const getListBearersQueryKey = (params?: ListBearersParams) => {
  return [`/bearers`, ...(params ? [params] : [])] as const;
};

export const useListBearersQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListBearersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: ListBearersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListBearersHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListBearersQueryKey(params);

  const listBearers = useListBearersHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListBearersHook>>>
  > = ({ signal }) => listBearers(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListBearersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListBearersQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListBearersHook>>>
>;
export type ListBearersQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useListBearers<
  TData = Awaited<ReturnType<ReturnType<typeof useListBearersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: undefined | ListBearersParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListBearersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListBearersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListBearersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListBearers<
  TData = Awaited<ReturnType<ReturnType<typeof useListBearersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: ListBearersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListBearersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListBearersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListBearersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListBearers<
  TData = Awaited<ReturnType<ReturnType<typeof useListBearersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: ListBearersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListBearersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List bearers
 */

export function useListBearers<
  TData = Awaited<ReturnType<ReturnType<typeof useListBearersHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: ListBearersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListBearersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListBearersQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Create a group ticket producset, using an existing productset as a template

 * @summary Create a group ticket productset
 */
export const useCreateGroupTicketProductHook = () => {
  const createGroupTicketProduct = useAxiosAlltix<GroupTicketProductSetDTO>();

  return useCallback(
    (
      groupTicketProductSetCreateDTO: GroupTicketProductSetCreateDTO,
      signal?: AbortSignal,
    ) => {
      return createGroupTicketProduct({
        url: `/products/create-group-ticket`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: groupTicketProductSetCreateDTO,
        signal,
      });
    },
    [createGroupTicketProduct],
  );
};

export const useCreateGroupTicketProductMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateGroupTicketProductHook>>>,
    TError,
    { data: GroupTicketProductSetCreateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateGroupTicketProductHook>>>,
  TError,
  { data: GroupTicketProductSetCreateDTO },
  TContext
> => {
  const mutationKey = ['createGroupTicketProduct'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createGroupTicketProduct = useCreateGroupTicketProductHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateGroupTicketProductHook>>>,
    { data: GroupTicketProductSetCreateDTO }
  > = (props) => {
    const { data } = props ?? {};

    return createGroupTicketProduct(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateGroupTicketProductMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateGroupTicketProductHook>>>
>;
export type CreateGroupTicketProductMutationBody =
  GroupTicketProductSetCreateDTO;
export type CreateGroupTicketProductMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | InternalServerErrorResponse
>;

/**
 * @summary Create a group ticket productset
 */
export const useCreateGroupTicketProduct = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateGroupTicketProductHook>>>,
      TError,
      { data: GroupTicketProductSetCreateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateGroupTicketProductHook>>>,
  TError,
  { data: GroupTicketProductSetCreateDTO },
  TContext
> => {
  const mutationOptions = useCreateGroupTicketProductMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Purchase a product set for one or more collection members
 * @summary Purchase a product set for one or more collection members
 */
export const usePurchaseProductSetForCollectionMembersHook = () => {
  const purchaseProductSetForCollectionMembers =
    useAxiosAlltix<PurchaseResultDTO>();

  return useCallback(
    (
      productPurchaseForCollectionMembersDTO: ProductPurchaseForCollectionMembersDTO,
      signal?: AbortSignal,
    ) => {
      return purchaseProductSetForCollectionMembers({
        url: `/products/purchase-for-collection-members`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: productPurchaseForCollectionMembersDTO,
        signal,
      });
    },
    [purchaseProductSetForCollectionMembers],
  );
};

export const usePurchaseProductSetForCollectionMembersMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<
        ReturnType<typeof usePurchaseProductSetForCollectionMembersHook>
      >
    >,
    TError,
    { data: ProductPurchaseForCollectionMembersDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<
    ReturnType<ReturnType<typeof usePurchaseProductSetForCollectionMembersHook>>
  >,
  TError,
  { data: ProductPurchaseForCollectionMembersDTO },
  TContext
> => {
  const mutationKey = ['purchaseProductSetForCollectionMembers'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const purchaseProductSetForCollectionMembers =
    usePurchaseProductSetForCollectionMembersHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<
        ReturnType<typeof usePurchaseProductSetForCollectionMembersHook>
      >
    >,
    { data: ProductPurchaseForCollectionMembersDTO }
  > = (props) => {
    const { data } = props ?? {};

    return purchaseProductSetForCollectionMembers(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PurchaseProductSetForCollectionMembersMutationResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof usePurchaseProductSetForCollectionMembersHook>>
  >
>;
export type PurchaseProductSetForCollectionMembersMutationBody =
  ProductPurchaseForCollectionMembersDTO;
export type PurchaseProductSetForCollectionMembersMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | InternalServerErrorResponse
>;

/**
 * @summary Purchase a product set for one or more collection members
 */
export const usePurchaseProductSetForCollectionMembers = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          ReturnType<typeof usePurchaseProductSetForCollectionMembersHook>
        >
      >,
      TError,
      { data: ProductPurchaseForCollectionMembersDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<ReturnType<typeof usePurchaseProductSetForCollectionMembersHook>>
  >,
  TError,
  { data: ProductPurchaseForCollectionMembersDTO },
  TContext
> => {
  const mutationOptions =
    usePurchaseProductSetForCollectionMembersMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Purchase a product set for one or more external recipients
 * @summary Purchase a product set for one or more external recipients
 */
export const usePurchaseProductSetForExternalRecipientsHook = () => {
  const purchaseProductSetForExternalRecipients =
    useAxiosAlltix<PurchaseResultDTO>();

  return useCallback(
    (
      productPurchaseForExternalRecipientsDTO: ProductPurchaseForExternalRecipientsDTO,
      signal?: AbortSignal,
    ) => {
      return purchaseProductSetForExternalRecipients({
        url: `/products/purchase-for-external-recipients`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: productPurchaseForExternalRecipientsDTO,
        signal,
      });
    },
    [purchaseProductSetForExternalRecipients],
  );
};

export const usePurchaseProductSetForExternalRecipientsMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<
        ReturnType<typeof usePurchaseProductSetForExternalRecipientsHook>
      >
    >,
    TError,
    { data: ProductPurchaseForExternalRecipientsDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<
    ReturnType<
      ReturnType<typeof usePurchaseProductSetForExternalRecipientsHook>
    >
  >,
  TError,
  { data: ProductPurchaseForExternalRecipientsDTO },
  TContext
> => {
  const mutationKey = ['purchaseProductSetForExternalRecipients'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const purchaseProductSetForExternalRecipients =
    usePurchaseProductSetForExternalRecipientsHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<
        ReturnType<typeof usePurchaseProductSetForExternalRecipientsHook>
      >
    >,
    { data: ProductPurchaseForExternalRecipientsDTO }
  > = (props) => {
    const { data } = props ?? {};

    return purchaseProductSetForExternalRecipients(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PurchaseProductSetForExternalRecipientsMutationResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof usePurchaseProductSetForExternalRecipientsHook>
    >
  >
>;
export type PurchaseProductSetForExternalRecipientsMutationBody =
  ProductPurchaseForExternalRecipientsDTO;
export type PurchaseProductSetForExternalRecipientsMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | InternalServerErrorResponse
>;

/**
 * @summary Purchase a product set for one or more external recipients
 */
export const usePurchaseProductSetForExternalRecipients = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          ReturnType<typeof usePurchaseProductSetForExternalRecipientsHook>
        >
      >,
      TError,
      { data: ProductPurchaseForExternalRecipientsDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<
      ReturnType<typeof usePurchaseProductSetForExternalRecipientsHook>
    >
  >,
  TError,
  { data: ProductPurchaseForExternalRecipientsDTO },
  TContext
> => {
  const mutationOptions =
    usePurchaseProductSetForExternalRecipientsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Submit an organization application
 */
export const useSubmitOrganizationApplicationHook = () => {
  const submitOrganizationApplication = useAxiosAlltix<void>();

  return useCallback(
    (
      organizationApplicationCreateDTO: OrganizationApplicationCreateDTO,
      signal?: AbortSignal,
    ) => {
      return submitOrganizationApplication({
        url: `/public/organization-applications`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: organizationApplicationCreateDTO,
        signal,
      });
    },
    [submitOrganizationApplication],
  );
};

export const useSubmitOrganizationApplicationMutationOptions = <
  TError = ErrorType<BadRequestResponse | NotImplementedResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useSubmitOrganizationApplicationHook>>
    >,
    TError,
    { data: OrganizationApplicationCreateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useSubmitOrganizationApplicationHook>>>,
  TError,
  { data: OrganizationApplicationCreateDTO },
  TContext
> => {
  const mutationKey = ['submitOrganizationApplication'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const submitOrganizationApplication = useSubmitOrganizationApplicationHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useSubmitOrganizationApplicationHook>>
    >,
    { data: OrganizationApplicationCreateDTO }
  > = (props) => {
    const { data } = props ?? {};

    return submitOrganizationApplication(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SubmitOrganizationApplicationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSubmitOrganizationApplicationHook>>>
>;
export type SubmitOrganizationApplicationMutationBody =
  OrganizationApplicationCreateDTO;
export type SubmitOrganizationApplicationMutationError = ErrorType<
  BadRequestResponse | NotImplementedResponse
>;

/**
 * @summary Submit an organization application
 */
export const useSubmitOrganizationApplication = <
  TError = ErrorType<BadRequestResponse | NotImplementedResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useSubmitOrganizationApplicationHook>>
      >,
      TError,
      { data: OrganizationApplicationCreateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useSubmitOrganizationApplicationHook>>>,
  TError,
  { data: OrganizationApplicationCreateDTO },
  TContext
> => {
  const mutationOptions =
    useSubmitOrganizationApplicationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * List all tenants
 * @summary List tenants
 */
export const useListTenantsHook = () => {
  const listTenants = useAxiosAlltix<TenantPublicDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listTenants({ url: `/public/tenants`, method: 'GET', signal });
    },
    [listTenants],
  );
};

export const getListTenantsQueryKey = () => {
  return [`/public/tenants`] as const;
};

export const useListTenantsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
  TError = ErrorType<InternalServerErrorResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListTenantsQueryKey();

  const listTenants = useListTenantsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>
  > = ({ signal }) => listTenants(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListTenantsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>
>;
export type ListTenantsQueryError = ErrorType<InternalServerErrorResponse>;

export function useListTenants<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
  TError = ErrorType<InternalServerErrorResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListTenants<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
  TError = ErrorType<InternalServerErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListTenants<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
  TError = ErrorType<InternalServerErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List tenants
 */

export function useListTenants<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
  TError = ErrorType<InternalServerErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListTenantsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get a list of etis journeys
 * @summary Get etis journeys
 */
export const useGetEtisJourneysHook = () => {
  const getEtisJourneys = useAxiosAlltix<EtisJourneyResultDTO>();

  return useCallback(
    (params: GetEtisJourneysParams, signal?: AbortSignal) => {
      return getEtisJourneys({
        url: `/planner/journeys`,
        method: 'GET',
        params,
        signal,
      });
    },
    [getEtisJourneys],
  );
};

export const getGetEtisJourneysQueryKey = (params?: GetEtisJourneysParams) => {
  return [`/planner/journeys`, ...(params ? [params] : [])] as const;
};

export const useGetEtisJourneysQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetEtisJourneysHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params: GetEtisJourneysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetEtisJourneysHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetEtisJourneysQueryKey(params);

  const getEtisJourneys = useGetEtisJourneysHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetEtisJourneysHook>>>
  > = ({ signal }) => getEtisJourneys(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetEtisJourneysHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetEtisJourneysQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetEtisJourneysHook>>>
>;
export type GetEtisJourneysQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
>;

export function useGetEtisJourneys<
  TData = Awaited<ReturnType<ReturnType<typeof useGetEtisJourneysHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params: GetEtisJourneysParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetEtisJourneysHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetEtisJourneysHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetEtisJourneysHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetEtisJourneys<
  TData = Awaited<ReturnType<ReturnType<typeof useGetEtisJourneysHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params: GetEtisJourneysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetEtisJourneysHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetEtisJourneysHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetEtisJourneysHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetEtisJourneys<
  TData = Awaited<ReturnType<ReturnType<typeof useGetEtisJourneysHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params: GetEtisJourneysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetEtisJourneysHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get etis journeys
 */

export function useGetEtisJourneys<
  TData = Awaited<ReturnType<ReturnType<typeof useGetEtisJourneysHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params: GetEtisJourneysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetEtisJourneysHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetEtisJourneysQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get a list of etis stops
 * @summary Get etis stops
 */
export const useGetEtisStopsHook = () => {
  const getEtisStops = useAxiosAlltix<EtisStopDTO[]>();

  return useCallback(
    (params: GetEtisStopsParams, signal?: AbortSignal) => {
      return getEtisStops({
        url: `/planner/stops`,
        method: 'GET',
        params,
        signal,
      });
    },
    [getEtisStops],
  );
};

export const getGetEtisStopsQueryKey = (params?: GetEtisStopsParams) => {
  return [`/planner/stops`, ...(params ? [params] : [])] as const;
};

export const useGetEtisStopsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetEtisStopsHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params: GetEtisStopsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetEtisStopsHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetEtisStopsQueryKey(params);

  const getEtisStops = useGetEtisStopsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetEtisStopsHook>>>
  > = ({ signal }) => getEtisStops(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetEtisStopsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetEtisStopsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetEtisStopsHook>>>
>;
export type GetEtisStopsQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
>;

export function useGetEtisStops<
  TData = Awaited<ReturnType<ReturnType<typeof useGetEtisStopsHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params: GetEtisStopsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetEtisStopsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetEtisStopsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetEtisStopsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetEtisStops<
  TData = Awaited<ReturnType<ReturnType<typeof useGetEtisStopsHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params: GetEtisStopsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetEtisStopsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetEtisStopsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetEtisStopsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetEtisStops<
  TData = Awaited<ReturnType<ReturnType<typeof useGetEtisStopsHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params: GetEtisStopsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetEtisStopsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get etis stops
 */

export function useGetEtisStops<
  TData = Awaited<ReturnType<ReturnType<typeof useGetEtisStopsHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params: GetEtisStopsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetEtisStopsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetEtisStopsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get a list of bobcat product sets for the etis journey
 * @summary Get bobcat product sets
 */
export const useGetBobcatProductSetsHook = () => {
  const getBobcatProductSets = useAxiosAlltix<BobcatProductSetDTO[]>();

  return useCallback(
    (params: GetBobcatProductSetsParams, signal?: AbortSignal) => {
      return getBobcatProductSets({
        url: `/planner/tickets`,
        method: 'GET',
        params,
        signal,
      });
    },
    [getBobcatProductSets],
  );
};

export const getGetBobcatProductSetsQueryKey = (
  params?: GetBobcatProductSetsParams,
) => {
  return [`/planner/tickets`, ...(params ? [params] : [])] as const;
};

export const useGetBobcatProductSetsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetBobcatProductSetsHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params: GetBobcatProductSetsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetBobcatProductSetsHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBobcatProductSetsQueryKey(params);

  const getBobcatProductSets = useGetBobcatProductSetsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetBobcatProductSetsHook>>>
  > = ({ signal }) => getBobcatProductSets(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetBobcatProductSetsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetBobcatProductSetsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetBobcatProductSetsHook>>>
>;
export type GetBobcatProductSetsQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
>;

export function useGetBobcatProductSets<
  TData = Awaited<ReturnType<ReturnType<typeof useGetBobcatProductSetsHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params: GetBobcatProductSetsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetBobcatProductSetsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetBobcatProductSetsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetBobcatProductSetsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetBobcatProductSets<
  TData = Awaited<ReturnType<ReturnType<typeof useGetBobcatProductSetsHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params: GetBobcatProductSetsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetBobcatProductSetsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetBobcatProductSetsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetBobcatProductSetsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetBobcatProductSets<
  TData = Awaited<ReturnType<ReturnType<typeof useGetBobcatProductSetsHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params: GetBobcatProductSetsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetBobcatProductSetsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get bobcat product sets
 */

export function useGetBobcatProductSets<
  TData = Awaited<ReturnType<ReturnType<typeof useGetBobcatProductSetsHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params: GetBobcatProductSetsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetBobcatProductSetsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetBobcatProductSetsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Get issued tickets for transactionID
 */
export const useGetIssuedTicketsWithTravellerByTransactionIDHook = () => {
  const getIssuedTicketsWithTravellerByTransactionID =
    useAxiosAlltix<IssuedTicketWithTravellerDTO[]>();

  return useCallback(
    (
      params: GetIssuedTicketsWithTravellerByTransactionIDParams,
      signal?: AbortSignal,
    ) => {
      return getIssuedTicketsWithTravellerByTransactionID({
        url: `/issued-tickets`,
        method: 'GET',
        params,
        signal,
      });
    },
    [getIssuedTicketsWithTravellerByTransactionID],
  );
};

export const getGetIssuedTicketsWithTravellerByTransactionIDQueryKey = (
  params?: GetIssuedTicketsWithTravellerByTransactionIDParams,
) => {
  return [`/issued-tickets`, ...(params ? [params] : [])] as const;
};

export const useGetIssuedTicketsWithTravellerByTransactionIDQueryOptions = <
  TData = Awaited<
    ReturnType<
      ReturnType<typeof useGetIssuedTicketsWithTravellerByTransactionIDHook>
    >
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: GetIssuedTicketsWithTravellerByTransactionIDParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<
              typeof useGetIssuedTicketsWithTravellerByTransactionIDHook
            >
          >
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetIssuedTicketsWithTravellerByTransactionIDQueryKey(params);

  const getIssuedTicketsWithTravellerByTransactionID =
    useGetIssuedTicketsWithTravellerByTransactionIDHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<
        ReturnType<typeof useGetIssuedTicketsWithTravellerByTransactionIDHook>
      >
    >
  > = ({ signal }) =>
    getIssuedTicketsWithTravellerByTransactionID(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<
      ReturnType<
        ReturnType<typeof useGetIssuedTicketsWithTravellerByTransactionIDHook>
      >
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetIssuedTicketsWithTravellerByTransactionIDQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        ReturnType<typeof useGetIssuedTicketsWithTravellerByTransactionIDHook>
      >
    >
  >;
export type GetIssuedTicketsWithTravellerByTransactionIDQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetIssuedTicketsWithTravellerByTransactionID<
  TData = Awaited<
    ReturnType<
      ReturnType<typeof useGetIssuedTicketsWithTravellerByTransactionIDHook>
    >
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: GetIssuedTicketsWithTravellerByTransactionIDParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<
              typeof useGetIssuedTicketsWithTravellerByTransactionIDHook
            >
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<
                typeof useGetIssuedTicketsWithTravellerByTransactionIDHook
              >
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<
                typeof useGetIssuedTicketsWithTravellerByTransactionIDHook
              >
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetIssuedTicketsWithTravellerByTransactionID<
  TData = Awaited<
    ReturnType<
      ReturnType<typeof useGetIssuedTicketsWithTravellerByTransactionIDHook>
    >
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: GetIssuedTicketsWithTravellerByTransactionIDParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<
              typeof useGetIssuedTicketsWithTravellerByTransactionIDHook
            >
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<
                typeof useGetIssuedTicketsWithTravellerByTransactionIDHook
              >
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<
                typeof useGetIssuedTicketsWithTravellerByTransactionIDHook
              >
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetIssuedTicketsWithTravellerByTransactionID<
  TData = Awaited<
    ReturnType<
      ReturnType<typeof useGetIssuedTicketsWithTravellerByTransactionIDHook>
    >
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: GetIssuedTicketsWithTravellerByTransactionIDParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<
              typeof useGetIssuedTicketsWithTravellerByTransactionIDHook
            >
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get issued tickets for transactionID
 */

export function useGetIssuedTicketsWithTravellerByTransactionID<
  TData = Awaited<
    ReturnType<
      ReturnType<typeof useGetIssuedTicketsWithTravellerByTransactionIDHook>
    >
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: GetIssuedTicketsWithTravellerByTransactionIDParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<
              typeof useGetIssuedTicketsWithTravellerByTransactionIDHook
            >
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions =
    useGetIssuedTicketsWithTravellerByTransactionIDQueryOptions(
      params,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get the tickets for a traveller
 * @summary Get tickets for traveller
 */
export const useGetTicketsForTravellerHook = () => {
  const getTicketsForTraveller = useAxiosAlltix<IssuedTicketFindResultDTO[]>();

  return useCallback(
    (travellerID: string, signal?: AbortSignal) => {
      return getTicketsForTraveller({
        url: `/issued-tickets/traveller/${travellerID}`,
        method: 'GET',
        signal,
      });
    },
    [getTicketsForTraveller],
  );
};

export const getGetTicketsForTravellerQueryKey = (travellerID?: string) => {
  return [`/issued-tickets/traveller/${travellerID}`] as const;
};

export const useGetTicketsForTravellerQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetTicketsForTravellerHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTicketsForTravellerHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTicketsForTravellerQueryKey(travellerID);

  const getTicketsForTraveller = useGetTicketsForTravellerHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetTicketsForTravellerHook>>>
  > = ({ signal }) => getTicketsForTraveller(travellerID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!travellerID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTicketsForTravellerHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetTicketsForTravellerQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTicketsForTravellerHook>>>
>;
export type GetTicketsForTravellerQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetTicketsForTraveller<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTicketsForTravellerHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  travellerID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTicketsForTravellerHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTicketsForTravellerHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTicketsForTravellerHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetTicketsForTraveller<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTicketsForTravellerHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTicketsForTravellerHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTicketsForTravellerHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTicketsForTravellerHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetTicketsForTraveller<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTicketsForTravellerHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTicketsForTravellerHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get tickets for traveller
 */

export function useGetTicketsForTraveller<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTicketsForTravellerHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTicketsForTravellerHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetTicketsForTravellerQueryOptions(
    travellerID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get tickets for user organization
 * @summary Get tickets for user organization
 */
export const useGetTicketsForUserOrganizationHook = () => {
  const getTicketsForUserOrganization =
    useAxiosAlltix<IssuedTicketFindResultDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getTicketsForUserOrganization({
        url: `/issued-tickets/user-organization`,
        method: 'GET',
        signal,
      });
    },
    [getTicketsForUserOrganization],
  );
};

export const getGetTicketsForUserOrganizationQueryKey = () => {
  return [`/issued-tickets/user-organization`] as const;
};

export const useGetTicketsForUserOrganizationQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTicketsForUserOrganizationHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<
        ReturnType<ReturnType<typeof useGetTicketsForUserOrganizationHook>>
      >,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTicketsForUserOrganizationQueryKey();

  const getTicketsForUserOrganization = useGetTicketsForUserOrganizationHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetTicketsForUserOrganizationHook>>>
  > = ({ signal }) => getTicketsForUserOrganization(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<
      ReturnType<ReturnType<typeof useGetTicketsForUserOrganizationHook>>
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetTicketsForUserOrganizationQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTicketsForUserOrganizationHook>>>
>;
export type GetTicketsForUserOrganizationQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetTicketsForUserOrganization<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTicketsForUserOrganizationHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTicketsForUserOrganizationHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetTicketsForUserOrganizationHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetTicketsForUserOrganizationHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetTicketsForUserOrganization<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTicketsForUserOrganizationHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTicketsForUserOrganizationHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetTicketsForUserOrganizationHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetTicketsForUserOrganizationHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetTicketsForUserOrganization<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTicketsForUserOrganizationHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTicketsForUserOrganizationHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get tickets for user organization
 */

export function useGetTicketsForUserOrganization<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTicketsForUserOrganizationHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTicketsForUserOrganizationHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetTicketsForUserOrganizationQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Revoke an issued ticket
 * @summary Revoke ticket
 */
export const useRevokeIssuedTicketHook = () => {
  const revokeIssuedTicket = useAxiosAlltix<IssuedTicketDTO>();

  return useCallback(
    (id: string, issuedTicketRevokeDTO?: IssuedTicketRevokeDTO) => {
      return revokeIssuedTicket({
        url: `/issued-tickets/${id}`,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        data: issuedTicketRevokeDTO,
      });
    },
    [revokeIssuedTicket],
  );
};

export const useRevokeIssuedTicketMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useRevokeIssuedTicketHook>>>,
    TError,
    { id: string; data: IssuedTicketRevokeDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useRevokeIssuedTicketHook>>>,
  TError,
  { id: string; data: IssuedTicketRevokeDTO },
  TContext
> => {
  const mutationKey = ['revokeIssuedTicket'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const revokeIssuedTicket = useRevokeIssuedTicketHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useRevokeIssuedTicketHook>>>,
    { id: string; data: IssuedTicketRevokeDTO }
  > = (props) => {
    const { id, data } = props ?? {};

    return revokeIssuedTicket(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeIssuedTicketMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useRevokeIssuedTicketHook>>>
>;
export type RevokeIssuedTicketMutationBody = IssuedTicketRevokeDTO;
export type RevokeIssuedTicketMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Revoke ticket
 */
export const useRevokeIssuedTicket = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useRevokeIssuedTicketHook>>>,
      TError,
      { id: string; data: IssuedTicketRevokeDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useRevokeIssuedTicketHook>>>,
  TError,
  { id: string; data: IssuedTicketRevokeDTO },
  TContext
> => {
  const mutationOptions = useRevokeIssuedTicketMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Get bearers for issued ticket
 */
export const useGetBearersForIssuedTicketHook = () => {
  const getBearersForIssuedTicket = useAxiosAlltix<IssuedTicketBearerDTO[]>();

  return useCallback(
    (ticketID: string, signal?: AbortSignal) => {
      return getBearersForIssuedTicket({
        url: `/issued-tickets/${ticketID}/bearers`,
        method: 'GET',
        signal,
      });
    },
    [getBearersForIssuedTicket],
  );
};

export const getGetBearersForIssuedTicketQueryKey = (ticketID?: string) => {
  return [`/issued-tickets/${ticketID}/bearers`] as const;
};

export const useGetBearersForIssuedTicketQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetBearersForIssuedTicketHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  ticketID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetBearersForIssuedTicketHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBearersForIssuedTicketQueryKey(ticketID);

  const getBearersForIssuedTicket = useGetBearersForIssuedTicketHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetBearersForIssuedTicketHook>>>
  > = ({ signal }) => getBearersForIssuedTicket(ticketID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!ticketID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetBearersForIssuedTicketHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetBearersForIssuedTicketQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetBearersForIssuedTicketHook>>>
>;
export type GetBearersForIssuedTicketQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetBearersForIssuedTicket<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetBearersForIssuedTicketHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  ticketID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetBearersForIssuedTicketHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetBearersForIssuedTicketHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetBearersForIssuedTicketHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetBearersForIssuedTicket<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetBearersForIssuedTicketHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  ticketID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetBearersForIssuedTicketHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetBearersForIssuedTicketHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetBearersForIssuedTicketHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetBearersForIssuedTicket<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetBearersForIssuedTicketHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  ticketID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetBearersForIssuedTicketHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get bearers for issued ticket
 */

export function useGetBearersForIssuedTicket<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetBearersForIssuedTicketHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  ticketID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetBearersForIssuedTicketHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetBearersForIssuedTicketQueryOptions(
    ticketID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Update bearer for issued ticket
 */
export const useUpdateBearerForIssuedTicketHook = () => {
  const updateBearerForIssuedTicket = useAxiosAlltix<void>();

  return useCallback(
    (
      ticketID: string,
      issuedTicketBearerUpdateDTO: IssuedTicketBearerUpdateDTO,
    ) => {
      return updateBearerForIssuedTicket({
        url: `/issued-tickets/${ticketID}/bearers`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: issuedTicketBearerUpdateDTO,
      });
    },
    [updateBearerForIssuedTicket],
  );
};

export const useUpdateBearerForIssuedTicketMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateBearerForIssuedTicketHook>>>,
    TError,
    { ticketID: string; data: IssuedTicketBearerUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateBearerForIssuedTicketHook>>>,
  TError,
  { ticketID: string; data: IssuedTicketBearerUpdateDTO },
  TContext
> => {
  const mutationKey = ['updateBearerForIssuedTicket'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateBearerForIssuedTicket = useUpdateBearerForIssuedTicketHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateBearerForIssuedTicketHook>>>,
    { ticketID: string; data: IssuedTicketBearerUpdateDTO }
  > = (props) => {
    const { ticketID, data } = props ?? {};

    return updateBearerForIssuedTicket(ticketID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateBearerForIssuedTicketMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateBearerForIssuedTicketHook>>>
>;
export type UpdateBearerForIssuedTicketMutationBody =
  IssuedTicketBearerUpdateDTO;
export type UpdateBearerForIssuedTicketMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Update bearer for issued ticket
 */
export const useUpdateBearerForIssuedTicket = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useUpdateBearerForIssuedTicketHook>>
      >,
      TError,
      { ticketID: string; data: IssuedTicketBearerUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateBearerForIssuedTicketHook>>>,
  TError,
  { ticketID: string; data: IssuedTicketBearerUpdateDTO },
  TContext
> => {
  const mutationOptions =
    useUpdateBearerForIssuedTicketMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get all transactions (purchases, refunds, transfers) that reference the issued ticket
 * @summary Get transactions for issued ticket
 */
export const useGetTransactionsForIssuedTicketHook = () => {
  const getTransactionsForIssuedTicket = useAxiosAlltix<TransactionDTO[]>();

  return useCallback(
    (ticketID: string, signal?: AbortSignal) => {
      return getTransactionsForIssuedTicket({
        url: `/issued-tickets/${ticketID}/transactions`,
        method: 'GET',
        signal,
      });
    },
    [getTransactionsForIssuedTicket],
  );
};

export const getGetTransactionsForIssuedTicketQueryKey = (
  ticketID?: string,
) => {
  return [`/issued-tickets/${ticketID}/transactions`] as const;
};

export const useGetTransactionsForIssuedTicketQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTransactionsForIssuedTicketHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  ticketID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTransactionsForIssuedTicketHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetTransactionsForIssuedTicketQueryKey(ticketID);

  const getTransactionsForIssuedTicket =
    useGetTransactionsForIssuedTicketHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<ReturnType<typeof useGetTransactionsForIssuedTicketHook>>
    >
  > = ({ signal }) => getTransactionsForIssuedTicket(ticketID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!ticketID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<
      ReturnType<ReturnType<typeof useGetTransactionsForIssuedTicketHook>>
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetTransactionsForIssuedTicketQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTransactionsForIssuedTicketHook>>>
>;
export type GetTransactionsForIssuedTicketQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

export function useGetTransactionsForIssuedTicket<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTransactionsForIssuedTicketHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  ticketID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTransactionsForIssuedTicketHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetTransactionsForIssuedTicketHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetTransactionsForIssuedTicketHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetTransactionsForIssuedTicket<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTransactionsForIssuedTicketHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  ticketID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTransactionsForIssuedTicketHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetTransactionsForIssuedTicketHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetTransactionsForIssuedTicketHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetTransactionsForIssuedTicket<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTransactionsForIssuedTicketHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  ticketID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTransactionsForIssuedTicketHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get transactions for issued ticket
 */

export function useGetTransactionsForIssuedTicket<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTransactionsForIssuedTicketHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  ticketID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTransactionsForIssuedTicketHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetTransactionsForIssuedTicketQueryOptions(
    ticketID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Lend issued ticket to a different bearer
 */
export const useLendIssuedTicketHook = () => {
  const lendIssuedTicket = useAxiosAlltix<void>();

  return useCallback(
    (ticketID: string, issuedTicketLendDTO: IssuedTicketLendDTO) => {
      return lendIssuedTicket({
        url: `/issued-tickets/${ticketID}/lend`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: issuedTicketLendDTO,
      });
    },
    [lendIssuedTicket],
  );
};

export const useLendIssuedTicketMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useLendIssuedTicketHook>>>,
    TError,
    { ticketID: string; data: IssuedTicketLendDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useLendIssuedTicketHook>>>,
  TError,
  { ticketID: string; data: IssuedTicketLendDTO },
  TContext
> => {
  const mutationKey = ['lendIssuedTicket'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const lendIssuedTicket = useLendIssuedTicketHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useLendIssuedTicketHook>>>,
    { ticketID: string; data: IssuedTicketLendDTO }
  > = (props) => {
    const { ticketID, data } = props ?? {};

    return lendIssuedTicket(ticketID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type LendIssuedTicketMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useLendIssuedTicketHook>>>
>;
export type LendIssuedTicketMutationBody = IssuedTicketLendDTO;
export type LendIssuedTicketMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Lend issued ticket to a different bearer
 */
export const useLendIssuedTicket = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useLendIssuedTicketHook>>>,
      TError,
      { ticketID: string; data: IssuedTicketLendDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useLendIssuedTicketHook>>>,
  TError,
  { ticketID: string; data: IssuedTicketLendDTO },
  TContext
> => {
  const mutationOptions = useLendIssuedTicketMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Fetches the current data from Bobcat Sales for the requested issued tickets and
synchronizes any state changes (active/inactive and final states) back to the local
database. expires_at is the stored value and is never synchronized. Returns the current
Bobcat-derived data for every requested ticket: the current activation window, activation
counts and validity bounds. A maximum of 50 ids may be requested per call.

 * @summary Get Bobcat Sales data for issued tickets
 */
export const useGetBobcatDataForTicketsHook = () => {
  const getBobcatDataForTickets =
    useAxiosAlltix<IssuedTicketBobcatDataMapDTO>();

  return useCallback(
    (params: GetBobcatDataForTicketsParams, signal?: AbortSignal) => {
      return getBobcatDataForTickets({
        url: `/issued-tickets/bobcat-data`,
        method: 'GET',
        params,
        signal,
      });
    },
    [getBobcatDataForTickets],
  );
};

export const getGetBobcatDataForTicketsQueryKey = (
  params?: GetBobcatDataForTicketsParams,
) => {
  return [`/issued-tickets/bobcat-data`, ...(params ? [params] : [])] as const;
};

export const useGetBobcatDataForTicketsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetBobcatDataForTicketsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: GetBobcatDataForTicketsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetBobcatDataForTicketsHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBobcatDataForTicketsQueryKey(params);

  const getBobcatDataForTickets = useGetBobcatDataForTicketsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetBobcatDataForTicketsHook>>>
  > = ({ signal }) => getBobcatDataForTickets(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetBobcatDataForTicketsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetBobcatDataForTicketsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetBobcatDataForTicketsHook>>>
>;
export type GetBobcatDataForTicketsQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetBobcatDataForTickets<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetBobcatDataForTicketsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: GetBobcatDataForTicketsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetBobcatDataForTicketsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetBobcatDataForTicketsHook>>
          >,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetBobcatDataForTicketsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetBobcatDataForTickets<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetBobcatDataForTicketsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: GetBobcatDataForTicketsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetBobcatDataForTicketsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetBobcatDataForTicketsHook>>
          >,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetBobcatDataForTicketsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetBobcatDataForTickets<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetBobcatDataForTicketsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: GetBobcatDataForTicketsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetBobcatDataForTicketsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get Bobcat Sales data for issued tickets
 */

export function useGetBobcatDataForTickets<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetBobcatDataForTicketsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: GetBobcatDataForTicketsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetBobcatDataForTicketsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetBobcatDataForTicketsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * List files for the current user
 * @summary List files for user
 */
export const useGetFilesListHook = () => {
  const getFilesList = useAxiosAlltix<FileDTO[]>();

  return useCallback(
    (params?: GetFilesListParams, signal?: AbortSignal) => {
      return getFilesList({ url: `/files`, method: 'GET', params, signal });
    },
    [getFilesList],
  );
};

export const getGetFilesListQueryKey = (params?: GetFilesListParams) => {
  return [`/files`, ...(params ? [params] : [])] as const;
};

export const useGetFilesListQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetFilesListHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: GetFilesListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetFilesListHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetFilesListQueryKey(params);

  const getFilesList = useGetFilesListHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetFilesListHook>>>
  > = ({ signal }) => getFilesList(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetFilesListHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetFilesListQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetFilesListHook>>>
>;
export type GetFilesListQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetFilesList<
  TData = Awaited<ReturnType<ReturnType<typeof useGetFilesListHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: undefined | GetFilesListParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetFilesListHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetFilesListHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetFilesListHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetFilesList<
  TData = Awaited<ReturnType<ReturnType<typeof useGetFilesListHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: GetFilesListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetFilesListHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetFilesListHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetFilesListHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetFilesList<
  TData = Awaited<ReturnType<ReturnType<typeof useGetFilesListHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: GetFilesListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetFilesListHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List files for user
 */

export function useGetFilesList<
  TData = Awaited<ReturnType<ReturnType<typeof useGetFilesListHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: GetFilesListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetFilesListHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetFilesListQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Upload files for an organization or department
 * @summary Upload files
 */
export const useUploadFilesHook = () => {
  const uploadFiles = useAxiosAlltix<FileDTO[]>();

  return useCallback(
    (fileUploadFormDataDTO: FileUploadFormDataDTO, signal?: AbortSignal) => {
      const formData = new FormData();
      fileUploadFormDataDTO.files.forEach((value) =>
        formData.append(`files`, value),
      );
      formData.append(`metadata`, fileUploadFormDataDTO.metadata);

      return uploadFiles({
        url: `/files`,
        method: 'POST',
        headers: { 'Content-Type': 'multipart/form-data' },
        data: formData,
        signal,
      });
    },
    [uploadFiles],
  );
};

export const useUploadFilesMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUploadFilesHook>>>,
    TError,
    { data: FileUploadFormDataDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUploadFilesHook>>>,
  TError,
  { data: FileUploadFormDataDTO },
  TContext
> => {
  const mutationKey = ['uploadFiles'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const uploadFiles = useUploadFilesHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUploadFilesHook>>>,
    { data: FileUploadFormDataDTO }
  > = (props) => {
    const { data } = props ?? {};

    return uploadFiles(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UploadFilesMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUploadFilesHook>>>
>;
export type UploadFilesMutationBody = FileUploadFormDataDTO;
export type UploadFilesMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Upload files
 */
export const useUploadFiles = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUploadFilesHook>>>,
      TError,
      { data: FileUploadFormDataDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUploadFilesHook>>>,
  TError,
  { data: FileUploadFormDataDTO },
  TContext
> => {
  const mutationOptions = useUploadFilesMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get transactions for a tenant/organization
 * @summary Get transactions for a tenant/organization
 */
export const useGetTransactionsHook = () => {
  const getTransactions = useAxiosAlltix<TransactionWithProductsDTO[]>();

  return useCallback(
    (params?: GetTransactionsParams, signal?: AbortSignal) => {
      return getTransactions({
        url: `/transactions`,
        method: 'GET',
        params,
        signal,
      });
    },
    [getTransactions],
  );
};

export const getGetTransactionsQueryKey = (params?: GetTransactionsParams) => {
  return [`/transactions`, ...(params ? [params] : [])] as const;
};

export const useGetTransactionsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params?: GetTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTransactionsQueryKey(params);

  const getTransactions = useGetTransactionsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>
  > = ({ signal }) => getTransactions(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetTransactionsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>
>;
export type GetTransactionsQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
>;

export function useGetTransactions<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params: undefined | GetTransactionsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetTransactions<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params?: GetTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetTransactions<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params?: GetTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get transactions for a tenant/organization
 */

export function useGetTransactions<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  params?: GetTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetTransactionsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Export transactions for a tenant/organization
 * @summary Export transactions for a tenant/organization
 */
export const useExportTransactionsHook = () => {
  const exportTransactions =
    useAxiosAlltix<TransactionWithBillingDetailsDTO[]>();

  return useCallback(
    (params: ExportTransactionsParams, signal?: AbortSignal) => {
      return exportTransactions({
        url: `/transactions/export`,
        method: 'GET',
        params,
        signal,
      });
    },
    [exportTransactions],
  );
};

export const getExportTransactionsQueryKey = (
  params?: ExportTransactionsParams,
) => {
  return [`/transactions/export`, ...(params ? [params] : [])] as const;
};

export const useExportTransactionsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useExportTransactionsHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
>(
  params: ExportTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useExportTransactionsHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getExportTransactionsQueryKey(params);

  const exportTransactions = useExportTransactionsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useExportTransactionsHook>>>
  > = ({ signal }) => exportTransactions(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useExportTransactionsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ExportTransactionsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useExportTransactionsHook>>>
>;
export type ExportTransactionsQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | InternalServerErrorResponse
>;

export function useExportTransactions<
  TData = Awaited<ReturnType<ReturnType<typeof useExportTransactionsHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
>(
  params: ExportTransactionsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useExportTransactionsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useExportTransactionsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useExportTransactionsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useExportTransactions<
  TData = Awaited<ReturnType<ReturnType<typeof useExportTransactionsHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
>(
  params: ExportTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useExportTransactionsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useExportTransactionsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useExportTransactionsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useExportTransactions<
  TData = Awaited<ReturnType<ReturnType<typeof useExportTransactionsHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
>(
  params: ExportTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useExportTransactionsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Export transactions for a tenant/organization
 */

export function useExportTransactions<
  TData = Awaited<ReturnType<ReturnType<typeof useExportTransactionsHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
>(
  params: ExportTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useExportTransactionsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useExportTransactionsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get transaction with products
 * @summary Get transaction with products
 */
export const useGetTransactionWithProductsHook = () => {
  const getTransactionWithProducts =
    useAxiosAlltix<TransactionWithProductsDTO>();

  return useCallback(
    (transactionID: string, signal?: AbortSignal) => {
      return getTransactionWithProducts({
        url: `/transactions/${transactionID}`,
        method: 'GET',
        signal,
      });
    },
    [getTransactionWithProducts],
  );
};

export const getGetTransactionWithProductsQueryKey = (
  transactionID?: string,
) => {
  return [`/transactions/${transactionID}`] as const;
};

export const useGetTransactionWithProductsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTransactionWithProductsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  transactionID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTransactionWithProductsHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetTransactionWithProductsQueryKey(transactionID);

  const getTransactionWithProducts = useGetTransactionWithProductsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetTransactionWithProductsHook>>>
  > = ({ signal }) => getTransactionWithProducts(transactionID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!transactionID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTransactionWithProductsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetTransactionWithProductsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTransactionWithProductsHook>>>
>;
export type GetTransactionWithProductsQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetTransactionWithProducts<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTransactionWithProductsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  transactionID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTransactionWithProductsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetTransactionWithProductsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetTransactionWithProductsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetTransactionWithProducts<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTransactionWithProductsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  transactionID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTransactionWithProductsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetTransactionWithProductsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetTransactionWithProductsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetTransactionWithProducts<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTransactionWithProductsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  transactionID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTransactionWithProductsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get transaction with products
 */

export function useGetTransactionWithProducts<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetTransactionWithProductsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  transactionID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetTransactionWithProductsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetTransactionWithProductsQueryOptions(
    transactionID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Refund a transaction
 * @summary Refund transaction
 */
export const useRefundTransactionHook = () => {
  const refundTransaction = useAxiosAlltix<TransactionDTO>();

  return useCallback(
    (transactionID: string) => {
      return refundTransaction({
        url: `/transactions/${transactionID}`,
        method: 'DELETE',
      });
    },
    [refundTransaction],
  );
};

export const useRefundTransactionMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useRefundTransactionHook>>>,
    TError,
    { transactionID: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useRefundTransactionHook>>>,
  TError,
  { transactionID: string },
  TContext
> => {
  const mutationKey = ['refundTransaction'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const refundTransaction = useRefundTransactionHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useRefundTransactionHook>>>,
    { transactionID: string }
  > = (props) => {
    const { transactionID } = props ?? {};

    return refundTransaction(transactionID);
  };

  return { mutationFn, ...mutationOptions };
};

export type RefundTransactionMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useRefundTransactionHook>>>
>;

export type RefundTransactionMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Refund transaction
 */
export const useRefundTransaction = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useRefundTransactionHook>>>,
      TError,
      { transactionID: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useRefundTransactionHook>>>,
  TError,
  { transactionID: string },
  TContext
> => {
  const mutationOptions = useRefundTransactionMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * List names for users by IDs
 * @summary List names for users by IDs
 */
export const useUsersListNamesForIDsHook = () => {
  const usersListNamesForIDs = useAxiosAlltix<UserNameInfoDTO[]>();

  return useCallback(
    (params: UsersListNamesForIDsParams, signal?: AbortSignal) => {
      return usersListNamesForIDs({
        url: `/users/names`,
        method: 'GET',
        params,
        signal,
      });
    },
    [usersListNamesForIDs],
  );
};

export const getUsersListNamesForIDsQueryKey = (
  params?: UsersListNamesForIDsParams,
) => {
  return [`/users/names`, ...(params ? [params] : [])] as const;
};

export const useUsersListNamesForIDsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useUsersListNamesForIDsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: UsersListNamesForIDsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useUsersListNamesForIDsHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getUsersListNamesForIDsQueryKey(params);

  const usersListNamesForIDs = useUsersListNamesForIDsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useUsersListNamesForIDsHook>>>
  > = ({ signal }) => usersListNamesForIDs(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useUsersListNamesForIDsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type UsersListNamesForIDsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUsersListNamesForIDsHook>>>
>;
export type UsersListNamesForIDsQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useUsersListNamesForIDs<
  TData = Awaited<ReturnType<ReturnType<typeof useUsersListNamesForIDsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: UsersListNamesForIDsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useUsersListNamesForIDsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useUsersListNamesForIDsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useUsersListNamesForIDsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useUsersListNamesForIDs<
  TData = Awaited<ReturnType<ReturnType<typeof useUsersListNamesForIDsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: UsersListNamesForIDsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useUsersListNamesForIDsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useUsersListNamesForIDsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useUsersListNamesForIDsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useUsersListNamesForIDs<
  TData = Awaited<ReturnType<ReturnType<typeof useUsersListNamesForIDsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: UsersListNamesForIDsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useUsersListNamesForIDsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List names for users by IDs
 */

export function useUsersListNamesForIDs<
  TData = Awaited<ReturnType<ReturnType<typeof useUsersListNamesForIDsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: UsersListNamesForIDsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useUsersListNamesForIDsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useUsersListNamesForIDsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Deletes a user profile by ID
 * @summary Deletes a user profile by ID
 */
export const useDeleteUserProfileByIDHook = () => {
  const deleteUserProfileByID = useAxiosAlltix<UserProfileDTO>();

  return useCallback(
    (id: string) => {
      return deleteUserProfileByID({
        url: `/users/user-profiles/${id}`,
        method: 'DELETE',
      });
    },
    [deleteUserProfileByID],
  );
};

export const useDeleteUserProfileByIDMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteUserProfileByIDHook>>>,
    TError,
    { id: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteUserProfileByIDHook>>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ['deleteUserProfileByID'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteUserProfileByID = useDeleteUserProfileByIDHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteUserProfileByIDHook>>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return deleteUserProfileByID(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteUserProfileByIDMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteUserProfileByIDHook>>>
>;

export type DeleteUserProfileByIDMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Deletes a user profile by ID
 */
export const useDeleteUserProfileByID = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteUserProfileByIDHook>>>,
      TError,
      { id: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteUserProfileByIDHook>>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = useDeleteUserProfileByIDMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get spent credit for organization
 * @summary Get spent credit for organization
 */
export const useGetSpentCreditHook = () => {
  const getSpentCredit = useAxiosAlltix<number>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getSpentCredit({ url: `/credit`, method: 'GET', signal });
    },
    [getSpentCredit],
  );
};

export const getGetSpentCreditQueryKey = () => {
  return [`/credit`] as const;
};

export const useGetSpentCreditQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetSpentCreditHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useGetSpentCreditHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSpentCreditQueryKey();

  const getSpentCredit = useGetSpentCreditHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetSpentCreditHook>>>
  > = ({ signal }) => getSpentCredit(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetSpentCreditHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetSpentCreditQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetSpentCreditHook>>>
>;
export type GetSpentCreditQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetSpentCredit<
  TData = Awaited<ReturnType<ReturnType<typeof useGetSpentCreditHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetSpentCreditHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetSpentCreditHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetSpentCreditHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetSpentCredit<
  TData = Awaited<ReturnType<ReturnType<typeof useGetSpentCreditHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetSpentCreditHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetSpentCreditHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetSpentCreditHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetSpentCredit<
  TData = Awaited<ReturnType<ReturnType<typeof useGetSpentCreditHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetSpentCreditHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get spent credit for organization
 */

export function useGetSpentCredit<
  TData = Awaited<ReturnType<ReturnType<typeof useGetSpentCreditHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetSpentCreditHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetSpentCreditQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * TravelCard claimability check
 * @summary TravelCard claimability check
 */
export const useTravelCardIsClaimableHook = () => {
  const travelCardIsClaimable = useAxiosAlltix<ClaimabilityStatusDTO>();

  return useCallback(
    (params: TravelCardIsClaimableParams, signal?: AbortSignal) => {
      return travelCardIsClaimable({
        url: `/travelcards`,
        method: 'GET',
        params,
        signal,
      });
    },
    [travelCardIsClaimable],
  );
};

export const getTravelCardIsClaimableQueryKey = (
  params?: TravelCardIsClaimableParams,
) => {
  return [`/travelcards`, ...(params ? [params] : [])] as const;
};

export const useTravelCardIsClaimableQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useTravelCardIsClaimableHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  params: TravelCardIsClaimableParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useTravelCardIsClaimableHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getTravelCardIsClaimableQueryKey(params);

  const travelCardIsClaimable = useTravelCardIsClaimableHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useTravelCardIsClaimableHook>>>
  > = ({ signal }) => travelCardIsClaimable(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useTravelCardIsClaimableHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type TravelCardIsClaimableQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useTravelCardIsClaimableHook>>>
>;
export type TravelCardIsClaimableQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

export function useTravelCardIsClaimable<
  TData = Awaited<ReturnType<ReturnType<typeof useTravelCardIsClaimableHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  params: TravelCardIsClaimableParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useTravelCardIsClaimableHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useTravelCardIsClaimableHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useTravelCardIsClaimableHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useTravelCardIsClaimable<
  TData = Awaited<ReturnType<ReturnType<typeof useTravelCardIsClaimableHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  params: TravelCardIsClaimableParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useTravelCardIsClaimableHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useTravelCardIsClaimableHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useTravelCardIsClaimableHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useTravelCardIsClaimable<
  TData = Awaited<ReturnType<ReturnType<typeof useTravelCardIsClaimableHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  params: TravelCardIsClaimableParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useTravelCardIsClaimableHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary TravelCard claimability check
 */

export function useTravelCardIsClaimable<
  TData = Awaited<ReturnType<ReturnType<typeof useTravelCardIsClaimableHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
>(
  params: TravelCardIsClaimableParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useTravelCardIsClaimableHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useTravelCardIsClaimableQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Claim a TravelCard
 * @summary Claim a TravelCard
 */
export const useTravelCardClaimHook = () => {
  const travelCardClaim = useAxiosAlltix<TravelCardResponseDTO>();

  return useCallback(
    (
      travelCardClaimRequestDTO: TravelCardClaimRequestDTO,
      signal?: AbortSignal,
    ) => {
      return travelCardClaim({
        url: `/travelcards`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: travelCardClaimRequestDTO,
        signal,
      });
    },
    [travelCardClaim],
  );
};

export const useTravelCardClaimMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useTravelCardClaimHook>>>,
    TError,
    { data: TravelCardClaimRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useTravelCardClaimHook>>>,
  TError,
  { data: TravelCardClaimRequestDTO },
  TContext
> => {
  const mutationKey = ['travelCardClaim'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const travelCardClaim = useTravelCardClaimHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useTravelCardClaimHook>>>,
    { data: TravelCardClaimRequestDTO }
  > = (props) => {
    const { data } = props ?? {};

    return travelCardClaim(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type TravelCardClaimMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useTravelCardClaimHook>>>
>;
export type TravelCardClaimMutationBody = TravelCardClaimRequestDTO;
export type TravelCardClaimMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Claim a TravelCard
 */
export const useTravelCardClaim = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useTravelCardClaimHook>>>,
      TError,
      { data: TravelCardClaimRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useTravelCardClaimHook>>>,
  TError,
  { data: TravelCardClaimRequestDTO },
  TContext
> => {
  const mutationOptions = useTravelCardClaimMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Rename a TravelCard
 * @summary Rename a TravelCard
 */
export const useTravelCardRenameHook = () => {
  const travelCardRename = useAxiosAlltix<TravelCardResponseDTO>();

  return useCallback(
    (travelCardRenameRequestDTO: TravelCardRenameRequestDTO) => {
      return travelCardRename({
        url: `/travelcards`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: travelCardRenameRequestDTO,
      });
    },
    [travelCardRename],
  );
};

export const useTravelCardRenameMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useTravelCardRenameHook>>>,
    TError,
    { data: TravelCardRenameRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useTravelCardRenameHook>>>,
  TError,
  { data: TravelCardRenameRequestDTO },
  TContext
> => {
  const mutationKey = ['travelCardRename'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const travelCardRename = useTravelCardRenameHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useTravelCardRenameHook>>>,
    { data: TravelCardRenameRequestDTO }
  > = (props) => {
    const { data } = props ?? {};

    return travelCardRename(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type TravelCardRenameMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useTravelCardRenameHook>>>
>;
export type TravelCardRenameMutationBody = TravelCardRenameRequestDTO;
export type TravelCardRenameMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Rename a TravelCard
 */
export const useTravelCardRename = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useTravelCardRenameHook>>>,
      TError,
      { data: TravelCardRenameRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useTravelCardRenameHook>>>,
  TError,
  { data: TravelCardRenameRequestDTO },
  TContext
> => {
  const mutationOptions = useTravelCardRenameMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Claim a blank TravelCard for someone who does not exist in the portal. The card is associated with a parking traveller in bobcat and no record of it is kept here.
 * @summary Claim a TravelCard anonymously
 */
export const useTravelCardClaimAnonymousHook = () => {
  const travelCardClaimAnonymous =
    useAxiosAlltix<TravelCardClaimAnonymousResponseDTO>();

  return useCallback(
    (
      travelCardClaimAnonymousRequestDTO: TravelCardClaimAnonymousRequestDTO,
      signal?: AbortSignal,
    ) => {
      return travelCardClaimAnonymous({
        url: `/travelcards/anonymous`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: travelCardClaimAnonymousRequestDTO,
        signal,
      });
    },
    [travelCardClaimAnonymous],
  );
};

export const useTravelCardClaimAnonymousMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useTravelCardClaimAnonymousHook>>>,
    TError,
    { data: TravelCardClaimAnonymousRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useTravelCardClaimAnonymousHook>>>,
  TError,
  { data: TravelCardClaimAnonymousRequestDTO },
  TContext
> => {
  const mutationKey = ['travelCardClaimAnonymous'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const travelCardClaimAnonymous = useTravelCardClaimAnonymousHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useTravelCardClaimAnonymousHook>>>,
    { data: TravelCardClaimAnonymousRequestDTO }
  > = (props) => {
    const { data } = props ?? {};

    return travelCardClaimAnonymous(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type TravelCardClaimAnonymousMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useTravelCardClaimAnonymousHook>>>
>;
export type TravelCardClaimAnonymousMutationBody =
  TravelCardClaimAnonymousRequestDTO;
export type TravelCardClaimAnonymousMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Claim a TravelCard anonymously
 */
export const useTravelCardClaimAnonymous = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useTravelCardClaimAnonymousHook>>>,
      TError,
      { data: TravelCardClaimAnonymousRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useTravelCardClaimAnonymousHook>>>,
  TError,
  { data: TravelCardClaimAnonymousRequestDTO },
  TContext
> => {
  const mutationOptions = useTravelCardClaimAnonymousMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get a list of all card credits for the user's organization
 * @summary List card credits
 */
export const useListCardCreditsHook = () => {
  const listCardCredits = useAxiosAlltix<CardCreditDTO[]>();

  return useCallback(
    (params?: ListCardCreditsParams, signal?: AbortSignal) => {
      return listCardCredits({
        url: `/card-credits`,
        method: 'GET',
        params,
        signal,
      });
    },
    [listCardCredits],
  );
};

export const getListCardCreditsQueryKey = (params?: ListCardCreditsParams) => {
  return [`/card-credits`, ...(params ? [params] : [])] as const;
};

export const useListCardCreditsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListCardCreditsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: ListCardCreditsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListCardCreditsHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCardCreditsQueryKey(params);

  const listCardCredits = useListCardCreditsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListCardCreditsHook>>>
  > = ({ signal }) => listCardCredits(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListCardCreditsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListCardCreditsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListCardCreditsHook>>>
>;
export type ListCardCreditsQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useListCardCredits<
  TData = Awaited<ReturnType<ReturnType<typeof useListCardCreditsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: undefined | ListCardCreditsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListCardCreditsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListCardCreditsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListCardCreditsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListCardCredits<
  TData = Awaited<ReturnType<ReturnType<typeof useListCardCreditsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: ListCardCreditsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListCardCreditsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListCardCreditsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListCardCreditsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListCardCredits<
  TData = Awaited<ReturnType<ReturnType<typeof useListCardCreditsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: ListCardCreditsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListCardCreditsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List card credits
 */

export function useListCardCredits<
  TData = Awaited<ReturnType<ReturnType<typeof useListCardCreditsHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: ListCardCreditsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListCardCreditsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListCardCreditsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Create a new credit on card
 * @summary Create credit on card
 */
export const useCreateCardCreditHook = () => {
  const createCardCredit = useAxiosAlltix<CardCreditDTO>();

  return useCallback(
    (cardCreditCreateDTO: CardCreditCreateDTO, signal?: AbortSignal) => {
      return createCardCredit({
        url: `/card-credits`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: cardCreditCreateDTO,
        signal,
      });
    },
    [createCardCredit],
  );
};

export const useCreateCardCreditMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateCardCreditHook>>>,
    TError,
    { data: CardCreditCreateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateCardCreditHook>>>,
  TError,
  { data: CardCreditCreateDTO },
  TContext
> => {
  const mutationKey = ['createCardCredit'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createCardCredit = useCreateCardCreditHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateCardCreditHook>>>,
    { data: CardCreditCreateDTO }
  > = (props) => {
    const { data } = props ?? {};

    return createCardCredit(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateCardCreditMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateCardCreditHook>>>
>;
export type CreateCardCreditMutationBody = CardCreditCreateDTO;
export type CreateCardCreditMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Create credit on card
 */
export const useCreateCardCredit = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateCardCreditHook>>>,
      TError,
      { data: CardCreditCreateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateCardCreditHook>>>,
  TError,
  { data: CardCreditCreateDTO },
  TContext
> => {
  const mutationOptions = useCreateCardCreditMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Check if a card serial is claimable
 * @summary Validate card credit serial
 */
export const useValidateCardCreditSerialHook = () => {
  const validateCardCreditSerial = useAxiosAlltix<CardCreditValidationDTO>();

  return useCallback(
    (cardSerial: string, signal?: AbortSignal) => {
      return validateCardCreditSerial({
        url: `/card-credits/validate/${cardSerial}`,
        method: 'GET',
        signal,
      });
    },
    [validateCardCreditSerial],
  );
};

export const getValidateCardCreditSerialQueryKey = (cardSerial?: string) => {
  return [`/card-credits/validate/${cardSerial}`] as const;
};

export const useValidateCardCreditSerialQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useValidateCardCreditSerialHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | InternalServerErrorResponse
  >,
>(
  cardSerial: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useValidateCardCreditSerialHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getValidateCardCreditSerialQueryKey(cardSerial);

  const validateCardCreditSerial = useValidateCardCreditSerialHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useValidateCardCreditSerialHook>>>
  > = ({ signal }) => validateCardCreditSerial(cardSerial, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!cardSerial,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useValidateCardCreditSerialHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ValidateCardCreditSerialQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useValidateCardCreditSerialHook>>>
>;
export type ValidateCardCreditSerialQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | InternalServerErrorResponse
>;

export function useValidateCardCreditSerial<
  TData = Awaited<
    ReturnType<ReturnType<typeof useValidateCardCreditSerialHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | InternalServerErrorResponse
  >,
>(
  cardSerial: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useValidateCardCreditSerialHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useValidateCardCreditSerialHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useValidateCardCreditSerialHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useValidateCardCreditSerial<
  TData = Awaited<
    ReturnType<ReturnType<typeof useValidateCardCreditSerialHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | InternalServerErrorResponse
  >,
>(
  cardSerial: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useValidateCardCreditSerialHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useValidateCardCreditSerialHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useValidateCardCreditSerialHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useValidateCardCreditSerial<
  TData = Awaited<
    ReturnType<ReturnType<typeof useValidateCardCreditSerialHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | InternalServerErrorResponse
  >,
>(
  cardSerial: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useValidateCardCreditSerialHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Validate card credit serial
 */

export function useValidateCardCreditSerial<
  TData = Awaited<
    ReturnType<ReturnType<typeof useValidateCardCreditSerialHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | InternalServerErrorResponse
  >,
>(
  cardSerial: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useValidateCardCreditSerialHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useValidateCardCreditSerialQueryOptions(
    cardSerial,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Update the selected card credit
 * @summary Update card credit
 */
export const useUpdateCardCreditBySerialHook = () => {
  const updateCardCreditBySerial = useAxiosAlltix<CardCreditDTO>();

  return useCallback(
    (cardSerial: string, cardCreditUpdateDTO: CardCreditUpdateDTO) => {
      return updateCardCreditBySerial({
        url: `/card-credits/${cardSerial}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: cardCreditUpdateDTO,
      });
    },
    [updateCardCreditBySerial],
  );
};

export const useUpdateCardCreditBySerialMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateCardCreditBySerialHook>>>,
    TError,
    { cardSerial: string; data: CardCreditUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateCardCreditBySerialHook>>>,
  TError,
  { cardSerial: string; data: CardCreditUpdateDTO },
  TContext
> => {
  const mutationKey = ['updateCardCreditBySerial'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateCardCreditBySerial = useUpdateCardCreditBySerialHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateCardCreditBySerialHook>>>,
    { cardSerial: string; data: CardCreditUpdateDTO }
  > = (props) => {
    const { cardSerial, data } = props ?? {};

    return updateCardCreditBySerial(cardSerial, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateCardCreditBySerialMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateCardCreditBySerialHook>>>
>;
export type UpdateCardCreditBySerialMutationBody = CardCreditUpdateDTO;
export type UpdateCardCreditBySerialMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Update card credit
 */
export const useUpdateCardCreditBySerial = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateCardCreditBySerialHook>>>,
      TError,
      { cardSerial: string; data: CardCreditUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateCardCreditBySerialHook>>>,
  TError,
  { cardSerial: string; data: CardCreditUpdateDTO },
  TContext
> => {
  const mutationOptions = useUpdateCardCreditBySerialMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Deletes a card credit by card serial
 * @summary Deletes a card credit
 */
export const useDeleteCardCreditBySerialHook = () => {
  const deleteCardCreditBySerial = useAxiosAlltix<CardCreditDTO>();

  return useCallback(
    (cardSerial: string) => {
      return deleteCardCreditBySerial({
        url: `/card-credits/${cardSerial}`,
        method: 'DELETE',
      });
    },
    [deleteCardCreditBySerial],
  );
};

export const useDeleteCardCreditBySerialMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteCardCreditBySerialHook>>>,
    TError,
    { cardSerial: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteCardCreditBySerialHook>>>,
  TError,
  { cardSerial: string },
  TContext
> => {
  const mutationKey = ['deleteCardCreditBySerial'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteCardCreditBySerial = useDeleteCardCreditBySerialHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteCardCreditBySerialHook>>>,
    { cardSerial: string }
  > = (props) => {
    const { cardSerial } = props ?? {};

    return deleteCardCreditBySerial(cardSerial);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteCardCreditBySerialMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteCardCreditBySerialHook>>>
>;

export type DeleteCardCreditBySerialMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Deletes a card credit
 */
export const useDeleteCardCreditBySerial = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteCardCreditBySerialHook>>>,
      TError,
      { cardSerial: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteCardCreditBySerialHook>>>,
  TError,
  { cardSerial: string },
  TContext
> => {
  const mutationOptions = useDeleteCardCreditBySerialMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get a list of all organizations for the current tenant (including deleted)
 * @summary List organizations (including deleted)
 */
export const useM2mListOrganizationsIncludingDeletedHook = () => {
  const m2mListOrganizationsIncludingDeleted =
    useAxiosAlltix<OrganizationDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return m2mListOrganizationsIncludingDeleted({
        url: `/m2m/organizations`,
        method: 'GET',
        signal,
      });
    },
    [m2mListOrganizationsIncludingDeleted],
  );
};

export const getM2mListOrganizationsIncludingDeletedQueryKey = () => {
  return [`/m2m/organizations`] as const;
};

export const useM2mListOrganizationsIncludingDeletedQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListOrganizationsIncludingDeletedHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<
        ReturnType<
          ReturnType<typeof useM2mListOrganizationsIncludingDeletedHook>
        >
      >,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getM2mListOrganizationsIncludingDeletedQueryKey();

  const m2mListOrganizationsIncludingDeleted =
    useM2mListOrganizationsIncludingDeletedHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<ReturnType<typeof useM2mListOrganizationsIncludingDeletedHook>>
    >
  > = ({ signal }) => m2mListOrganizationsIncludingDeleted(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<
      ReturnType<ReturnType<typeof useM2mListOrganizationsIncludingDeletedHook>>
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type M2mListOrganizationsIncludingDeletedQueryResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useM2mListOrganizationsIncludingDeletedHook>>
  >
>;
export type M2mListOrganizationsIncludingDeletedQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useM2mListOrganizationsIncludingDeleted<
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListOrganizationsIncludingDeletedHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useM2mListOrganizationsIncludingDeletedHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useM2mListOrganizationsIncludingDeletedHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useM2mListOrganizationsIncludingDeletedHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useM2mListOrganizationsIncludingDeleted<
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListOrganizationsIncludingDeletedHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useM2mListOrganizationsIncludingDeletedHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useM2mListOrganizationsIncludingDeletedHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useM2mListOrganizationsIncludingDeletedHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useM2mListOrganizationsIncludingDeleted<
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListOrganizationsIncludingDeletedHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useM2mListOrganizationsIncludingDeletedHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List organizations (including deleted)
 */

export function useM2mListOrganizationsIncludingDeleted<
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListOrganizationsIncludingDeletedHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useM2mListOrganizationsIncludingDeletedHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions =
    useM2mListOrganizationsIncludingDeletedQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get departments for tenant (includes deleted)
 * @summary List departments for tenant
 */
export const useM2mListDepartmentsForTenantHook = () => {
  const m2mListDepartmentsForTenant = useAxiosAlltix<DepartmentDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return m2mListDepartmentsForTenant({
        url: `/m2m/departments`,
        method: 'GET',
        signal,
      });
    },
    [m2mListDepartmentsForTenant],
  );
};

export const getM2mListDepartmentsForTenantQueryKey = () => {
  return [`/m2m/departments`] as const;
};

export const useM2mListDepartmentsForTenantQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListDepartmentsForTenantHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<
        ReturnType<ReturnType<typeof useM2mListDepartmentsForTenantHook>>
      >,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getM2mListDepartmentsForTenantQueryKey();

  const m2mListDepartmentsForTenant = useM2mListDepartmentsForTenantHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useM2mListDepartmentsForTenantHook>>>
  > = ({ signal }) => m2mListDepartmentsForTenant(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useM2mListDepartmentsForTenantHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type M2mListDepartmentsForTenantQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useM2mListDepartmentsForTenantHook>>>
>;
export type M2mListDepartmentsForTenantQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useM2mListDepartmentsForTenant<
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListDepartmentsForTenantHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useM2mListDepartmentsForTenantHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useM2mListDepartmentsForTenantHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useM2mListDepartmentsForTenantHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useM2mListDepartmentsForTenant<
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListDepartmentsForTenantHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useM2mListDepartmentsForTenantHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useM2mListDepartmentsForTenantHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useM2mListDepartmentsForTenantHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useM2mListDepartmentsForTenant<
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListDepartmentsForTenantHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useM2mListDepartmentsForTenantHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List departments for tenant
 */

export function useM2mListDepartmentsForTenant<
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListDepartmentsForTenantHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useM2mListDepartmentsForTenantHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useM2mListDepartmentsForTenantQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Fetches several travellers by ID
 * @summary Get travellers
 */
export const useM2mTravellersGetManyHook = () => {
  const m2mTravellersGetMany = useAxiosAlltix<TravellerDTO[]>();

  return useCallback(
    (params?: M2mTravellersGetManyParams, signal?: AbortSignal) => {
      return m2mTravellersGetMany({
        url: `/m2m/travellers`,
        method: 'GET',
        params,
        signal,
      });
    },
    [m2mTravellersGetMany],
  );
};

export const getM2mTravellersGetManyQueryKey = (
  params?: M2mTravellersGetManyParams,
) => {
  return [`/m2m/travellers`, ...(params ? [params] : [])] as const;
};

export const useM2mTravellersGetManyQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useM2mTravellersGetManyHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: M2mTravellersGetManyParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useM2mTravellersGetManyHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getM2mTravellersGetManyQueryKey(params);

  const m2mTravellersGetMany = useM2mTravellersGetManyHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useM2mTravellersGetManyHook>>>
  > = ({ signal }) => m2mTravellersGetMany(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useM2mTravellersGetManyHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type M2mTravellersGetManyQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useM2mTravellersGetManyHook>>>
>;
export type M2mTravellersGetManyQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useM2mTravellersGetMany<
  TData = Awaited<ReturnType<ReturnType<typeof useM2mTravellersGetManyHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params: undefined | M2mTravellersGetManyParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useM2mTravellersGetManyHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useM2mTravellersGetManyHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useM2mTravellersGetManyHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useM2mTravellersGetMany<
  TData = Awaited<ReturnType<ReturnType<typeof useM2mTravellersGetManyHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: M2mTravellersGetManyParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useM2mTravellersGetManyHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useM2mTravellersGetManyHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useM2mTravellersGetManyHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useM2mTravellersGetMany<
  TData = Awaited<ReturnType<ReturnType<typeof useM2mTravellersGetManyHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: M2mTravellersGetManyParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useM2mTravellersGetManyHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get travellers
 */

export function useM2mTravellersGetMany<
  TData = Awaited<ReturnType<ReturnType<typeof useM2mTravellersGetManyHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  params?: M2mTravellersGetManyParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useM2mTravellersGetManyHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useM2mTravellersGetManyQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get a list of all payers for the current tenant
 * @summary List payers for tenant
 */
export const useM2mListPayersForTenantHook = () => {
  const m2mListPayersForTenant = useAxiosAlltix<PayerDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return m2mListPayersForTenant({
        url: `/m2m/payers`,
        method: 'GET',
        signal,
      });
    },
    [m2mListPayersForTenant],
  );
};

export const getM2mListPayersForTenantQueryKey = () => {
  return [`/m2m/payers`] as const;
};

export const useM2mListPayersForTenantQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useM2mListPayersForTenantHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useM2mListPayersForTenantHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getM2mListPayersForTenantQueryKey();

  const m2mListPayersForTenant = useM2mListPayersForTenantHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useM2mListPayersForTenantHook>>>
  > = ({ signal }) => m2mListPayersForTenant(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useM2mListPayersForTenantHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type M2mListPayersForTenantQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useM2mListPayersForTenantHook>>>
>;
export type M2mListPayersForTenantQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useM2mListPayersForTenant<
  TData = Awaited<ReturnType<ReturnType<typeof useM2mListPayersForTenantHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useM2mListPayersForTenantHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useM2mListPayersForTenantHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useM2mListPayersForTenantHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useM2mListPayersForTenant<
  TData = Awaited<ReturnType<ReturnType<typeof useM2mListPayersForTenantHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useM2mListPayersForTenantHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useM2mListPayersForTenantHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useM2mListPayersForTenantHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useM2mListPayersForTenant<
  TData = Awaited<ReturnType<ReturnType<typeof useM2mListPayersForTenantHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useM2mListPayersForTenantHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List payers for tenant
 */

export function useM2mListPayersForTenant<
  TData = Awaited<ReturnType<ReturnType<typeof useM2mListPayersForTenantHook>>>,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useM2mListPayersForTenantHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useM2mListPayersForTenantQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get Allpay users with billing details
 * @summary Get Allpay users with billing details
 */
export const useM2mListAllpayUsersWithBillingDetailsHook = () => {
  const m2mListAllpayUsersWithBillingDetails =
    useAxiosAlltix<AllpayUserWithBillingDetailsDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return m2mListAllpayUsersWithBillingDetails({
        url: `/m2m/allpay-users`,
        method: 'GET',
        signal,
      });
    },
    [m2mListAllpayUsersWithBillingDetails],
  );
};

export const getM2mListAllpayUsersWithBillingDetailsQueryKey = () => {
  return [`/m2m/allpay-users`] as const;
};

export const useM2mListAllpayUsersWithBillingDetailsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListAllpayUsersWithBillingDetailsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<
        ReturnType<
          ReturnType<typeof useM2mListAllpayUsersWithBillingDetailsHook>
        >
      >,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getM2mListAllpayUsersWithBillingDetailsQueryKey();

  const m2mListAllpayUsersWithBillingDetails =
    useM2mListAllpayUsersWithBillingDetailsHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<ReturnType<typeof useM2mListAllpayUsersWithBillingDetailsHook>>
    >
  > = ({ signal }) => m2mListAllpayUsersWithBillingDetails(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<
      ReturnType<ReturnType<typeof useM2mListAllpayUsersWithBillingDetailsHook>>
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type M2mListAllpayUsersWithBillingDetailsQueryResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useM2mListAllpayUsersWithBillingDetailsHook>>
  >
>;
export type M2mListAllpayUsersWithBillingDetailsQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | InternalServerErrorResponse
>;

export function useM2mListAllpayUsersWithBillingDetails<
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListAllpayUsersWithBillingDetailsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useM2mListAllpayUsersWithBillingDetailsHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useM2mListAllpayUsersWithBillingDetailsHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useM2mListAllpayUsersWithBillingDetailsHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useM2mListAllpayUsersWithBillingDetails<
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListAllpayUsersWithBillingDetailsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useM2mListAllpayUsersWithBillingDetailsHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useM2mListAllpayUsersWithBillingDetailsHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useM2mListAllpayUsersWithBillingDetailsHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useM2mListAllpayUsersWithBillingDetails<
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListAllpayUsersWithBillingDetailsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useM2mListAllpayUsersWithBillingDetailsHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get Allpay users with billing details
 */

export function useM2mListAllpayUsersWithBillingDetails<
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListAllpayUsersWithBillingDetailsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useM2mListAllpayUsersWithBillingDetailsHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions =
    useM2mListAllpayUsersWithBillingDetailsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get Transactions with billing details for date range
 * @summary Get Transactions with billing details for date range
 */
export const useM2mListTransactionsWithBillingDetailsHook = () => {
  const m2mListTransactionsWithBillingDetails =
    useAxiosAlltix<TransactionWithBillingDetailsDTO[]>();

  return useCallback(
    (
      params?: M2mListTransactionsWithBillingDetailsParams,
      signal?: AbortSignal,
    ) => {
      return m2mListTransactionsWithBillingDetails({
        url: `/m2m/transactions`,
        method: 'GET',
        params,
        signal,
      });
    },
    [m2mListTransactionsWithBillingDetails],
  );
};

export const getM2mListTransactionsWithBillingDetailsQueryKey = (
  params?: M2mListTransactionsWithBillingDetailsParams,
) => {
  return [`/m2m/transactions`, ...(params ? [params] : [])] as const;
};

export const useM2mListTransactionsWithBillingDetailsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListTransactionsWithBillingDetailsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
>(
  params?: M2mListTransactionsWithBillingDetailsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useM2mListTransactionsWithBillingDetailsHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getM2mListTransactionsWithBillingDetailsQueryKey(params);

  const m2mListTransactionsWithBillingDetails =
    useM2mListTransactionsWithBillingDetailsHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<
        ReturnType<typeof useM2mListTransactionsWithBillingDetailsHook>
      >
    >
  > = ({ signal }) => m2mListTransactionsWithBillingDetails(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<
      ReturnType<
        ReturnType<typeof useM2mListTransactionsWithBillingDetailsHook>
      >
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type M2mListTransactionsWithBillingDetailsQueryResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useM2mListTransactionsWithBillingDetailsHook>>
  >
>;
export type M2mListTransactionsWithBillingDetailsQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | InternalServerErrorResponse
>;

export function useM2mListTransactionsWithBillingDetails<
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListTransactionsWithBillingDetailsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
>(
  params: undefined | M2mListTransactionsWithBillingDetailsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useM2mListTransactionsWithBillingDetailsHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useM2mListTransactionsWithBillingDetailsHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useM2mListTransactionsWithBillingDetailsHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useM2mListTransactionsWithBillingDetails<
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListTransactionsWithBillingDetailsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
>(
  params?: M2mListTransactionsWithBillingDetailsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useM2mListTransactionsWithBillingDetailsHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useM2mListTransactionsWithBillingDetailsHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useM2mListTransactionsWithBillingDetailsHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useM2mListTransactionsWithBillingDetails<
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListTransactionsWithBillingDetailsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
>(
  params?: M2mListTransactionsWithBillingDetailsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useM2mListTransactionsWithBillingDetailsHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get Transactions with billing details for date range
 */

export function useM2mListTransactionsWithBillingDetails<
  TData = Awaited<
    ReturnType<ReturnType<typeof useM2mListTransactionsWithBillingDetailsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InternalServerErrorResponse
  >,
>(
  params?: M2mListTransactionsWithBillingDetailsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useM2mListTransactionsWithBillingDetailsHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useM2mListTransactionsWithBillingDetailsQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Creates the AllTix-side artifacts for a single reseller ticket purchase:
an order, a transaction (with its transaction_product line item and
transaction_ticket link), a traveller (find-or-create within the
department's organization), a master-collection membership, and an
issued ticket.

The endpoint is non-idempotent — retrying the same call creates a new
order, transaction, and issued ticket. The traveller and collection
membership are upserted by unique constraints and will not duplicate.

 * @summary Create a reseller purchase
 */
export const useM2mCreateResellerPurchaseHook = () => {
  const m2mCreateResellerPurchase = useAxiosAlltix<ResellerPurchaseResultDTO>();

  return useCallback(
    (
      resellerPurchaseCreateDTO: ResellerPurchaseCreateDTO,
      signal?: AbortSignal,
    ) => {
      return m2mCreateResellerPurchase({
        url: `/m2m/reseller-purchases`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: resellerPurchaseCreateDTO,
        signal,
      });
    },
    [m2mCreateResellerPurchase],
  );
};

export const useM2mCreateResellerPurchaseMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useM2mCreateResellerPurchaseHook>>>,
    TError,
    { data: ResellerPurchaseCreateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useM2mCreateResellerPurchaseHook>>>,
  TError,
  { data: ResellerPurchaseCreateDTO },
  TContext
> => {
  const mutationKey = ['m2mCreateResellerPurchase'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const m2mCreateResellerPurchase = useM2mCreateResellerPurchaseHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useM2mCreateResellerPurchaseHook>>>,
    { data: ResellerPurchaseCreateDTO }
  > = (props) => {
    const { data } = props ?? {};

    return m2mCreateResellerPurchase(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type M2mCreateResellerPurchaseMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useM2mCreateResellerPurchaseHook>>>
>;
export type M2mCreateResellerPurchaseMutationBody = ResellerPurchaseCreateDTO;
export type M2mCreateResellerPurchaseMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Create a reseller purchase
 */
export const useM2mCreateResellerPurchase = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useM2mCreateResellerPurchaseHook>>>,
      TError,
      { data: ResellerPurchaseCreateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useM2mCreateResellerPurchaseHook>>>,
  TError,
  { data: ResellerPurchaseCreateDTO },
  TContext
> => {
  const mutationOptions = useM2mCreateResellerPurchaseMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get your notification settings, if you're an administrator
 * @summary Get your notifications settings
 */
export const useGetAdminNotificationSettingsHook = () => {
  const getAdminNotificationSettings =
    useAxiosAlltix<AdminNotificationSettingsDTO>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getAdminNotificationSettings({
        url: `/admin-notifications`,
        method: 'GET',
        signal,
      });
    },
    [getAdminNotificationSettings],
  );
};

export const getGetAdminNotificationSettingsQueryKey = () => {
  return [`/admin-notifications`] as const;
};

export const useGetAdminNotificationSettingsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminNotificationSettingsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<
        ReturnType<ReturnType<typeof useGetAdminNotificationSettingsHook>>
      >,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAdminNotificationSettingsQueryKey();

  const getAdminNotificationSettings = useGetAdminNotificationSettingsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetAdminNotificationSettingsHook>>>
  > = ({ signal }) => getAdminNotificationSettings(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetAdminNotificationSettingsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetAdminNotificationSettingsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetAdminNotificationSettingsHook>>>
>;
export type GetAdminNotificationSettingsQueryError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

export function useGetAdminNotificationSettings<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminNotificationSettingsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminNotificationSettingsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetAdminNotificationSettingsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetAdminNotificationSettingsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetAdminNotificationSettings<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminNotificationSettingsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminNotificationSettingsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetAdminNotificationSettingsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetAdminNotificationSettingsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetAdminNotificationSettings<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminNotificationSettingsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminNotificationSettingsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get your notifications settings
 */

export function useGetAdminNotificationSettings<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetAdminNotificationSettingsHook>>
  >,
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetAdminNotificationSettingsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetAdminNotificationSettingsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Update your notification settings, if you're an administrator
 * @summary Update your notification settings
 */
export const useSetAdminNotificationSettingsHook = () => {
  const setAdminNotificationSettings =
    useAxiosAlltix<AdminNotificationSettingsDTO>();

  return useCallback(
    (adminNotificationSettingsDTO: AdminNotificationSettingsDTO) => {
      return setAdminNotificationSettings({
        url: `/admin-notifications`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: adminNotificationSettingsDTO,
      });
    },
    [setAdminNotificationSettings],
  );
};

export const useSetAdminNotificationSettingsMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSetAdminNotificationSettingsHook>>>,
    TError,
    { data: AdminNotificationSettingsDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useSetAdminNotificationSettingsHook>>>,
  TError,
  { data: AdminNotificationSettingsDTO },
  TContext
> => {
  const mutationKey = ['setAdminNotificationSettings'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const setAdminNotificationSettings = useSetAdminNotificationSettingsHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useSetAdminNotificationSettingsHook>>>,
    { data: AdminNotificationSettingsDTO }
  > = (props) => {
    const { data } = props ?? {};

    return setAdminNotificationSettings(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SetAdminNotificationSettingsMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSetAdminNotificationSettingsHook>>>
>;
export type SetAdminNotificationSettingsMutationBody =
  AdminNotificationSettingsDTO;
export type SetAdminNotificationSettingsMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Update your notification settings
 */
export const useSetAdminNotificationSettings = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useSetAdminNotificationSettingsHook>>
      >,
      TError,
      { data: AdminNotificationSettingsDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useSetAdminNotificationSettingsHook>>>,
  TError,
  { data: AdminNotificationSettingsDTO },
  TContext
> => {
  const mutationOptions =
    useSetAdminNotificationSettingsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Sysadmin-only. Revives a failed card transfer task: clears the failure state, resets the retry budget, and lets the runner resume from the last successful phase. Returns 409 if the task is not failed or if another active task already exists for the same source card.
 * @summary Force-retry a failed card transfer task
 */
export const useRetryCardTransferTaskHook = () => {
  const retryCardTransferTask = useAxiosAlltix<CardTransferTaskDTO>();

  return useCallback(
    (taskID: string, signal?: AbortSignal) => {
      return retryCardTransferTask({
        url: `/admin/card-transfer-tasks/${taskID}/retry`,
        method: 'POST',
        signal,
      });
    },
    [retryCardTransferTask],
  );
};

export const useRetryCardTransferTaskMutationOptions = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useRetryCardTransferTaskHook>>>,
    TError,
    { taskID: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useRetryCardTransferTaskHook>>>,
  TError,
  { taskID: string },
  TContext
> => {
  const mutationKey = ['retryCardTransferTask'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const retryCardTransferTask = useRetryCardTransferTaskHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useRetryCardTransferTaskHook>>>,
    { taskID: string }
  > = (props) => {
    const { taskID } = props ?? {};

    return retryCardTransferTask(taskID);
  };

  return { mutationFn, ...mutationOptions };
};

export type RetryCardTransferTaskMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useRetryCardTransferTaskHook>>>
>;

export type RetryCardTransferTaskMutationError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse
>;

/**
 * @summary Force-retry a failed card transfer task
 */
export const useRetryCardTransferTask = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useRetryCardTransferTaskHook>>>,
      TError,
      { taskID: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useRetryCardTransferTaskHook>>>,
  TError,
  { taskID: string },
  TContext
> => {
  const mutationOptions = useRetryCardTransferTaskMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Sysadmin-only. Cancels a card transfer task that has not yet mutated any bobcat state. Only valid while the task is pending and in phase 'created' or 'products_listed'; once the bobcat dance has begun, recovery is force-retry only. Returns 409 if the task is not pending or its phase is past 'products_listed'.
 * @summary Cancel a pre-mutation card transfer task
 */
export const useCancelCardTransferTaskHook = () => {
  const cancelCardTransferTask = useAxiosAlltix<CardTransferTaskDTO>();

  return useCallback(
    (taskID: string, signal?: AbortSignal) => {
      return cancelCardTransferTask({
        url: `/admin/card-transfer-tasks/${taskID}/cancel`,
        method: 'POST',
        signal,
      });
    },
    [cancelCardTransferTask],
  );
};

export const useCancelCardTransferTaskMutationOptions = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCancelCardTransferTaskHook>>>,
    TError,
    { taskID: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCancelCardTransferTaskHook>>>,
  TError,
  { taskID: string },
  TContext
> => {
  const mutationKey = ['cancelCardTransferTask'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const cancelCardTransferTask = useCancelCardTransferTaskHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCancelCardTransferTaskHook>>>,
    { taskID: string }
  > = (props) => {
    const { taskID } = props ?? {};

    return cancelCardTransferTask(taskID);
  };

  return { mutationFn, ...mutationOptions };
};

export type CancelCardTransferTaskMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCancelCardTransferTaskHook>>>
>;

export type CancelCardTransferTaskMutationError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse
>;

/**
 * @summary Cancel a pre-mutation card transfer task
 */
export const useCancelCardTransferTask = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCancelCardTransferTaskHook>>>,
      TError,
      { taskID: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCancelCardTransferTaskHook>>>,
  TError,
  { taskID: string },
  TContext
> => {
  const mutationOptions = useCancelCardTransferTaskMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Sysadmin-only. Deletes the card's bearer in Bobcat and drops the portal's assignment of the serial so it can be claimed again, mainly to reuse cards during testing. Returns 404 if the serial is not assigned in the tenant, and 409 if the card was issued by the system, backs a wallet card, or carries a live issued ticket.
 * @summary Purge a card serial from the tenant
 */
export const usePurgeCardHook = () => {
  const purgeCard = useAxiosAlltix<void>();

  return useCallback(
    (cardSerial: string, signal?: AbortSignal) => {
      return purgeCard({
        url: `/admin/cards/${cardSerial}/purge`,
        method: 'POST',
        signal,
      });
    },
    [purgeCard],
  );
};

export const usePurgeCardMutationOptions = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePurgeCardHook>>>,
    TError,
    { cardSerial: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePurgeCardHook>>>,
  TError,
  { cardSerial: string },
  TContext
> => {
  const mutationKey = ['purgeCard'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const purgeCard = usePurgeCardHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePurgeCardHook>>>,
    { cardSerial: string }
  > = (props) => {
    const { cardSerial } = props ?? {};

    return purgeCard(cardSerial);
  };

  return { mutationFn, ...mutationOptions };
};

export type PurgeCardMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePurgeCardHook>>>
>;

export type PurgeCardMutationError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse
>;

/**
 * @summary Purge a card serial from the tenant
 */
export const usePurgeCard = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePurgeCardHook>>>,
      TError,
      { cardSerial: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePurgeCardHook>>>,
  TError,
  { cardSerial: string },
  TContext
> => {
  const mutationOptions = usePurgeCardMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Sysadmin-only. Every assignment of the serial in the tenant, newest first. A serial that was never assigned has an empty history.
 * @summary Get a card serial's assignment history
 */
export const useAdminGetCardHistoryHook = () => {
  const adminGetCardHistory = useAxiosAlltix<CardAssignmentDTO[]>();

  return useCallback(
    (cardSerial: string, signal?: AbortSignal) => {
      return adminGetCardHistory({
        url: `/admin/cards/${cardSerial}/history`,
        method: 'GET',
        signal,
      });
    },
    [adminGetCardHistory],
  );
};

export const getAdminGetCardHistoryQueryKey = (cardSerial?: string) => {
  return [`/admin/cards/${cardSerial}/history`] as const;
};

export const useAdminGetCardHistoryQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useAdminGetCardHistoryHook>>>,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(
  cardSerial: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useAdminGetCardHistoryHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getAdminGetCardHistoryQueryKey(cardSerial);

  const adminGetCardHistory = useAdminGetCardHistoryHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useAdminGetCardHistoryHook>>>
  > = ({ signal }) => adminGetCardHistory(cardSerial, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!cardSerial,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useAdminGetCardHistoryHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type AdminGetCardHistoryQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useAdminGetCardHistoryHook>>>
>;
export type AdminGetCardHistoryQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse
>;

export function useAdminGetCardHistory<
  TData = Awaited<ReturnType<ReturnType<typeof useAdminGetCardHistoryHook>>>,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(
  cardSerial: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useAdminGetCardHistoryHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useAdminGetCardHistoryHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useAdminGetCardHistoryHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useAdminGetCardHistory<
  TData = Awaited<ReturnType<ReturnType<typeof useAdminGetCardHistoryHook>>>,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(
  cardSerial: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useAdminGetCardHistoryHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useAdminGetCardHistoryHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useAdminGetCardHistoryHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useAdminGetCardHistory<
  TData = Awaited<ReturnType<ReturnType<typeof useAdminGetCardHistoryHook>>>,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(
  cardSerial: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useAdminGetCardHistoryHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get a card serial's assignment history
 */

export function useAdminGetCardHistory<
  TData = Awaited<ReturnType<ReturnType<typeof useAdminGetCardHistoryHook>>>,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(
  cardSerial: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useAdminGetCardHistoryHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useAdminGetCardHistoryQueryOptions(cardSerial, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Sysadmin-only. Every card assigned to the traveller anywhere in the tenant, newest first, including cards since released. Deleted travellers keep their history; an unknown traveller has an empty one.
 * @summary Get a traveller's card history across the tenant
 */
export const useAdminGetTravellerCardHistoryHook = () => {
  const adminGetTravellerCardHistory = useAxiosAlltix<CardAssignmentDTO[]>();

  return useCallback(
    (travellerID: string, signal?: AbortSignal) => {
      return adminGetTravellerCardHistory({
        url: `/admin/travellers/${travellerID}/card-history`,
        method: 'GET',
        signal,
      });
    },
    [adminGetTravellerCardHistory],
  );
};

export const getAdminGetTravellerCardHistoryQueryKey = (
  travellerID?: string,
) => {
  return [`/admin/travellers/${travellerID}/card-history`] as const;
};

export const useAdminGetTravellerCardHistoryQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useAdminGetTravellerCardHistoryHook>>
  >,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useAdminGetTravellerCardHistoryHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getAdminGetTravellerCardHistoryQueryKey(travellerID);

  const adminGetTravellerCardHistory = useAdminGetTravellerCardHistoryHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useAdminGetTravellerCardHistoryHook>>>
  > = ({ signal }) => adminGetTravellerCardHistory(travellerID, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!travellerID,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useAdminGetTravellerCardHistoryHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type AdminGetTravellerCardHistoryQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useAdminGetTravellerCardHistoryHook>>>
>;
export type AdminGetTravellerCardHistoryQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse
>;

export function useAdminGetTravellerCardHistory<
  TData = Awaited<
    ReturnType<ReturnType<typeof useAdminGetTravellerCardHistoryHook>>
  >,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(
  travellerID: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useAdminGetTravellerCardHistoryHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useAdminGetTravellerCardHistoryHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useAdminGetTravellerCardHistoryHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useAdminGetTravellerCardHistory<
  TData = Awaited<
    ReturnType<ReturnType<typeof useAdminGetTravellerCardHistoryHook>>
  >,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useAdminGetTravellerCardHistoryHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useAdminGetTravellerCardHistoryHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useAdminGetTravellerCardHistoryHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useAdminGetTravellerCardHistory<
  TData = Awaited<
    ReturnType<ReturnType<typeof useAdminGetTravellerCardHistoryHook>>
  >,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useAdminGetTravellerCardHistoryHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get a traveller's card history across the tenant
 */

export function useAdminGetTravellerCardHistory<
  TData = Awaited<
    ReturnType<ReturnType<typeof useAdminGetTravellerCardHistoryHook>>
  >,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(
  travellerID: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useAdminGetTravellerCardHistoryHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useAdminGetTravellerCardHistoryQueryOptions(
    travellerID,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Sends a magic link to the provided email address for passwordless authentication
 * @summary Request self-service magic link
 */
export const useSelfServiceRequestMagicLinkHook = () => {
  const selfServiceRequestMagicLink = useAxiosAlltix<void>();

  return useCallback(
    (
      selfServiceMagicLinkRequestDTO: SelfServiceMagicLinkRequestDTO,
      signal?: AbortSignal,
    ) => {
      return selfServiceRequestMagicLink({
        url: `/self-service/auth/request`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: selfServiceMagicLinkRequestDTO,
        signal,
      });
    },
    [selfServiceRequestMagicLink],
  );
};

export const useSelfServiceRequestMagicLinkMutationOptions = <
  TError = ErrorType<BadRequestResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceRequestMagicLinkHook>>>,
    TError,
    { data: SelfServiceMagicLinkRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceRequestMagicLinkHook>>>,
  TError,
  { data: SelfServiceMagicLinkRequestDTO },
  TContext
> => {
  const mutationKey = ['selfServiceRequestMagicLink'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const selfServiceRequestMagicLink = useSelfServiceRequestMagicLinkHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceRequestMagicLinkHook>>>,
    { data: SelfServiceMagicLinkRequestDTO }
  > = (props) => {
    const { data } = props ?? {};

    return selfServiceRequestMagicLink(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SelfServiceRequestMagicLinkMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceRequestMagicLinkHook>>>
>;
export type SelfServiceRequestMagicLinkMutationBody =
  SelfServiceMagicLinkRequestDTO;
export type SelfServiceRequestMagicLinkMutationError =
  ErrorType<BadRequestResponse>;

/**
 * @summary Request self-service magic link
 */
export const useSelfServiceRequestMagicLink = <
  TError = ErrorType<BadRequestResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useSelfServiceRequestMagicLinkHook>>
      >,
      TError,
      { data: SelfServiceMagicLinkRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceRequestMagicLinkHook>>>,
  TError,
  { data: SelfServiceMagicLinkRequestDTO },
  TContext
> => {
  const mutationOptions =
    useSelfServiceRequestMagicLinkMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Exchanges a magic link token for a JWT session token
 * @summary Verify magic link token
 */
export const useSelfServiceVerifyMagicLinkHook = () => {
  const selfServiceVerifyMagicLink =
    useAxiosAlltix<SelfServiceJWTResponseDTO>();

  return useCallback(
    (
      selfServiceVerifyRequestDTO: SelfServiceVerifyRequestDTO,
      signal?: AbortSignal,
    ) => {
      return selfServiceVerifyMagicLink({
        url: `/self-service/auth/verify`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: selfServiceVerifyRequestDTO,
        signal,
      });
    },
    [selfServiceVerifyMagicLink],
  );
};

export const useSelfServiceVerifyMagicLinkMutationOptions = <
  TError = ErrorType<BadRequestResponse | UnauthorizedResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceVerifyMagicLinkHook>>>,
    TError,
    { data: SelfServiceVerifyRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceVerifyMagicLinkHook>>>,
  TError,
  { data: SelfServiceVerifyRequestDTO },
  TContext
> => {
  const mutationKey = ['selfServiceVerifyMagicLink'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const selfServiceVerifyMagicLink = useSelfServiceVerifyMagicLinkHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceVerifyMagicLinkHook>>>,
    { data: SelfServiceVerifyRequestDTO }
  > = (props) => {
    const { data } = props ?? {};

    return selfServiceVerifyMagicLink(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SelfServiceVerifyMagicLinkMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceVerifyMagicLinkHook>>>
>;
export type SelfServiceVerifyMagicLinkMutationBody =
  SelfServiceVerifyRequestDTO;
export type SelfServiceVerifyMagicLinkMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse
>;

/**
 * @summary Verify magic link token
 */
export const useSelfServiceVerifyMagicLink = <
  TError = ErrorType<BadRequestResponse | UnauthorizedResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useSelfServiceVerifyMagicLinkHook>>>,
      TError,
      { data: SelfServiceVerifyRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceVerifyMagicLinkHook>>>,
  TError,
  { data: SelfServiceVerifyRequestDTO },
  TContext
> => {
  const mutationOptions = useSelfServiceVerifyMagicLinkMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Exchanges a valid JWT for a new JWT with fresh expiry (idle timeout mechanism)
 * @summary Refresh JWT token
 */
export const useSelfServiceRefreshTokenHook = () => {
  const selfServiceRefreshToken = useAxiosAlltix<SelfServiceJWTResponseDTO>();

  return useCallback(
    (signal?: AbortSignal) => {
      return selfServiceRefreshToken({
        url: `/self-service/auth/refresh`,
        method: 'POST',
        signal,
      });
    },
    [selfServiceRefreshToken],
  );
};

export const useSelfServiceRefreshTokenMutationOptions = <
  TError = ErrorType<UnauthorizedResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceRefreshTokenHook>>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceRefreshTokenHook>>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ['selfServiceRefreshToken'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const selfServiceRefreshToken = useSelfServiceRefreshTokenHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceRefreshTokenHook>>>,
    void
  > = () => {
    return selfServiceRefreshToken();
  };

  return { mutationFn, ...mutationOptions };
};

export type SelfServiceRefreshTokenMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceRefreshTokenHook>>>
>;

export type SelfServiceRefreshTokenMutationError =
  ErrorType<UnauthorizedResponse>;

/**
 * @summary Refresh JWT token
 */
export const useSelfServiceRefreshToken = <
  TError = ErrorType<UnauthorizedResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useSelfServiceRefreshTokenHook>>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceRefreshTokenHook>>>,
  TError,
  void,
  TContext
> => {
  const mutationOptions = useSelfServiceRefreshTokenMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get all active and inactive tickets for the authenticated user's own travellers (self) and wards (for guardians), grouped by traveller
 * @summary List issued tickets
 */
export const useSelfServiceListTicketsHook = () => {
  const selfServiceListTickets =
    useAxiosAlltix<SelfServiceTicketsResponseDTO>();

  return useCallback(
    (signal?: AbortSignal) => {
      return selfServiceListTickets({
        url: `/self-service/tickets`,
        method: 'GET',
        signal,
      });
    },
    [selfServiceListTickets],
  );
};

export const getSelfServiceListTicketsQueryKey = () => {
  return [`/self-service/tickets`] as const;
};

export const useSelfServiceListTicketsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useSelfServiceListTicketsHook>>>,
  TError = ErrorType<UnauthorizedResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useSelfServiceListTicketsHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getSelfServiceListTicketsQueryKey();

  const selfServiceListTickets = useSelfServiceListTicketsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceListTicketsHook>>>
  > = ({ signal }) => selfServiceListTickets(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceListTicketsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type SelfServiceListTicketsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceListTicketsHook>>>
>;
export type SelfServiceListTicketsQueryError = ErrorType<UnauthorizedResponse>;

export function useSelfServiceListTickets<
  TData = Awaited<ReturnType<ReturnType<typeof useSelfServiceListTicketsHook>>>,
  TError = ErrorType<UnauthorizedResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSelfServiceListTicketsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useSelfServiceListTicketsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useSelfServiceListTicketsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useSelfServiceListTickets<
  TData = Awaited<ReturnType<ReturnType<typeof useSelfServiceListTicketsHook>>>,
  TError = ErrorType<UnauthorizedResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSelfServiceListTicketsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useSelfServiceListTicketsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useSelfServiceListTicketsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useSelfServiceListTickets<
  TData = Awaited<ReturnType<ReturnType<typeof useSelfServiceListTicketsHook>>>,
  TError = ErrorType<UnauthorizedResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSelfServiceListTicketsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List issued tickets
 */

export function useSelfServiceListTickets<
  TData = Awaited<ReturnType<ReturnType<typeof useSelfServiceListTicketsHook>>>,
  TError = ErrorType<UnauthorizedResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSelfServiceListTicketsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useSelfServiceListTicketsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Move an issued ticket to a different bearer owned by the authenticated user or their wards
 * @summary Move ticket to a different bearer
 */
export const useSelfServiceMoveTicketHook = () => {
  const selfServiceMoveTicket = useAxiosAlltix<void>();

  return useCallback(
    (
      ticketID: string,
      issuedTicketBearerUpdateDTO: IssuedTicketBearerUpdateDTO,
    ) => {
      return selfServiceMoveTicket({
        url: `/self-service/tickets/${ticketID}/move`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: issuedTicketBearerUpdateDTO,
      });
    },
    [selfServiceMoveTicket],
  );
};

export const useSelfServiceMoveTicketMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceMoveTicketHook>>>,
    TError,
    { ticketID: string; data: IssuedTicketBearerUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceMoveTicketHook>>>,
  TError,
  { ticketID: string; data: IssuedTicketBearerUpdateDTO },
  TContext
> => {
  const mutationKey = ['selfServiceMoveTicket'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const selfServiceMoveTicket = useSelfServiceMoveTicketHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceMoveTicketHook>>>,
    { ticketID: string; data: IssuedTicketBearerUpdateDTO }
  > = (props) => {
    const { ticketID, data } = props ?? {};

    return selfServiceMoveTicket(ticketID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SelfServiceMoveTicketMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceMoveTicketHook>>>
>;
export type SelfServiceMoveTicketMutationBody = IssuedTicketBearerUpdateDTO;
export type SelfServiceMoveTicketMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Move ticket to a different bearer
 */
export const useSelfServiceMoveTicket = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useSelfServiceMoveTicketHook>>>,
      TError,
      { ticketID: string; data: IssuedTicketBearerUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceMoveTicketHook>>>,
  TError,
  { ticketID: string; data: IssuedTicketBearerUpdateDTO },
  TContext
> => {
  const mutationOptions = useSelfServiceMoveTicketMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Lend an issued ticket to a different bearer owned by the authenticated user or their wards
 * @summary Lend ticket to a different bearer
 */
export const useSelfServiceLendTicketHook = () => {
  const selfServiceLendTicket = useAxiosAlltix<void>();

  return useCallback(
    (ticketID: string, issuedTicketLendDTO: IssuedTicketLendDTO) => {
      return selfServiceLendTicket({
        url: `/self-service/tickets/${ticketID}/lend`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: issuedTicketLendDTO,
      });
    },
    [selfServiceLendTicket],
  );
};

export const useSelfServiceLendTicketMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceLendTicketHook>>>,
    TError,
    { ticketID: string; data: IssuedTicketLendDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceLendTicketHook>>>,
  TError,
  { ticketID: string; data: IssuedTicketLendDTO },
  TContext
> => {
  const mutationKey = ['selfServiceLendTicket'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const selfServiceLendTicket = useSelfServiceLendTicketHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceLendTicketHook>>>,
    { ticketID: string; data: IssuedTicketLendDTO }
  > = (props) => {
    const { ticketID, data } = props ?? {};

    return selfServiceLendTicket(ticketID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SelfServiceLendTicketMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceLendTicketHook>>>
>;
export type SelfServiceLendTicketMutationBody = IssuedTicketLendDTO;
export type SelfServiceLendTicketMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Lend ticket to a different bearer
 */
export const useSelfServiceLendTicket = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useSelfServiceLendTicketHook>>>,
      TError,
      { ticketID: string; data: IssuedTicketLendDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceLendTicketHook>>>,
  TError,
  { ticketID: string; data: IssuedTicketLendDTO },
  TContext
> => {
  const mutationOptions = useSelfServiceLendTicketMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get bearers (from Bobcat) and travel cards (from local DB) for authenticated user and their wards
 * @summary List bearers and travel cards
 */
export const useSelfServiceListBearersHook = () => {
  const selfServiceListBearers =
    useAxiosAlltix<SelfServiceBearersResponseDTO>();

  return useCallback(
    (signal?: AbortSignal) => {
      return selfServiceListBearers({
        url: `/self-service/bearers`,
        method: 'GET',
        signal,
      });
    },
    [selfServiceListBearers],
  );
};

export const getSelfServiceListBearersQueryKey = () => {
  return [`/self-service/bearers`] as const;
};

export const useSelfServiceListBearersQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useSelfServiceListBearersHook>>>,
  TError = ErrorType<UnauthorizedResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useSelfServiceListBearersHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getSelfServiceListBearersQueryKey();

  const selfServiceListBearers = useSelfServiceListBearersHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceListBearersHook>>>
  > = ({ signal }) => selfServiceListBearers(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceListBearersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type SelfServiceListBearersQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceListBearersHook>>>
>;
export type SelfServiceListBearersQueryError = ErrorType<UnauthorizedResponse>;

export function useSelfServiceListBearers<
  TData = Awaited<ReturnType<ReturnType<typeof useSelfServiceListBearersHook>>>,
  TError = ErrorType<UnauthorizedResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSelfServiceListBearersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useSelfServiceListBearersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useSelfServiceListBearersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useSelfServiceListBearers<
  TData = Awaited<ReturnType<ReturnType<typeof useSelfServiceListBearersHook>>>,
  TError = ErrorType<UnauthorizedResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSelfServiceListBearersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useSelfServiceListBearersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useSelfServiceListBearersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useSelfServiceListBearers<
  TData = Awaited<ReturnType<ReturnType<typeof useSelfServiceListBearersHook>>>,
  TError = ErrorType<UnauthorizedResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSelfServiceListBearersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List bearers and travel cards
 */

export function useSelfServiceListBearers<
  TData = Awaited<ReturnType<ReturnType<typeof useSelfServiceListBearersHook>>>,
  TError = ErrorType<UnauthorizedResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSelfServiceListBearersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useSelfServiceListBearersQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Add a travel card for self or a ward
 * @summary Claim a travel card
 */
export const useSelfServiceClaimCardHook = () => {
  const selfServiceClaimCard = useAxiosAlltix<SelfServiceCardDTO>();

  return useCallback(
    (
      selfServiceClaimCardRequestDTO: SelfServiceClaimCardRequestDTO,
      signal?: AbortSignal,
    ) => {
      return selfServiceClaimCard({
        url: `/self-service/cards`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: selfServiceClaimCardRequestDTO,
        signal,
      });
    },
    [selfServiceClaimCard],
  );
};

export const useSelfServiceClaimCardMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceClaimCardHook>>>,
    TError,
    { data: SelfServiceClaimCardRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceClaimCardHook>>>,
  TError,
  { data: SelfServiceClaimCardRequestDTO },
  TContext
> => {
  const mutationKey = ['selfServiceClaimCard'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const selfServiceClaimCard = useSelfServiceClaimCardHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceClaimCardHook>>>,
    { data: SelfServiceClaimCardRequestDTO }
  > = (props) => {
    const { data } = props ?? {};

    return selfServiceClaimCard(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SelfServiceClaimCardMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceClaimCardHook>>>
>;
export type SelfServiceClaimCardMutationBody = SelfServiceClaimCardRequestDTO;
export type SelfServiceClaimCardMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Claim a travel card
 */
export const useSelfServiceClaimCard = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useSelfServiceClaimCardHook>>>,
      TError,
      { data: SelfServiceClaimCardRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceClaimCardHook>>>,
  TError,
  { data: SelfServiceClaimCardRequestDTO },
  TContext
> => {
  const mutationOptions = useSelfServiceClaimCardMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Check whether a travel card can be claimed in self-service.

A card that is already claimed or not registered is reported as a 200 with
`claimable: false` and a reason, not as an error.

 * @summary Travel card claimability check
 */
export const useSelfServiceCardIsClaimableHook = () => {
  const selfServiceCardIsClaimable = useAxiosAlltix<ClaimabilityStatusDTO>();

  return useCallback(
    (params: SelfServiceCardIsClaimableParams, signal?: AbortSignal) => {
      return selfServiceCardIsClaimable({
        url: `/self-service/cards/claimable`,
        method: 'GET',
        params,
        signal,
      });
    },
    [selfServiceCardIsClaimable],
  );
};

export const getSelfServiceCardIsClaimableQueryKey = (
  params?: SelfServiceCardIsClaimableParams,
) => {
  return [
    `/self-service/cards/claimable`,
    ...(params ? [params] : []),
  ] as const;
};

export const useSelfServiceCardIsClaimableQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useSelfServiceCardIsClaimableHook>>
  >,
  TError = ErrorType<UnauthorizedResponse | InternalServerErrorResponse>,
>(
  params: SelfServiceCardIsClaimableParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useSelfServiceCardIsClaimableHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getSelfServiceCardIsClaimableQueryKey(params);

  const selfServiceCardIsClaimable = useSelfServiceCardIsClaimableHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceCardIsClaimableHook>>>
  > = ({ signal }) => selfServiceCardIsClaimable(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceCardIsClaimableHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type SelfServiceCardIsClaimableQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceCardIsClaimableHook>>>
>;
export type SelfServiceCardIsClaimableQueryError = ErrorType<
  UnauthorizedResponse | InternalServerErrorResponse
>;

export function useSelfServiceCardIsClaimable<
  TData = Awaited<
    ReturnType<ReturnType<typeof useSelfServiceCardIsClaimableHook>>
  >,
  TError = ErrorType<UnauthorizedResponse | InternalServerErrorResponse>,
>(
  params: SelfServiceCardIsClaimableParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useSelfServiceCardIsClaimableHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useSelfServiceCardIsClaimableHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useSelfServiceCardIsClaimableHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useSelfServiceCardIsClaimable<
  TData = Awaited<
    ReturnType<ReturnType<typeof useSelfServiceCardIsClaimableHook>>
  >,
  TError = ErrorType<UnauthorizedResponse | InternalServerErrorResponse>,
>(
  params: SelfServiceCardIsClaimableParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useSelfServiceCardIsClaimableHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useSelfServiceCardIsClaimableHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useSelfServiceCardIsClaimableHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useSelfServiceCardIsClaimable<
  TData = Awaited<
    ReturnType<ReturnType<typeof useSelfServiceCardIsClaimableHook>>
  >,
  TError = ErrorType<UnauthorizedResponse | InternalServerErrorResponse>,
>(
  params: SelfServiceCardIsClaimableParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useSelfServiceCardIsClaimableHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Travel card claimability check
 */

export function useSelfServiceCardIsClaimable<
  TData = Awaited<
    ReturnType<ReturnType<typeof useSelfServiceCardIsClaimableHook>>
  >,
  TError = ErrorType<UnauthorizedResponse | InternalServerErrorResponse>,
>(
  params: SelfServiceCardIsClaimableParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useSelfServiceCardIsClaimableHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useSelfServiceCardIsClaimableQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Update bobcat_private_email on a traveller
 * @summary Update a traveller
 */
export const useSelfServiceUpdateTravellerHook = () => {
  const selfServiceUpdateTraveller =
    useAxiosAlltix<SelfServiceUpdateTraveller200>();

  return useCallback(
    (
      travellerID: string,
      selfServiceTravellerUpdateDTO: SelfServiceTravellerUpdateDTO,
    ) => {
      return selfServiceUpdateTraveller({
        url: `/self-service/travellers/${travellerID}`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: selfServiceTravellerUpdateDTO,
      });
    },
    [selfServiceUpdateTraveller],
  );
};

export const useSelfServiceUpdateTravellerMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceUpdateTravellerHook>>>,
    TError,
    { travellerID: string; data: SelfServiceTravellerUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceUpdateTravellerHook>>>,
  TError,
  { travellerID: string; data: SelfServiceTravellerUpdateDTO },
  TContext
> => {
  const mutationKey = ['selfServiceUpdateTraveller'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const selfServiceUpdateTraveller = useSelfServiceUpdateTravellerHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useSelfServiceUpdateTravellerHook>>>,
    { travellerID: string; data: SelfServiceTravellerUpdateDTO }
  > = (props) => {
    const { travellerID, data } = props ?? {};

    return selfServiceUpdateTraveller(travellerID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SelfServiceUpdateTravellerMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceUpdateTravellerHook>>>
>;
export type SelfServiceUpdateTravellerMutationBody =
  SelfServiceTravellerUpdateDTO;
export type SelfServiceUpdateTravellerMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | NotFoundResponse
>;

/**
 * @summary Update a traveller
 */
export const useSelfServiceUpdateTraveller = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useSelfServiceUpdateTravellerHook>>>,
      TError,
      { travellerID: string; data: SelfServiceTravellerUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useSelfServiceUpdateTravellerHook>>>,
  TError,
  { travellerID: string; data: SelfServiceTravellerUpdateDTO },
  TContext
> => {
  const mutationOptions = useSelfServiceUpdateTravellerMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Set the favored bearer for the authenticated user or one of their wards. The bearer must belong to the traveller in bobcat and must not be blocked.
 * @summary Set the traveller favored bearer
 */
export const useSelfServiceSetTravellerFavoredBearerHook = () => {
  const selfServiceSetTravellerFavoredBearer = useAxiosAlltix<void>();

  return useCallback(
    (
      travellerID: string,
      travellerFavoredBearerUpdateDTO: TravellerFavoredBearerUpdateDTO,
    ) => {
      return selfServiceSetTravellerFavoredBearer({
        url: `/self-service/travellers/${travellerID}/favored-bearer`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: travellerFavoredBearerUpdateDTO,
      });
    },
    [selfServiceSetTravellerFavoredBearer],
  );
};

export const useSelfServiceSetTravellerFavoredBearerMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useSelfServiceSetTravellerFavoredBearerHook>>
    >,
    TError,
    { travellerID: string; data: TravellerFavoredBearerUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<
    ReturnType<ReturnType<typeof useSelfServiceSetTravellerFavoredBearerHook>>
  >,
  TError,
  { travellerID: string; data: TravellerFavoredBearerUpdateDTO },
  TContext
> => {
  const mutationKey = ['selfServiceSetTravellerFavoredBearer'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const selfServiceSetTravellerFavoredBearer =
    useSelfServiceSetTravellerFavoredBearerHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useSelfServiceSetTravellerFavoredBearerHook>>
    >,
    { travellerID: string; data: TravellerFavoredBearerUpdateDTO }
  > = (props) => {
    const { travellerID, data } = props ?? {};

    return selfServiceSetTravellerFavoredBearer(travellerID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SelfServiceSetTravellerFavoredBearerMutationResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useSelfServiceSetTravellerFavoredBearerHook>>
  >
>;
export type SelfServiceSetTravellerFavoredBearerMutationBody =
  TravellerFavoredBearerUpdateDTO;
export type SelfServiceSetTravellerFavoredBearerMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | NotFoundResponse
>;

/**
 * @summary Set the traveller favored bearer
 */
export const useSelfServiceSetTravellerFavoredBearer = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          ReturnType<typeof useSelfServiceSetTravellerFavoredBearerHook>
        >
      >,
      TError,
      { travellerID: string; data: TravellerFavoredBearerUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<ReturnType<typeof useSelfServiceSetTravellerFavoredBearerHook>>
  >,
  TError,
  { travellerID: string; data: TravellerFavoredBearerUpdateDTO },
  TContext
> => {
  const mutationOptions =
    useSelfServiceSetTravellerFavoredBearerMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Clear the favored bearer for the authenticated user or one of their wards
 * @summary Clear the traveller favored bearer
 */
export const useSelfServiceClearTravellerFavoredBearerHook = () => {
  const selfServiceClearTravellerFavoredBearer = useAxiosAlltix<void>();

  return useCallback(
    (travellerID: string) => {
      return selfServiceClearTravellerFavoredBearer({
        url: `/self-service/travellers/${travellerID}/favored-bearer`,
        method: 'DELETE',
      });
    },
    [selfServiceClearTravellerFavoredBearer],
  );
};

export const useSelfServiceClearTravellerFavoredBearerMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<
        ReturnType<typeof useSelfServiceClearTravellerFavoredBearerHook>
      >
    >,
    TError,
    { travellerID: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<
    ReturnType<ReturnType<typeof useSelfServiceClearTravellerFavoredBearerHook>>
  >,
  TError,
  { travellerID: string },
  TContext
> => {
  const mutationKey = ['selfServiceClearTravellerFavoredBearer'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const selfServiceClearTravellerFavoredBearer =
    useSelfServiceClearTravellerFavoredBearerHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<
        ReturnType<typeof useSelfServiceClearTravellerFavoredBearerHook>
      >
    >,
    { travellerID: string }
  > = (props) => {
    const { travellerID } = props ?? {};

    return selfServiceClearTravellerFavoredBearer(travellerID);
  };

  return { mutationFn, ...mutationOptions };
};

export type SelfServiceClearTravellerFavoredBearerMutationResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useSelfServiceClearTravellerFavoredBearerHook>>
  >
>;

export type SelfServiceClearTravellerFavoredBearerMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | NotFoundResponse
>;

/**
 * @summary Clear the traveller favored bearer
 */
export const useSelfServiceClearTravellerFavoredBearer = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          ReturnType<typeof useSelfServiceClearTravellerFavoredBearerHook>
        >
      >,
      TError,
      { travellerID: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<ReturnType<typeof useSelfServiceClearTravellerFavoredBearerHook>>
  >,
  TError,
  { travellerID: string },
  TContext
> => {
  const mutationOptions =
    useSelfServiceClearTravellerFavoredBearerMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Rename a bearer for the authenticated user or one of their wards. The bearer must belong to the traveller's alltix bobcat traveller and must not be blocked. Private bearers are read-only from the portal.
 * @summary Rename a traveller bearer
 */
export const useSelfServiceRenameTravellerBearerHook = () => {
  const selfServiceRenameTravellerBearer = useAxiosAlltix<TravellerBearerDTO>();

  return useCallback(
    (
      travellerID: string,
      mtbBearerID: string,
      selfServiceBearerUpdateDTO: SelfServiceBearerUpdateDTO,
    ) => {
      return selfServiceRenameTravellerBearer({
        url: `/self-service/travellers/${travellerID}/bearers/${mtbBearerID}`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: selfServiceBearerUpdateDTO,
      });
    },
    [selfServiceRenameTravellerBearer],
  );
};

export const useSelfServiceRenameTravellerBearerMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useSelfServiceRenameTravellerBearerHook>>
    >,
    TError,
    {
      travellerID: string;
      mtbBearerID: string;
      data: SelfServiceBearerUpdateDTO;
    },
    TContext
  >;
}): UseMutationOptions<
  Awaited<
    ReturnType<ReturnType<typeof useSelfServiceRenameTravellerBearerHook>>
  >,
  TError,
  {
    travellerID: string;
    mtbBearerID: string;
    data: SelfServiceBearerUpdateDTO;
  },
  TContext
> => {
  const mutationKey = ['selfServiceRenameTravellerBearer'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const selfServiceRenameTravellerBearer =
    useSelfServiceRenameTravellerBearerHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useSelfServiceRenameTravellerBearerHook>>
    >,
    {
      travellerID: string;
      mtbBearerID: string;
      data: SelfServiceBearerUpdateDTO;
    }
  > = (props) => {
    const { travellerID, mtbBearerID, data } = props ?? {};

    return selfServiceRenameTravellerBearer(travellerID, mtbBearerID, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SelfServiceRenameTravellerBearerMutationResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useSelfServiceRenameTravellerBearerHook>>
  >
>;
export type SelfServiceRenameTravellerBearerMutationBody =
  SelfServiceBearerUpdateDTO;
export type SelfServiceRenameTravellerBearerMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | NotFoundResponse
>;

/**
 * @summary Rename a traveller bearer
 */
export const useSelfServiceRenameTravellerBearer = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | NotFoundResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useSelfServiceRenameTravellerBearerHook>>
      >,
      TError,
      {
        travellerID: string;
        mtbBearerID: string;
        data: SelfServiceBearerUpdateDTO;
      },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<ReturnType<typeof useSelfServiceRenameTravellerBearerHook>>
  >,
  TError,
  {
    travellerID: string;
    mtbBearerID: string;
    data: SelfServiceBearerUpdateDTO;
  },
  TContext
> => {
  const mutationOptions =
    useSelfServiceRenameTravellerBearerMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
